/* ============================================================
   JUCE 3.0 · theme-rooms.js - the Duet and the Consultation.

   The two guided rooms (/pages/layering and /pages/finder) were most of
   theme.js by weight, and every other page downloaded, parsed and compiled
   them for nothing. They live here, and theme.js imports this file only on a
   page that carries [data-duet] or [data-finder] (bootEngineSurfaces there);
   layout/theme.liquid modulepreloads it on those two templates so it arrives
   with the engine rather than after it.

   HOW IT REACHES THEME.JS. The code below is theme.js's own, moved as it
   stood. What it still uses from theme.js arrives through mount(): the
   `let` list is filled once, from the kit theme.js hands over (chunkKit
   there), before either room runs.
   Nothing here imports theme.js by URL: a URL without the ?v= digest
   would be a second copy of theme.js, booted twice.

   JE is read at mount, and theme.js mounts this file only after loadEngine()
   has settled, so it is the engine or null exactly as it was in theme.js.
   ============================================================ */
let JE,
  SAMPLE_PROPS,
  armPairLight,
  cartAdd,
  cartBottles,
  cartFail,
  dialogShut,
  finePointer,
  juceIndex,
  nextCartTicket,
  onResizeSettled,
  openDrawer,
  openDrawerConfirm,
  reduceMotion,
  routes,
  setCartCount,
  spotlightLine,
  swapDrawer,
  syncInRoom,
  tagSet,
  toast;

export function mount(k) {
  ({
    JE,
    SAMPLE_PROPS,
    armPairLight,
    cartAdd,
    cartBottles,
    cartFail,
    dialogShut,
    finePointer,
    juceIndex,
    nextCartTicket,
    onResizeSettled,
    openDrawer,
    openDrawerConfirm,
    reduceMotion,
    routes,
    setCartCount,
    spotlightLine,
    swapDrawer,
    syncInRoom,
    tagSet,
    toast
  } = k);
  return { initDuet, initScentFinder };
}

/* ---- after the frame ----
   Work a shopper cannot see (records, a hand-off brief, a check whose answer
   only matters on a later screen) runs once the frame the tap produced is on
   screen: a frame, then a task. The timer is the floor for a tab that is not
   painting, where a frame never comes. (2026-09-22, lane 8.) */
function afterFrame(fn) {
  let done = false;
  const run = () => { if (done) return; done = true; fn(); };
  requestAnimationFrame(() => window.setTimeout(run, 0));
  window.setTimeout(run, 120);
}

/* ---- A guided room handing its brief to Jennie ----
   ONE PATH FOR EVERY ROOM (the scent finder, the layering duet, and whatever
   joins them), because there is one way to be honest about this and copying
   it per room is how one copy drifts.

   THE BUBBLE WAITS FOR THE HAND-OFF. The dispatch below opens the panel; the
   thing that decides whether the brief actually travels is three files away
   (initJennie's open -> jennie.js askHandoff) and it can decline: she is
   already mid-answer, the module failed to load, the endpoint is unset, or
   the shopper has cleared page context and only the room's name may go. A
   toast raised at click time claims a delivery in every one of those cases.

   So the room says what it handed over ONLY once the panel says it took it -
   the same rule initCart keeps for the drawer confirmation, where the
   comment reads "nothing was added, so nothing may look like it was". When
   it did not travel, the fallback line is still true: the panel is opening.

   `detail.done` is set by the juce:chat listener in initJennie and is
   undefined on a page with no controller, where the seam's own href is about
   to navigate and a bubble would be talking to nobody. ---- */
/* ---- A ROOM TELLING LOOM WHAT IT KNOWS ----
   (owner, 2026-09-03: "indepth analytics on what people are selecting,
   dislike, flow on these pages".)

   The tracker (assets/juce-loom.js) reads taps off the DOM and that is right
   for a tap - but a tap is not a fact about the brief. Whether a note ended
   up loved or ruled out is decided by the controller's cycle, which scent
   the answer settled on is decided by the engine, and whether the presence
   question was skipped by the ROOM (D13: it is moot when all three answers
   land on one scent) rather than by the shopper is known only in go(). So
   the rooms announce those facts themselves through the plug-in door.

   window.loom.track files EVERY name under the open x. namespace, so nothing
   said here can forge a canonical event (cart.add, order.place) - that is the
   whole reason the door is safe to hand feature code. Under Do Not Track or
   a refused gate window.loom is an inert stub and this is a no-op, and when
   the tracker is not loaded at all it is a no-op too. Measurement never
   breaks the room: the try is the law. */
const loomHeld = [];
function loomSay(name, props) {
  try {
    if (window.loom && typeof window.loom.track === 'function') { window.loom.track(name, props); return; }
    /* The tracker is a deferred module like this one and lands moments
       later; a room that boots off a fetch normally finds it already there.
       The first panel's arrival is the one fact worth holding for it - a
       short hold, replayed in order, dropped if the tracker never comes
       (Do Not Track, or Jennie switched off). */
    if (loomHeld.length < 40) loomHeld.push([name, props]);
    if (loomHeld.length === 1) loomDrain(0);
  } catch (err) { /* never the room's problem */ }
}
/* ---- AND SHOPIFY'S CUSTOMER EVENTS (2026-09-22, lane 8) ----
   The ad pixels (Meta, Google, TikTok) run inside Shopify's customer events
   and only ever heard Shopify's standard events, so the two rooms' answers -
   the most explicit statement of taste on the site - never reached them. The
   result is published here under the shop's own names:

     juce_finder_result    { scent, fit, band, via, alternatives, brief }
     juce_layering_result  { anchor, partner, vibe, mode, via, alternatives }

   Handles only, no person. A CUSTOM pixel (Settings > Customer events) is
   what hears a custom event: the app pixels do not, so forwarding these to
   Meta or GA4 takes a custom pixel with a subscribe for each (the code is in
   steering/theme/15-the-pour.md, L24). Shopify's own
   consent decides whether that pixel runs, exactly as for every other event;
   publish is a no-op before its analytics layer exists and never throws. */
function shopifySay(name, data) {
  try {
    const a = window.Shopify && window.Shopify.analytics;
    if (a && typeof a.publish === 'function') a.publish(name, data);
  } catch (err) { /* never the room's problem */ }
}
function loomDrain(tries) {
  window.setTimeout(() => {
    try {
      if (window.loom && typeof window.loom.track === 'function') {
        while (loomHeld.length) { const c = loomHeld.shift(); window.loom.track(c[0], c[1]); }
      } else if (tries < 40) loomDrain(tries + 1);
      else loomHeld.length = 0;
    } catch (err) { loomHeld.length = 0; }
  }, 250);
}

function handOffToJennie(detail, saidYes, saidNo) {
  const ev = new CustomEvent('juce:chat', { detail: detail });
  document.dispatchEvent(ev);
  const done = detail.done;
  if (!done || typeof done.then !== 'function') {
    /* NOBODY LISTENED, so this door is still a link. The room's own click
       handler has already stopped the anchor from following its href (it has
       to, or the panel opens twice), which on a page where initJennie never
       ran left the press doing nothing at all. Honour the href by hand. */
    if (detail.fallback) location.href = detail.fallback;
    return;
  }
  done.then((sent) => { toast(sent ? saidYes : saidNo); })
    .catch(() => { toast(saidNo); });
}

/* ============================================================
   THE WAY OUT OF A ROOM - one rule, both rooms (the Duet and the finder).

   Close used to be a plain history.back() whenever the shopper arrived
   from this site. That is right almost always and wrong in exactly one
   place: the two rooms link INTO each other (the finder's answer offers
   "layer it", the Duet's deck sends a shopper back to find a scent), so
   closing one of them handed the shopper straight into the other. Closing
   a wizard has to mean LEAVING - being dropped into a second wizard is the
   opposite of the thing the X promised (owner, 2026-09-01: "when I close
   the layering and it turns out it is taking me back to find my scent,
   instead it must just then redirect to home ... vice versa I dont want to
   redirect back into a wizard").

   So Close reads the referrer and picks one of three ways out:

     came from another room (or this one)  ->  home, a clean exit
     came from anywhere else on this site  ->  Back, where they were
     came from off-site / no history       ->  the link's own href stands

   Which paths count as rooms is NOT hard-coded here: the sections render
   data-rooms off settings.finder_url and routes.root_url, so a re-handled
   finder page or a locale prefix cannot fall out of step with this file.
   Query strings and trailing slashes are ignored - both rooms keep their
   whole brief in the URL (replaceState), so the referrer nearly always
   carries one.
   ============================================================ */
function roomPathSet(root) {
  const raw = (root && root.getAttribute('data-rooms')) || '';
  const out = [];
  raw.split(',').forEach((one) => {
    const t = one.trim();
    if (!t) return;
    try { out.push(new URL(t, location.href).pathname.replace(/\/+$/, '')); }
    catch (err) { /* an unparseable setting is simply not a room */ }
  });
  return out;
}

function wireRoomExit(root, exitEl) {
  if (!exitEl) return;
  const rooms = roomPathSet(root);
  const home = (root && root.getAttribute('data-home')) ||
               (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  exitEl.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    let ref = null;
    try { ref = document.referrer ? new URL(document.referrer) : null; }
    catch (err) { ref = null; }
    const sameSite = !!ref && ref.origin === location.origin;
    if (sameSite && rooms.indexOf(ref.pathname.replace(/\/+$/, '')) !== -1) {
      e.preventDefault();
      location.href = home;          /* never back into a wizard */
      return;
    }
    if (!sameSite || history.length < 2) return;   /* the href stands */
    e.preventDefault();
    history.back();
  });
}

/* ============================================================
   THE DUET - /pages/layering (sections/layering-duet.liquid).

   ONE SYSTEM, FROM FIRST PAINT. The old studio server-rendered a page and
   then mounted a story overlay over it once the index arrived - which read
   as two layering systems, the second sliding in seconds after the first.
   This controller ADOPTS the server-rendered cover and deck as slides
   instead of rebuilding them, and builds only what cannot exist without
   the index: the vibe question and the reveal. .is-live arms interception
   and lights the counts; it never changes what is already on screen.

   THE JOURNEY. cover (two doors) -> deck (which bottle) -> vibe (one
   question) -> the duet. The door is the first answer: "One of these is
   mine" sells only the partner on the reveal; "I am starting fresh" sells
   the pair, with the 10ml duet as the quiet second way in. Nothing
   auto-advances except a tap on a door, which IS the answer.

   ACCURACY. Every pairing, count and sentence is the layering model in
   juce-engine.js at runtime, off the same index every other surface
   reads. The vibes are honest operations: "As one" and "Loud" re-sort by
   the factors the model already returned (bridge / contrast - the same
   maths the old finish toggle used); "Daylight" and "After dark" filter
   on wearOf. When a narrowing empties the set the reveal SAYS it relaxed.

   JENNIE. The reveal asks her server for one line about the exact pair
   (GET /api/layer-note); the sentence fades in when it arrives and falls
   back to the model's own words when she is off or slow. Her door is an
   ACTION, not a prompt (D12): it prints what is about to be sent, hands
   the whole brief over as structure on the section root, draws a module
   line in her log and a bubble on this page. data-chat-open stays on it as
   the no-controller seam, and a real href fallback stands when the widget
   is off. See duetBrief / openDuetJennie below.

   Deep links, all shareable:
     /pages/layering?scent=<handle>                  opens on the vibe
     /pages/layering?scent=<a>&with=<b>&vibe=<v>     opens on the reveal
     &mode=own|fresh                                 carries the door
   ============================================================ */
function initDuet() {
  const root = document.querySelector('[data-duet]');
  if (!root) return;

  /* ---- the exit: Back on this site, home when Back is the other room.
     See wireRoomExit above. ---- */
  wireRoomExit(root, root.querySelector('[data-dt-exit]'));

  if (!JE) return;

  const roomEl = root.querySelector('[data-dt-room]');
  const slot = root.querySelector('[data-dt-slot]');
  const deck = root.querySelector('[data-dt-deck]');
  const search = root.querySelector('[data-dt-search]');
  const none = root.querySelector('[data-dt-none]');
  const askEl = root.querySelector('[data-dt-ask]');
  const stepword = root.querySelector('[data-dt-stepword]');
  const trailEl = root.querySelector('[data-dt-trail]');
  const whereEl = root.querySelector('[data-dt-where]');
  const backBtn = root.querySelector('[data-dt-back]');
  const edgeBtn = root.querySelector('[data-dt-edgeback]');
  const countEl = root.querySelector('[data-dt-count]');
  const status = root.querySelector('[data-dt-status]');
  const tiles = Array.from(root.querySelectorAll('[data-dt-pick]'));
  if (!roomEl || !slot || !deck) return;

  /* ---- A PANEL THAT IS NOT SHOWING IS INERT, FROM THE FIRST FRAME
     (2026-08-31, owner: "while the layering page and find scent page is open
     ... it seems to able to click things behind it for some reason").

     Both controllers already inert a panel as they walk OFF it, and both rooms
     paint every server-rendered panel at once and hide all but one. So the
     panels the shopper has not reached yet - the whole deck on one side, four
     whole questions on the other - were never inerted at all. They were
     `visibility: hidden`, which is enough for hit-testing and the tab ring in
     every engine we can measure, and it is NOT enough to be a guarantee:
     it leaves them in find-in-page, it leaves them reachable to anything that
     focuses by selector, and it is a paint property standing in for a
     semantic one.

     `inert` is the semantic one. It removes the subtree from hit-testing,
     from focus and from the accessibility tree outright, and it costs one
     line per room. The room can no longer hand a click to anything it is not
     showing, whatever a browser decides `visibility` means.
     ---- */
  roomEl.querySelectorAll('.dt-panel').forEach((p) => { p.inert = !p.classList.contains('is-on'); });

  const SHOP = root.dataset.shop || '/collections/all';
  /* /pages/help, never help.juce.co.za. The Gorgias help centre is retired and
     sections/header.liquid says in as many words not to point anything at it
     again. This is the mirror of the Liquid default in layering-duet.liquid -
     both have to say the same thing, because `chat_fallback` is a `url`
     setting and Shopify ignores a default on those, so the field arrives blank
     and whichever default fires is the one the shopper actually gets. */
  const HELP = root.dataset.help || '/pages/help';
  const JN_EP = ((window.theme && window.theme.jennie && window.theme.jennie.endpoint) || '')
    .trim().replace(/\/+$/, '');

  /* A to Z whatever the collection order does; a deck scanned by name has
     to be sorted by name. */
  if (tiles.length) {
    const sorted = tiles.slice().sort((a, b) =>
      String(a.dataset.title).localeCompare(String(b.dataset.title), undefined, { sensitivity: 'base' }));
    const frag = document.createDocumentFragment();
    sorted.forEach((t, i) => { t.style.setProperty('--i', i); frag.appendChild(t); });
    /* BEFORE the primer, not after it. The primer is rendered last in the
       deck on purpose ("the three rules, ON the page at the foot of the
       range"), and appending the re-sorted tiles pushed all 41 of them
       behind it - so opening the deck on a phone showed a 392px block of
       instructions and half a bottle. insertBefore(frag, null) appends, so
       a deck without a primer is unaffected. */
    deck.insertBefore(frag, deck.querySelector('.dt-primer'));
  }

  /* ---- state. Four keys, all of them in the URL. ---- */
  const S = { mode: 'fresh', anchor: '', vibe: '', at: 'cover', came: '' };
  const STEPS = ['pick', 'vibe', 'duet'];
  /* THE QUIZ (owner, 2026-08-26: "create new pair, a quiz instead of asking
     to select the product"). Create-a-new-pair no longer opens the deck: it
     asks the finder's three fastest questions - the shelf (multi-select),
     the character, the moment - and hands the brief to briefDuo, which
     picks BOTH bottles: the lead by the brief, the partner through the
     gated layering model. The deck stays one tap away ("Pick the bottle
     myself") and stays the whole no-script level, because the quiz panels
     are script-built exactly like the vibe and the reveal. */
  const Q = { for: [], character: [], when: '', season: '' };
  const QSTEPS = ['qshelf', 'qchar', 'qwhen'];
  /* ---- what LOOM is told (see loomSay, top level) ----
     The room's state as it stands, sent with every panel change; and the
     reveal, sent whenever a pair is put on the stage - by the quiz, by the
     deck, by a shared link, or by an alternate swapped in. `cameVia` is the
     door the ANCHOR came through and is separate from S.came, which the
     Back button reads and which only ever says 'quiz'. */
  let cameVia = 'deck';
  function loomBrief() {
    return {
      mode: S.mode, for: Q.for.slice(0, 3), character: Q.character.slice(0, 12),
      when: Q.when || '', season: Q.season || '',
      anchor: S.anchor || '', vibe: S.vibe || ''
    };
  }
  function sayReveal(via, passed) {
    const props = {
      anchor: S.anchor || '', vibe: S.vibe || '', mode: S.mode,
      via: via,
      alts: (RANKED || []).filter((it) => it !== CURRENT).slice(0, 4).map((it) => it.p.h)
    };
    if (CURRENT && CURRENT.p) props.subject = CURRENT.p.h;
    if (passed) props.passed = passed;
    loomSay('x.duet.reveal', props);
    /* a landing on a shared pair link is a view of a pair, not one made here */
    if (props.subject && via !== 'link') {
      shopifySay('juce_layering_result', {
        anchor: props.anchor, partner: props.subject, vibe: props.vibe, mode: props.mode,
        via: via, alternatives: props.alts
      });
    }
  }
  /* where each chapter sits on the three-dot trail, quiz and deck alike */
  const TRAILAT = { pick: 0, qshelf: 0, qchar: 1, qwhen: 1, vibe: 1, duet: 2 };
  let QSAID = [];           /* the read-back of the quiz brief, for the reveal */
  let PREP = null, CTX = null;

  /* ---- what is already in the bag. Read fresh for every reveal, because
     a page that offers to sell you the bottle sitting in your cart reads
     as a page that is not paying attention (owner, 2026-08-20). ---- */
  const BAG = { handles: new Set(), variants: new Set() };
  function readBag() {
    return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (c && c.items) {
          BAG.handles = new Set(c.items.map((it) => it.handle));
          BAG.variants = new Set(c.items.map((it) => it.id));
        }
        return BAG;
      })
      .catch(() => BAG);
  }
  let RANKED = null;       /* the vibe-ordered list the reveal reads */
  let CURRENT = null;      /* the pair item on stage */
  let LEAD = {};           /* vibe id -> the handle its door promised */
  let RELAXED = false;     /* the vibe emptied the set and was widened */
  /* THE PARTNERS THEY MOVED ON FROM. The room used to drop the bottle that
     was on stage the instant an alternate was tapped, so "I have already
     seen that one and said no" was the one thing the page knew and could
     never say. It is not a rejection - nobody pressed a No - so it travels
     as what it is: stepped back from. Titles only, this session only, and
     cleared whenever the anchor changes, because a pass is a pass against
     THAT bottle. */
  let PASSED = [];
  let jnAbort = null;      /* the in-flight Jennie note, if any */
  let jnSeq = 0;

  /* "reads as a single signature" and "each stays legible" are how a
     perfumer describes this to another perfumer. What a shopper needs to know
     is whether the two will sound like one scent or like two. */
  const VIBES = [
    { id: 'one',   k: 'As one scent',       s: 'The two read as one' },
    { id: 'loud',  k: 'Loud, both of them', s: 'Both stay recognisable' },
    { id: 'day',   k: 'For daylight',       s: 'Easy in the sun, safe at a desk' },
    { id: 'night', k: 'After dark',         s: 'Dinner, candles, later' }
  ];
  const VIBE_K = { one: 'As one scent', loud: 'Loud', day: 'For daylight', night: 'After dark' };

  function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function btn(cls, text) { const b = el('button', cls, text); b.type = 'button'; return b; }
  function order(node, i) { node.style.setProperty('--i', i); return node; }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function money(cents) { return JE.money(cents); }
  function big(src) { return String(src || '').replace(/width=\d+/, 'width=720'); }
  /* THE BOTTLE, NOT THE LIFESTYLE FILE (owner, 2026-08-21: "it is using the
     wrong image, and it is clashing"). `img` on an index row is whatever
     custom.main_product_image resolved to, and on 40 of 42 products that is a
     3413x2160 LANDSCAPE lifestyle photograph - see the note in
     snippets/search-row.liquid. Drawn as a portrait figure it crops to a
     slice, at auto width it is 1.6x as wide as tall and shoves its partner off
     the stage, and blended `darken` it darkens the page instead of melting
     into the #F2F2F2 bed. `pimg` is the square 2000x2000 variant packshot the
     same index row keeps back under its own name. Every figure in this room
     asks for it by name and falls back only when a product has none. */
  /* The CUT-OUT first. These bottles are drawn over each other and over the
     band's own ground, and a bedded plate cannot overlap anything without
     showing its plate as a seam. pimg stays as the fallback for a size that has
     no cutout - Desert Nomad's 50ml, and every bundle. */
  function shot(rec) { return big((rec && (rec.png || rec.pimg || rec.img)) || ''); }

  /* ============================================================
     THE FIT - every bottle the same size, wherever it is drawn.

     WHY IT IS MEASURED AND NOT SET. 40 of the 41 packshots are photographed
     to one frame: the bottle occupies 46% x 49% of the file, centred at
     56% / 49%. Desert Nomad is not - its bottle fills 58% x 71%. So a single
     fixed zoom (the 136% this room used everywhere) is calibrated for 40
     shots and draws the 41st half again bigger than every neighbour it sits
     beside. That is what the owner saw in the deck on 2026-08-22 ("apply a
     filtering thing so that they dont overlap each other"). It is not a bad
     CSS number to tune; it is a fact about one file, and the range will get
     more of them, so it is read off the file.

     HOW. A 64px copy of the packshot goes to a canvas and the ink bounding
     box on its #F2F2F2 bed is measured - width, height and centre, as
     fractions of the frame. --z then scales the drawn box until the ink
     lands at a target share of its frame, and --ox / --oy slide it until the
     ink centre IS the frame centre. Every bottle ends up the same optical
     height, standing in the middle of its box, whatever its source did - and
     because nothing can then overflow its cell, the figures could be grown.

     COST AND SAFETY. The measured copy is a 64px variant, one or two
     kilobytes, so the picture on screen is never re-fetched and the display
     images are left alone - a crossorigin attribute on THOSE would make the
     real packshot fail outright against a CDN that answered without CORS
     headers. Results cache in sessionStorage by filename: the range is
     measured once a visit and every later panel is instant. If anything
     refuses - no CORS, a tainted canvas, a blank read - nothing is set, the
     CSS fallback stands, and the page is exactly what it was.
     ============================================================ */
  const FIT_KEY = 'juce-shot-fit-2';
  const FIT_LANES = 4;
  let FITS = {};
  try { FITS = JSON.parse(sessionStorage.getItem(FIT_KEY) || '{}') || {}; }
  catch (err) { FITS = {}; }
  let fitLanes = 0, fitSave = 0;
  const fitWaiting = [];
  function fitName(src) { return String(src || '').split('?')[0].split('/').pop(); }
  function fitTiny(src) {
    const s = String(src || '');
    if (!s) return '';
    if (/[?&]width=\d+/.test(s)) return s.replace(/([?&])width=\d+/, '$1width=96');
    return s + (s.indexOf('?') === -1 ? '?' : '&') + 'width=96';
  }
  function fitStash() {
    window.clearTimeout(fitSave);
    fitSave = window.setTimeout(() => {
      try { sessionStorage.setItem(FIT_KEY, JSON.stringify(FITS)); }
      catch (err) { /* private mode: measuring again next visit is fine */ }
    }, 500);
  }
  /* the ink box: anything darker than the bed, shadow included - the shadow
     is part of the figure and leaving it out would sit every bottle a few
     pixels high in its frame */
  function inkBox(im) {
    /* 96, not 64. The ink height quantises to 1/S, and at 64 that is 1.6% -
       enough for two identically framed packshots to land 3% apart in the
       grid, which reads as a wobble in a row of bottles. At 96 it is 1%,
       under the eye, and the measured copy is still about three kilobytes. */
    const S = 96;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    const cx = c.getContext('2d', { willReadFrequently: true });
    if (!cx) return null;
    cx.drawImage(im, 0, 0, S, S);
    const d = cx.getImageData(0, 0, S, S).data;
    let x0 = S, y0 = S, x1 = -1, y1 = -1;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const i = (y * S + x) * 4;
        if (d[i + 3] > 8 && d[i] + d[i + 1] + d[i + 2] < 696) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < x0 || y1 < y0) return null;
    return {
      w: (x1 - x0 + 1) / S,
      h: (y1 - y0 + 1) / S,
      x: (x0 + x1 + 1) / 2 / S,
      y: (y0 + y1 + 1) / 2 / S
    };
  }
  function measureFit(src) {
    const name = fitName(src);
    if (!name) return Promise.resolve(null);
    if (name in FITS) return Promise.resolve(FITS[name] || null);
    return new Promise((done) => {
      const run = () => {
        fitLanes++;
        const im = new Image();
        im.crossOrigin = 'anonymous';
        const land = (box) => {
          FITS[name] = box || 0;          /* 0 remembers "asked, and no" */
          fitStash();
          fitLanes--;
          const next = fitWaiting.shift();
          if (next) next();
          done(box);
        };
        im.onload = () => {
          let box = null;
          try { box = inkBox(im); } catch (err) { box = null; }
          land(box);
        };
        im.onerror = () => land(null);
        im.src = fitTiny(src);
      };
      if (fitLanes < FIT_LANES) run(); else fitWaiting.push(run);
    });
  }
  /* tall / wide: the share of its box the bottle should fill. Whichever
     binds first wins, so a wide figure can never grow past its cell. */
  function fitShot(img, tall, wide) {
    if (!img) return;
    const src = img.getAttribute('src') || img.src;
    /* no isConnected guard. Every figure the controller builds is measured
       while it is still a detached node - buildVibe and buildDuet assemble a
       whole panel before appending it - so a connectedness test here silently
       skipped the vibe, the stage, the wear rows and the alternates, i.e.
       everything except the deck. Writing a custom property onto a node that
       is about to be appended is exactly the point. */
    const draw = (box) => {
      if (!box || !box.h || !box.w) return;
      const z = Math.min((tall || 0.78) / box.h, (wide || 0.86) / box.w);
      img.style.setProperty('--z', z.toFixed(3));
      img.style.setProperty('--ox', ((0.5 - box.x) * 100).toFixed(2) + '%');
      img.style.setProperty('--oy', ((0.5 - box.y) * 100).toFixed(2) + '%');
    };
    const known = FITS[fitName(src)];
    if (known) { draw(known); return; }
    if (known === 0) return;
    measureFit(src).then(draw);
  }
  /* WHAT THE ROOM SAYS IS A TOAST NOW (2026-08-31 - rooms-v2.css, WHAT THE
     ROOM SAYS IS A TOAST). It used to be a bar the room made height for, and
     the height was written back here as --dt-say so the panel's floor could
     rise above it. The pill floats and is `pointer-events: none`, so there is
     no floor to make: the property is cleared rather than measured, and the
     panel under a passing remark no longer re-lays-out because of it. */
  let sayTimer = 0;
  function say(msg) {
    if (!status) return;
    status.textContent = msg || '';
    status.hidden = !msg;
    root.style.setProperty('--dt-say', '0px');
    if (sayTimer) { window.clearTimeout(sayTimer); sayTimer = 0; }
    /* long enough to read twice, short enough to be a remark rather than a
       fixture. It was eight seconds when it cost the panel real height. */
    if (msg) sayTimer = window.setTimeout(() => { sayTimer = 0; say(''); }, 5500);
  }
  function fv(item, key) {
    const f = ((item.report && item.report.factors) || []).filter((x) => x.key === key)[0];
    return f ? f.value : 0;
  }
  function wearSide(rec) { return JE.wearOf ? JE.wearOf(rec) : ''; }

  /* ---- the model, asked once per anchor ---- */
  let poolFor = { h: '', list: [] };
  function pairsFor(anchor) {
    if (poolFor.h === anchor.h) return poolFor.list;
    poolFor = { h: anchor.h, list: JE.layerPairs(PREP, anchor, { limit: 999, min: 'good', availableOnly: true }) };
    return poolFor.list;
  }
  function seamlessRank(it) {
    const shared = ((it.report && it.report.shared) || []).length;
    return fv(it, 'bridge') * 2 + fv(it, 'bridgeSpecificity') + fv(it, 'accordGraph') + shared * 1.5;
  }
  function boldRank(it) {
    const shared = ((it.report && it.report.shared) || []).length;
    return fv(it, 'contrast') * 2 - shared * 1.2;
  }
  function vibePool(base, vibe) {
    if (vibe === 'one') return base.filter((it) => ((it.report && it.report.shared) || []).length > 0);
    if (vibe === 'loud') return base.filter((it) => fv(it, 'contrast') > 0);
    if (vibe === 'day' || vibe === 'night') {
      return base.filter((it) => {
        const w = wearSide(it.p);
        return w === vibe || w === 'both';
      });
    }
    return base.slice();
  }
  function choose(anchor, vibe) {
    const base = pairsFor(anchor);
    let pool = vibePool(base, vibe);
    RELAXED = !pool.length && base.length > 0;
    if (RELAXED) pool = base.slice();
    if (vibe === 'one') pool.sort((x, y) => seamlessRank(y) - seamlessRank(x) || y.score - x.score || x.p.h.localeCompare(y.p.h));
    else if (vibe === 'loud') pool.sort((x, y) => boldRank(y) - boldRank(x) || y.score - x.score || x.p.h.localeCompare(y.p.h));
    return pool;
  }
  function relaxSay(vibe) {
    if (vibe === 'day') return 'Nothing daytime-only worked, so this is the best pair at any hour.';
    if (vibe === 'night') return 'Nothing evening-only worked, so this is the best pair at any hour.';
    if (vibe === 'one') return 'No note in common, so this pair leans on family and weight instead.';
    return 'Nothing fit that exactly, so we widened the search.';
  }
  function tinyWhy(it) {
    /* a cross-shelf item's caption IS its disclosure, everywhere it appears */
    if (it.cross) return it.reason;
    const shared = (it.report && it.report.shared) || [];
    if (shared.length) return cap(String(shared[0]).toLowerCase()) + ' bridge';
    if (fv(it, 'contrast') > 0) return 'Bold contrast';
    return 'Same family';
  }

  /* ---- the room: panels push toward the seam ---- */
  function panels() { return Array.from(roomEl.querySelectorAll('.dt-panel')); }
  function serverPanel(step) { return roomEl.querySelector('[data-dt-panel="' + step + '"]'); }
  function trail() {
    if (!trailEl) return;
    const i = TRAILAT[S.at] != null ? TRAILAT[S.at] : -1;
    Array.from(trailEl.children).forEach((t, j) => {
      t.classList.toggle('is-done', i > j);
      t.classList.toggle('is-live', i === j);
    });
  }
  function whereWord() {
    if (S.at === 'pick') return S.mode === 'own' ? 'Your bottle' : 'The bottle';
    /* Same two renames as the finder's WHERE map (owner, 2026-08-28) - this
       room asks the same two questions and was using the same house jargon
       for them. The step keys stay qshelf / qchar: they are the markup's own
       names and are matched in five other places. */
    if (S.at === 'qshelf') return 'Preference';
    if (S.at === 'qchar') return 'Style';
    if (S.at === 'qwhen') return 'When you wear it';
    if (S.at === 'vibe') return 'The vibe';
    if (S.at === 'duet' && CURRENT) {
      const a = PREP.byHandle[S.anchor];
      return a ? a.t + ' + ' + CURRENT.p.t : '';
    }
    return '';
  }
  function backable() {
    const on = S.at !== 'cover';
    if (backBtn) backBtn.classList.toggle('is-usable', on);
    if (edgeBtn) edgeBtn.classList.toggle('is-usable', on);
  }
  function syncUrl() {
    if (!('replaceState' in history)) return;
    const q = new URLSearchParams();
    if (S.anchor) q.set('scent', S.anchor);
    if (S.vibe) q.set('vibe', S.vibe);
    if (S.at === 'duet' && CURRENT) q.set('with', CURRENT.p.h);
    if (S.mode === 'own') q.set('mode', 'own');
    const qs = q.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
  }
  function go(step, dir) {
    if (step === 'pick') warmDeck();
    const next = step === 'vibe' ? buildVibe()
      : step === 'duet' ? buildDuet()
      : step === 'qshelf' || step === 'qchar' || step === 'qwhen' ? buildQuiz(step)
      : serverPanel(step);
    if (!next) return;
    const cur = roomEl.querySelector('.dt-panel.is-on');
    if (cur === next) return;
    dir = dir || 1;
    if (jnAbort) { jnAbort.abort(); jnAbort = null; }
    next.style.setProperty('--dir', String(dir));
    if (cur) {
      cur.style.setProperty('--dir', String(dir));
      cur.classList.remove('is-on');
      cur.classList.add('is-out');
      cur.inert = true;
      if (cur.dataset.dtBuilt) window.setTimeout(() => cur.remove(), reduceMotion.matches ? 0 : 680);
      else {
        /* a server panel that has finished leaving goes to rest, which is
           what lets duet.css stop laying it out (A PANEL AT REST) */
        window.clearTimeout(cur._restT);
        cur._restT = window.setTimeout(() => {
          if (!cur.classList.contains('is-on')) cur.classList.add('is-rest');
        }, reduceMotion.matches ? 0 : 700);
      }
    }
    window.clearTimeout(next._restT);
    next.classList.remove('is-out', 'is-rest');
    /* a style flush fixes the slide's from-state; `offsetWidth` laid the
       whole room out for it, the deck's 41 tiles included (2026-09-22,
       lane 8 - see the finder's go() for the whole note) */
    void getComputedStyle(next).opacity;
    next.classList.add('is-on');
    next.inert = false;
    const prev = S.at;
    S.at = step;
    root.dataset.at = step;
    /* the records and Jennie's brief wait for the frame (afterFrame) */
    const via = S.came === 'quiz' ? 'quiz' : cameVia;
    const brief = loomBrief();
    afterFrame(() => {
      loomSay('x.duet.step', { subject: step, from: prev || '', dir: dir, brief: brief });
      if (step === 'duet') sayReveal(via);
    });
    trail();
    if (whereEl) whereEl.textContent = whereWord();
    backable();
    syncUrl();
    /* every step is a change to what she would arrive holding (D12) */
    afterFrame(() => { if (S.at === step) paintDuetJennie(); });
    say('');
    const q = next.querySelector('.dt-q');
    if (q) { q.tabIndex = -1; q.focus({ preventScroll: true }); }
  }
  function back() {
    if (S.at === 'duet') { go(S.came === 'quiz' ? 'qwhen' : 'vibe', -1); return; }
    if (S.at === 'vibe') { go(S.came === 'quiz' ? 'qwhen' : 'pick', -1); return; }
    if (S.at === 'qwhen') { go('qchar', -1); return; }
    if (S.at === 'qchar') { go('qshelf', -1); return; }
    if (S.at === 'qshelf') { go('cover', -1); return; }
    if (S.at === 'pick') go('cover', -1);
  }

  /* ---- the deck: search and the family words ---- */
  let fam = '';
  function sift() {
    const q = search ? JE.norm(search.value || '') : '';
    let shown = 0;
    tiles.forEach((t) => {
      const okQ = !q || JE.norm(t.dataset.title).indexOf(q) !== -1;
      const okF = !fam || (t.dataset.fam || '') === fam;
      const on = okQ && okF;
      t.hidden = !on;
      if (on) shown++;
    });
    if (none) none.hidden = shown !== 0;
  }
  if (search) {
    let t;
    search.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(sift, 110);
    });
  }

  /* ---- THE QUIZ - create a new pair without naming a bottle ----
     (owner, 2026-08-26). Three questions in this room's own grammar, the
     finder's fastest three: the shelf (multi-select, and it GATES the pair
     - masculine asks masculine or neutral, nothing else, unless the engine
     has to cross and says so), the character, the moment. briefDuo picks
     both bottles: the lead by the brief, the partner through the gated
     layering model, so the pair lands already obeying every house rule. */
  function famVocab() {
    const out = [];
    root.querySelectorAll('[data-dt-fam]').forEach((c) => {
      const v = c.dataset.dtFam || '';
      if (!v) return;
      /* the count is the deck's own: how many bottles carry this family.
         Derived, so the chip can say what choosing it actually spans. */
      const n = tiles.filter((t) => (t.dataset.fam || '') === v).length;
      out.push({ v: v, k: c.textContent.trim(), n: n });
    });
    if (out.length) return out;
    const seen = new Set();
    PREP.list.forEach((p) => {
      const d = p.profile && p.profile.dominant && p.profile.dominant[0];
      if (d && !seen.has(d)) { seen.add(d); out.push({ v: d, k: cap(d), n: 0 }); }
    });
    return out;
  }
  /* three faint bottles adrift behind every quiz panel, so the questions
     are asked IN the range rather than on a blank page - the vibe panel's
     own ghost grammar, widened */
  function quizBed() {
    const bed = el('div', 'dt-quiz__bed');
    bed.setAttribute('aria-hidden', 'true');
    /* CUT-OUTS ONLY. The bed is drawn at 14% opacity, which makes each image
       its own stacking context - and this file's measured law is that a
       stacking context between a packshot and the panel leaves its #F2F2F2
       bed printing as a rectangle (duet.css, THE LIGHT FIELD). A transparent
       PNG has no bed to print and needs no blend at all, so the bed only ever
       takes those and simply skips a bottle that has none. */
    const srcs = [];
    const step = Math.max(1, Math.floor(tiles.length / 3));
    for (let pass = 0; pass < 2 && srcs.length < 3; pass += 1) {
      for (let i = 0; i < tiles.length && srcs.length < 3; i += (pass ? 1 : step)) {
        const im = tiles[i] && tiles[i].querySelector('img');
        if (!im || !im.src || im.src.indexOf('-cut.') === -1) continue;
        if (srcs.indexOf(im.src) === -1) srcs.push(im.src);
      }
    }
    /* NO CUT-OUTS, NO BED. Forty of the forty-one have one, so this never
       runs today - and if the metafields ever went, the honest answer is a
       plain panel rather than three pale rectangles. A bedded plate at 12%
       opacity is its own stacking context, which is precisely the state this
       file measures as "the bed painting solid #F2F2F2 over the pool"; the
       decoration is optional and the artefact is not. */
    if (!srcs.length) return bed;
    srcs.forEach((src) => {
      const im = el('img');
      im.src = src; im.alt = ''; im.decoding = 'async'; im.loading = 'lazy';
      bed.appendChild(im);
      fitShot(im, 0.8, 0.88);
    });
    return bed;
  }
  function qLead(box, kicker, q, lede) {
    const lead = el('header', 'dt-pick__lead');
    lead.appendChild(order(el('p', 'dt-kicker', kicker), 0));
    lead.appendChild(el('h2', 'dt-q', q));
    if (lede) {
      const l = order(el('p', 'dt-lede', lede), 1);
      l.setAttribute('data-dt-rise', '');
      lead.appendChild(l);
    }
    box.appendChild(lead);
    return box;
  }
  function qRow(k, s, key, val, extra) {
    const d = btn('dt-qopt');
    d.setAttribute('data-dt-qpick', key);
    d.setAttribute('data-dt-qval', val);
    d.setAttribute('aria-pressed', 'false');
    if (extra) Object.keys(extra).forEach((a) => d.setAttribute(a, extra[a]));
    const w = el('span', 'dt-qopt__w');
    w.appendChild(el('span', 'dt-qopt__k', k));
    if (s) w.appendChild(el('span', 'dt-qopt__s', s));
    d.appendChild(w);
    d.appendChild(el('span', 'dt-qopt__a'));
    return d;
  }
  /* what the quiz can still honestly offer: the deck, gated by the shelf
     answer. Counts and dead-states on every later option read THIS, so a
     shelf choice narrows everything after it (owner, 2026-08-26: "we only
     want to show relevant things that we actually stock ... throughout") */
  function quizPool() {
    if (!PREP) return [];
    return PREP.list.filter((p) => p.noteSet && p.noteSet.size &&
      (!Q.for.length || !JE.audienceAdmits || JE.audienceAdmits(Q.for, p.g)));
  }
  function paintQuiz(panel) {
    if (!panel) return;
    const pool = quizPool();
    const admitted = new Set(pool.map((p) => p.h));
    panel.querySelectorAll('[data-dt-qpick]').forEach((n) => {
      const key = n.dataset.dtQpick;
      const val = n.dataset.dtQval || '';
      const held = Q[key];
      const on = n.hasAttribute('data-dt-qclear')
        ? Array.isArray(held) && held.length === 0
        : Array.isArray(held) ? held.indexOf(val) !== -1 : (held === val && val !== '');
      n.classList.toggle('is-on', on);
      if (n.hasAttribute('aria-pressed')) n.setAttribute('aria-pressed', on ? 'true' : 'false');
      /* the live number and the dead state, off the gated pool */
      if (!val || on) { n.classList.remove('is-dead'); if (!val) return; }
      let count = -1;
      if (key === 'character') {
        count = tiles.filter((t) => (t.dataset.fam || '') === val && admitted.has(t.dataset.dtPick)).length;
      } else if (key === 'season') {
        count = pool.filter((p) => p.se.indexOf(val) !== -1).length;
      } else if (key === 'when') {
        count = val === 'both' ? pool.length
          : pool.filter((p) => { const w = wearSide(p); return w === val || w === 'both'; }).length;
      } else if (key === 'for') {
        count = PREP.list.filter((p) => p.noteSet && p.noteSet.size &&
          JE.audienceAdmits && JE.audienceAdmits([val], p.g)).length;
      }
      if (count < 0) return;
      const num = n.querySelector('.dt-qchip__n');
      if (num) num.textContent = count ? String(count) : '';
      if (!on) n.classList.toggle('is-dead', count === 0);
    });
  }
  function qChip(k, key, val, n) {
    const c = btn('dt-qchip');
    c.setAttribute('data-dt-qpick', key);
    c.setAttribute('data-dt-qval', val);
    c.setAttribute('aria-pressed', 'false');
    c.appendChild(el('span', 'dt-qchip__tick'));
    c.appendChild(el('span', 'dt-qchip__k', k));
    /* always present, so paintQuiz can keep it LIVE as the shelf narrows */
    c.appendChild(el('b', 'dt-qchip__n', n ? String(n) : ''));
    return c;
  }
  function buildQuiz(step) {
    const box = el('section', 'dt-panel dt-panel--quiz');
    box.dataset.dtBuilt = '1';
    const scroll = el('div', 'dt-panel__scroll');
    const body = el('div', 'dt-quiz');
    /* THE BED BELONGS TO THE QUESTION, NOT TO THE SCREEN (2026-08-28, owner:
       three faint bottles printed straight through Continue and "Pick the
       bottle myself").

       It used to be appended to `box` - the panel - which is
       `position: absolute; inset: 0`, so `.dt-quiz__bed { inset: 0 }`
       resolved against the whole 390x664 phone and its `left: 78% / 88% /
       62%` bottles landed down the right edge and across the bottom, on the
       controls. `.dt-quiz { position: relative }` has existed in duet.css
       since the bed was written, for exactly this - it was simply never the
       bed's containing block. Measured live: bed 0,0 -> 390x664 against a
       quiz body of 20,62 -> 370,591. */
    body.appendChild(quizBed());

    /* ---- LEAD | ACT, WHICH IS THE FINDER'S SHAPE (2026-08-31) ----

       The quiz was one flat column: header, rows, Continue, the quiet word,
       all appended to `body`. On a phone that IS the design. On a desktop it
       left the question in a 522px strip on the left of a 1440px room with
       846px of nothing beside it - the owner's screenshot, and the "empty
       space right" they asked to have something happen in.

       The finder answers the same question with two pages: the copy holds one
       and the controls hold the other (finder.css, WIDER). The Duet's quiz
       now carries the same two boxes, so duet.css can make the same split
       with the same numbers, and the two rooms ask a question the same way.
       Everything that is not the header goes in here; nothing moves on a
       phone, where .dt-qact is simply the stack it replaced. ---- */
    const act = el('div', 'dt-qact');

    if (step === 'qshelf') {
      /* ---- ONE VOCABULARY, AND IT IS THE SHOPPER'S (2026-08-31, owner:
         "the copy is abit confusing and people don't understand it, like shelf
         needs to be switched to preference and stuff like that").

         "Which shelf?" is warehouse language: a shelf is where WE keep a
         bottle, and the sub-lines under it ("The feminine shelf") said the
         label again with the jargon added. The finder has asked this exact
         question in the shopper's own words since it was built - "What do you
         usually wear?" with "Written for a feminine wearer" underneath - and
         two rooms asking one question two ways is the confusion itself.

         "Show me everything" loses its sub outright: "No lean either way" is
         the same sentence with fewer of the words that matter. ---- */
      qLead(body, 'Create a new pair · One of three', 'What do you usually wear?',
        'Pick one or more. Gender neutral is always in the running.');
      const rows = el('div', 'dt-qopts');
      rows.appendChild(order(qRow('Feminine', 'Written for a feminine wearer', 'for', 'feminine'), 0));
      rows.appendChild(order(qRow('Masculine', 'Written for a masculine wearer', 'for', 'masculine'), 1));
      rows.appendChild(order(qRow('Gender Neutral', 'Made to work on anyone', 'for', 'gender neutral'), 2));
      rows.appendChild(order(qRow('Show me everything', '', 'for', '', { 'data-dt-qclear': '' }), 3));
      act.appendChild(rows);
      const goNext = btn('dt-qgo');
      goNext.appendChild(el('span', null, 'Continue'));
      goNext.appendChild(el('span', 'dt-qgo__a'));
      goNext.setAttribute('data-dt-qnext', 'qchar');
      act.appendChild(order(goNext, 5));
    } else if (step === 'qchar') {
      qLead(body, 'Create a new pair · Two of three', 'What should it smell like?',
        'Tap as many as pull you, or none.');
      const chips = el('div', 'dt-qchips');
      famVocab().forEach((f, i) => chips.appendChild(order(qChip(f.k, 'character', f.v, f.n), i)));
      act.appendChild(chips);
      const goNext = btn('dt-qgo');
      goNext.appendChild(el('span', null, 'Continue'));
      goNext.appendChild(el('span', 'dt-qgo__a'));
      goNext.setAttribute('data-dt-qnext', 'qwhen');
      act.appendChild(order(goNext, 8));
    } else {
      qLead(body, 'Create a new pair · Three of three', 'For when?',
        'Season if it matters, then the time of day.');
      const seasons = [];
      PREP.list.forEach((p) => p.se.forEach((s) => { if (seasons.indexOf(s) === -1) seasons.push(s); }));
      seasons.sort();
      if (seasons.length) {
        const chips = el('div', 'dt-qchips dt-qchips--aside');
        seasons.forEach((s, i) => {
          const c = qChip(cap(s), 'season', s, 0);
          if (CTX && CTX.season === s) c.appendChild(el('span', 'dt-nowtag', 'Now'));
          chips.appendChild(order(c, i));
        });
        act.appendChild(chips);
      }
      const rows = el('div', 'dt-qopts');
      rows.appendChild(order(qRow('Daytime', 'Work, errands, daylight', 'when', 'day'), 0));
      rows.appendChild(order(qRow('Evening', 'Dinner, dark, later', 'when', 'night'), 1));
      rows.appendChild(order(qRow('Both', 'It has to go everywhere', 'when', 'both'), 2));
      act.appendChild(rows);
      /* ---- THE LAST QUESTION ENDS ON A BUTTON, NOT ON A TAP ----
         (owner, 2026-09-02: "the quiz do not automatically go to the next
         step".)

         These three rows used to carry data-dt-qdone, which ran the whole
         quiz 300ms after one of them was touched. It made the third question
         behave unlike the two before it - Continue on one and two, nothing at
         all on three - and it put the single most expensive action in the
         room, the one that spends the answer and repaints it as a pair,
         behind a gesture the shopper had not finished making. Tapping Evening
         to see the counts move committed them to Evening.

         So the row is a row now, it toggles like every other single-choice
         row in the walk, and this button is the only thing that runs it. It
         says what it does rather than "Continue", because unlike the other
         two it does not go to another question - it goes to the answer. ---- */
      const goRun = btn('dt-qgo');
      goRun.appendChild(el('span', null, 'Show me the pair'));
      goRun.appendChild(el('span', 'dt-qgo__a'));
      goRun.setAttribute('data-dt-qrun', '');
      act.appendChild(order(goRun, 5));
    }

    const words = el('nav', 'dt-qwords');
    const deckWord = btn('dt-word dt-word--u', 'Pick the bottle myself');
    deckWord.setAttribute('data-dt-qdeck', '');
    words.appendChild(deckWord);
    act.appendChild(words);
    body.appendChild(act);

    scroll.appendChild(body);
    box.appendChild(scroll);
    slot.appendChild(box);
    paintQuiz(box);
    return box;
  }
  function runQuiz() {
    const brief = {
      audience: Q.for.slice(), families: Q.character.slice(),
      time: Q.when === 'both' ? '' : Q.when, season: Q.season
    };
    const duo = JE.briefDuo(PREP, brief, { limit: 6, pairLimit: 6, availableOnly: true });
    if (!duo || !duo.lead) {
      go('pick', 1);
      say('Nothing we make answers all of that at once - pick the bottle yourself and we will pair it.');
      return;
    }
    QSAID = (duo.match && duo.match.applied) || [];
    S.anchor = duo.lead.p.h;
    poolFor = { h: '', list: [] };
    S.came = 'quiz';
    PASSED = [];
    if (duo.partner) {
      RANKED = [duo.partner].concat(duo.alternates || []);
      CURRENT = duo.partner;
      S.vibe = '';
      go('duet', 1);
      /* the reveal was reached without a deck tap, so the view the deck
         would have minted is announced here instead - juce-loom listens */
      document.dispatchEvent(new CustomEvent('juce:loomview', {
        detail: { name: 'duet.view', subject: S.anchor, props: { via: 'quiz' } }
      }));
      return;
    }
    RANKED = null; CURRENT = null; S.vibe = '';
    go('vibe', 1);
    say('We found the scent; now pick how the pair should land.');
  }

  /* ---- chapter three, built: the vibe ----
     REBUILT 2026-08-21. Owner: "Feels like it is lacking alot, it needs to be
     a experience, remember mobile first". It was a kicker, a question and four
     ruled rows carrying a bare pool count - byte-for-byte the cover's own door
     grammar, so the third screen looked exactly like the first, said nothing
     only this screen could say, and put its subject in a 52px chip in the same
     top-right corner .dt-exit owns (they overlap once --dt-head bottoms out at
     52px on a short phone).

     Three changes, no new maths:
       the subject is the ground - the anchor packshot large and faint behind
         the question, so the middle of the phone carries the bottle being
         answered for instead of being empty, and the corner collision is gone;
       a derived lede - how many of the range actually cleared the bar, which
         is the one sentence only this panel can write;
       every door previews its real outcome - choose() already ranks each lens,
         so naming the bottle it would lead with (and showing it) turns four
         abstract words into four honest destinations. The count stays, because
         it is true and it is useful, but it reads as "14 pairs" rather than a
         numeral that looked like stock on hand.
     The rows stay in the thumb zone. That was never the bug.

     REBUILT AGAIN 2026-08-22. Owner: "How it should land is not nice, it
     doesnt make sense and not designed nicely... also it should not repeat
     the same product layering tho, those options should be dynamic."

     FOUR DOORS, FOUR BOTTLES. Two of the four lenses opened on the same
     bottle - Coastal Daze led both "As one scent" and "For daylight" - and
     four questions with two answers between them is not a question. lensPlan
     hands each lens a lead no other lens has taken, and the reveal then opens
     on exactly the bottle the row promised.

     IT DID NOT MAKE SENSE. "COASTAL DAZE LEADS · 10 PAIRS" set in 10px
     tracked caps is engine vocabulary. The row names its destination in the
     room's own voice now: the bottle in the card face, then a plain count.

     IT WAS NOT DESIGNED. A 48px thumbnail rode the right edge of the copy,
     small enough to read as an icon, and it carried the row's z-index so its
     packshot bed sat on the light field as a grey square. The figure leads
     the row now at up to 82px, and the row ends in the same arrow the cover's
     doors do, so it reads as a door rather than a list item. ---- */
  function lensPlan(anchor) {
    /* choose() IS the ranking the reveal will use, so a preview that called
       anything else could disagree with the screen it leads to. It sets
       RELAXED as a side effect though, and this runs four times before the
       shopper has chosen anything, so the flag is put back where it was. */
    const keep = RELAXED;
    const plan = VIBES.map((v) => {
      const ranked = choose(anchor, v.id);
      const widened = RELAXED;
      return { id: v.id, ranked: widened ? [] : ranked, lead: null };
    });
    RELAXED = keep;

    /* DISTINCT DOORS. The narrowest lens picks first, so a lens with three
       candidates is never left empty because a lens with thirty took its
       only one. Each then takes the best pair no earlier lens has claimed.

       This is a tie-break INSIDE a list the lens itself ranked - never a pair
       the lens did not vouch for - and the bottle that was passed over is
       still one tap away under "Other ways to wear it" on the reveal. */
    const taken = {};
    plan.slice()
      .sort((a, b) => a.ranked.length - b.ranked.length)
      .forEach((p) => {
        const pick = p.ranked.filter((it) => !taken[it.p.h])[0] || p.ranked[0] || null;
        if (!pick) return;
        p.lead = pick.p;
        taken[pick.p.h] = 1;
      });

    LEAD = {};
    plan.forEach((p) => { if (p.lead) LEAD[p.id] = p.lead.h; });
    return plan;
  }
  function buildVibe() {
    const anchor = PREP.byHandle[S.anchor];
    if (!anchor) return null;
    const base = pairsFor(anchor);
    const plan = lensPlan(anchor);
    const box = el('section', 'dt-panel dt-panel--vibe');
    box.dataset.dtBuilt = '1';

    const bed = el('div', 'dt-vibe__bed');
    bed.setAttribute('aria-hidden', 'true');
    const bimg = el('img');
    bimg.src = shot(anchor); bimg.alt = ''; bimg.decoding = 'async';
    bed.appendChild(bimg);
    fitShot(bimg, 0.86, 0.94);
    box.appendChild(bed);

    const scroll = el('div', 'dt-panel__scroll dt-vibe__scroll');
    const lead = el('header', 'dt-pick__lead');
    lead.appendChild(order(el('p', 'dt-kicker', 'The vibe'), 0));
    lead.appendChild(el('h2', 'dt-q', 'How should they land?'));
    const lede = order(el('p', 'dt-lede', base.length
      ? anchor.t + ' holds up against ' + base.length + ' of the range. Each of these opens on a different one of them.'
      : 'Nothing has cleared the bar next to ' + anchor.t + ' yet. Pick a way to wear it and we will widen the search.'), 1);
    lede.setAttribute('data-dt-rise', '');
    lead.appendChild(lede);
    scroll.appendChild(lead);

    const list = el('div', 'dt-vibes');
    VIBES.forEach((v, i) => {
      const p = plan.filter((x) => x.id === v.id)[0] || { ranked: [], lead: null };
      const d = order(btn('dt-vibe'), i);
      d.setAttribute('data-dt-vibe', v.id);

      const fig = el('span', 'dt-vibe__peek');
      if (p.lead) {
        const pi = el('img');
        pi.src = shot(p.lead); pi.alt = ''; pi.loading = 'lazy'; pi.decoding = 'async';
        fig.appendChild(pi);
        fitShot(pi, 0.82, 0.90);
      }
      d.appendChild(fig);

      const w = el('span', 'dt-vibe__w');
      w.appendChild(el('span', 'dt-vibe__k', v.k));
      w.appendChild(el('span', 'dt-vibe__s', v.s));
      const n = el('span', 'dt-vibe__n');
      if (p.lead) {
        /* three named parts, because the stylesheet draws the separator as a
           hairline rather than typing a middot at 10px, and the bottle's name
           is running text in --font-ui now (base.css:197, the Bebas rule) */
        n.appendChild(el('span', 'dt-vibe__pre', 'Opens on'));
        n.appendChild(el('em', 'dt-vibe__lead', p.lead.t));
        n.appendChild(el('span', 'dt-vibe__ct', p.ranked.length
          + (p.ranked.length === 1 ? ' pair' : ' pairs')));
        /* if the door's own lead had to cross the shelf, the door says so -
           the disclosure never waits for the reveal */
        const leadIt = p.ranked.filter((it) => it.p.h === p.lead.h)[0];
        if (leadIt && leadIt.cross) n.appendChild(el('span', 'dt-vibe__cross', 'Outside your preference'));
      } else {
        n.appendChild(el('span', 'dt-vibe__pre', 'Nothing held up on this one - we would widen it'));
      }
      w.appendChild(n);
      d.appendChild(w);

      d.appendChild(el('span', 'dt-vibe__a'));
      list.appendChild(d);
    });
    scroll.appendChild(list);
    box.appendChild(scroll);
    slot.appendChild(box);
    return box;
  }

  /* ---- THE STAGE ANSWERS THE POINTER, added 2026-08-22 ("add the pizazz
     animations and stuff"). steering/05 §1.4: the material responds to
     presence. The two bottles lean apart and together as the pointer crosses
     the reveal, which is the one gesture this screen is about, drawn.

     It is transform only, on two elements, rAF-batched to one write per
     frame, and it does not arm at all on a coarse pointer or under reduced
     motion - where the landed composition is the whole piece and nothing is
     missing. --tx / --ty default to 0px in the stylesheet, so an unarmed
     stage is simply the resting pose. ---- */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  let stageRaf = 0;
  function armStage(panel, stage) {
    if (reduceMotion.matches || !finePointer.matches) return;
    const figs = Array.from(stage.querySelectorAll('.dt-stage__shot img'));
    if (figs.length < 2) return;
    let px = 0, py = 0;
    const paint = () => {
      stageRaf = 0;
      figs.forEach((im, i) => {
        const away = i === 0 ? 1 : -1;
        im.style.setProperty('--tx', (px * 14 * away).toFixed(1) + 'px');
        im.style.setProperty('--ty', (py * 9).toFixed(1) + 'px');
      });
    };
    const queue = () => { if (!stageRaf) stageRaf = requestAnimationFrame(paint); };
    panel.addEventListener('pointermove', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const b = panel.getBoundingClientRect();
      if (!b.width || !b.height) return;
      px = (e.clientX - b.left) / b.width - 0.5;
      py = (e.clientY - b.top) / b.height - 0.5;
      queue();
    }, { passive: true });
    panel.addEventListener('pointerleave', () => { px = 0; py = 0; queue(); }, { passive: true });
  }

  /* ============================================================
     D12 - JENNIE ARRIVES ALREADY HOLDING THE PAIR.

     (owner, 2026-08-26: "On both when chatting with jennie, she needs to have
     context of what was selected and not liked and liked, not just a message
     that opens but a action that opens the chats and the things sent to
     jennie, and just a little bubble similar to add to cart one.")

     "On both" is the finder and this room, and the finder went first. This is
     the SAME mechanism, not a second one: the brief is serialised onto the
     section root, jennie.js reads it off the page with the one reader it uses
     for the finder, and it rides inside page.duet on every message. Nothing
     is invented here that the room does not already hold - the door they came
     through, the bottle they picked, the vibe they pressed, the partner drawn
     for them, the ones they stepped back from, and the verdict this page is
     already printing at them.

     Three things follow from putting it on the root rather than in a prompt:

       it rides EVERY message from this page, so she still has the pair four
         questions in, not only on the first;
       it obeys the panel's own "share what I am looking at" switch, because
         it lives in exactly the object that switch governs. A shopper who
         clears context sends none of it;
       nothing durable is read or written. Every value below is a control
         pressed on this screen in the last minute.
     ============================================================ */
  function pass(item) {
    const t = item && item.p && item.p.t;
    if (!t || PASSED.indexOf(t) !== -1) return;
    PASSED.push(t);
    if (PASSED.length > 5) PASSED.shift();
  }
  /* The brief as STRUCTURE. Bounded here as well as in the panel and again on
     the server: three cuts, because this is the only one of the three that
     knows the room. */
  function duetBrief() {
    if (!PREP) return null;
    const anchor = PREP.byHandle[S.anchor];
    const item = CURRENT;
    const p = item && item.p;
    const rep = (item && item.report) || {};
    const out = {
      /* the surface names itself so one reader can place it - see
         surfaceBrief() in assets/jennie.js */
      surface: 'duet',
      /* which door they came through. "own" is I already have one, and it
         changes what the room is selling as well as what she should say.
         Held back on the cover: S.mode is 'fresh' from the first line of this
         controller, and a shopper standing at two unpressed doors has not
         told us which one they want. */
      mode: S.at === 'cover' ? undefined : (S.mode === 'own' ? 'own' : 'fresh'),
      anchor: anchor ? anchor.t : undefined,
      anchor_handle: anchor ? anchor.h : undefined,
      partner: p ? p.t : undefined,
      partner_handle: p ? p.h : undefined,
      /* the words on the row they pressed, never the slug underneath it */
      vibe: S.vibe ? (VIBE_K[S.vibe] || undefined) : undefined,
      verdict: item ? (item.verdict === 'great' ? 'Made for each other' : 'Wears well') : undefined,
      why: item ? (item.why && item.why.long ? item.why.long : item.reason) : undefined,
      shared: (rep.shared || [])[0] ? cap(String((rep.shared || [])[0]).toLowerCase()) : undefined,
      /* the honest caveats this page prints, carried so she cannot contradict
         the screen: the lens emptied and was widened, and the partner had to
         be reached for across the shelf */
      relaxed: item && RELAXED ? true : undefined,
      cross: item && item.cross ? ((JE.LABEL && JE.LABEL[item.shelf]) || cap(item.shelf || '')) : undefined,
      passed: PASSED.length ? PASSED.slice(0, 5) : undefined,
      runners_up: RANKED && item
        ? RANKED.filter((it) => it !== item).slice(0, 3).map((it) => it.p.t)
        : undefined
    };
    if (!out.runners_up || !out.runners_up.length) delete out.runners_up;
    /* the quiz answers, in the finder's own field names because they are the
       same four questions - one shape, so the server bounds both with one
       function and phrases both with one vocabulary */
    if (Q.for.length) out.shelf = Q.for.length === 1 ? Q.for[0] : Q.for.slice(0, 3);
    if (Q.character.length) out.character = Q.character.map((f) => String(f).split('|')[0]).slice(0, 8);
    if (Q.when) out.when = Q.when;
    if (Q.season) out.season = Q.season;
    if (S.came === 'quiz' && QSAID.length) out.said = QSAID.slice(0, 12);
    Object.keys(out).forEach((k) => { if (out[k] === undefined) delete out[k]; });
    /* the name of the room is not a brief. On the cover nothing has been
       chosen yet, and an object saying only "duet" is a key on the wire with
       no fact in it. */
    return Object.keys(out).length > 1 ? out : null;
  }
  /* The same brief as chips - what the panel draws in the log, and what is
     printed on the button, so nothing travels unseen. Read off the room's own
     words rather than rebuilt from the values, the way the finder does it. */
  function duetChips() {
    const chips = [];
    const anchor = PREP && PREP.byHandle[S.anchor];
    const p = CURRENT && CURRENT.p;
    if (anchor && p) chips.push({ t: anchor.t + ' + ' + p.t });
    else if (anchor) chips.push({ t: anchor.t });
    if (S.vibe && VIBE_K[S.vibe]) chips.push({ t: VIBE_K[S.vibe] });
    if (S.mode === 'own' && anchor) chips.push({ t: 'Already owns ' + anchor.t });
    /* the quiz's answers, in the words the reveal is ALREADY printing two
       lines above this list (.dt-said, "You asked for: ..."). Read from QSAID
       rather than from the quiz panels: a panel is removed once it has been
       walked past, so a DOM read here kept the last screen's answers and
       silently dropped the shelf - and it picked up the "Now" tag inside the
       season chip, which is a label about today rather than a thing they
       chose. */
    if (S.came === 'quiz') QSAID.slice(0, 6).forEach((t) => chips.push({ t: String(t) }));
    PASSED.forEach((t) => chips.push({ t: t, no: true }));
    return chips;
  }
  /* Written on every step, not only on the reveal: a shopper who opens her
     from halfway through has still told the room something. */
  function paintDuetJennie() {
    const brief = duetBrief();
    try {
      if (brief) root.dataset.chatContext = JSON.stringify(brief);
      else delete root.dataset.chatContext;
    } catch (err) { delete root.dataset.chatContext; }
  }
  /* THE ACTION. Not a link to the chat with a sentence in the box: a module
     line in her log saying what was handed over, an event on the wire, and a
     bubble on this page confirming it - the same three beats an Add to bag
     has, which is the confirmation the owner pointed at. */
  function openDuetJennie(seam) {
    const chips = duetChips();
    const line = chips.map((c) => (c.no ? 'passed on ' + c.t : c.t)).join(', ');
    paintDuetJennie();
    /* The bubble is raised by handOffToJennie once the panel has taken it,
       never here: at this point nothing has been handed over yet. */
    handOffToJennie({
      source: 'layering',
      /* no composer prefill, ever - the hand-off block IS the call */
      prompt: '',
      fallback: (seam && seam.getAttribute('href')) || HELP,
      handoff: {
        surface: 'duet',
        line: line || '',
        answer: CURRENT && CURRENT.p ? CURRENT.p.t : ''
      }
    }, 'Jennie has the pair', 'Opening Jennie');
  }

  /* ---- the reveal: the duet itself ---- */
  function buildDuet() {
    const anchor = PREP.byHandle[S.anchor];
    if (!anchor) return null;
    if (!RANKED || !RANKED.length) RANKED = choose(anchor, S.vibe);
    /* the door promised a bottle by name and drew it; open on that one. It
       is always a member of this lens's own ranked list - lensPlan only ever
       tie-breaks within it - and LEAD is empty on a deep link straight to the
       reveal, where the top of the ranking is the right answer anyway. */
    if (!CURRENT && LEAD[S.vibe]) CURRENT = RANKED.filter((it) => it.p.h === LEAD[S.vibe])[0] || null;
    if (!CURRENT) CURRENT = RANKED[0] || null;
    if (!CURRENT) return null;
    const item = CURRENT;
    const p = item.p;
    const rep = item.report || {};
    const dir = item.directions || {};
    const first = PREP.byHandle[dir.first] || anchor;
    const second = PREP.byHandle[dir.second] || p;

    const box = el('section', 'dt-panel dt-panel--duet');
    box.dataset.dtBuilt = '1';
    const scroll = el('div', 'dt-panel__scroll');

    /* THE STAGE - the two bottles meet ON the seam, and the seam is drawn.
       It used to be one figure in dead space: both shots were sized off their
       HEIGHT with `width: auto`, so a 3413x2160 lifestyle file came out 1.6x
       as wide as tall and pushed its partner clean off the left of the stage
       (owner's screenshot: one lone bottle floating left of a big panel). Two
       scents are being named here, so the stage now draws two: square boxes
       that always fit their side, a hairline seam growing between them, and
       the note they actually share set on it. */
    /* EVERY BOTTLE ON THIS PAGE OPENS (owner, 2026-08-28: "ALL prouct images
       need to be clickable to show the popup and quick add to cart"). The two
       on the stage are the pair being recommended, so each one is a real
       button into its own peek - which carries what is in it, both prices and
       an Add. The stage itself stops being aria-hidden: it holds two named
       controls now, not decoration. */
    const stage = el('div', 'dt-stage');
    stage.appendChild(el('span', 'dt-stage__seam'));
    const shotA = btn('dt-stage__shot dt-stage__shot--a');
    shotA.setAttribute('data-dt-info', anchor.h);
    shotA.setAttribute('aria-label', 'About ' + anchor.t);
    const ia = el('img'); ia.src = shot(anchor); ia.alt = ''; ia.decoding = 'async';
    ia.setAttribute('data-lit', '');
    shotA.appendChild(ia);
    fitShot(ia, 0.88, 0.94);
    const shotB = btn('dt-stage__shot dt-stage__shot--b');
    shotB.setAttribute('data-dt-info', p.h);
    shotB.setAttribute('aria-label', 'About ' + p.t);
    const ib = el('img'); ib.src = shot(p); ib.alt = ''; ib.decoding = 'async';
    ib.setAttribute('data-lit', '');
    shotB.appendChild(ib);
    fitShot(ib, 0.88, 0.94);
    stage.appendChild(shotA);
    stage.appendChild(shotB);
    armStage(box, stage);
    /* the same one-light-two-bottles the PDP band uses. It rides alongside
       armStage above: that one moves the bottles (transform), this one moves
       the light on them (filter), so they never write the same property. */
    stage.setAttribute('data-pair-light', '');
    /* the light also breathes when nobody is touching it - see armPairLight.
       Opt-in per host, because the PDP band shares that function and lives on
       a page that scrolls, where a permanent idle loop is exactly the bargain
       this theme has already paid for once. This room never scrolls. */
    stage.setAttribute('data-lit-idle', '');
    armPairLight(stage);
    /* THE CAPTION, not the old ground-plated chip: the shared note said as a
       sentence, with only the note itself in ink (see .dt-stage__word). */
    const seamNote = (rep.shared || [])[0];
    const word = el('span', 'dt-stage__word');
    if (seamNote) {
      /* NOT "Both carry": the reason line four rows down opens with exactly
         those two words ("Both carry bergamot and musk..."), so the caption
         under the picture was the same sentence started twice. "They meet on"
         is the seam matrix's own phrasing, which is what this caption is a
         label for. */
      word.appendChild(el('span', 'dt-stage__pre', 'They meet on'));
      word.appendChild(el('span', null, cap(String(seamNote).toLowerCase())));
    } else {
      word.appendChild(el('span', 'dt-stage__pre', 'Where they meet'));
    }
    stage.appendChild(word);
    scroll.appendChild(stage);

    const rev = el('div', 'dt-rev');

    /* the stamp: the verdict, then the clock chips */
    const stamp = el('div', 'dt-stamp');
    stamp.appendChild(order(el('span', 'dt-stamp__v',
      item.verdict === 'great' ? 'Made for each other' : 'Wears well'), 0));
    let chips = [];
    try { const px = JE.pairContext ? JE.pairContext(PREP, anchor, p, { report: rep }) : null; chips = (px && px.chips) || []; }
    catch (err) { chips = []; }
    /* ---- EVERY CHIP MUST SAY SOMETHING NOTHING ELSE ON THE SCREEN SAYS
       (2026-08-31, owner: "Avoid text duplicate and text that doesn't have any
       value to the customer").

       Measured on the Desert Rose + Bold Ambition reveal: the shared note was
       stated FOUR times on one screen - the caption under the bottles ("Both
       carry Leather"), a chip ("Leather bridges"), the reason line ("Both
       carry leather and sandalwood...") and the fold repeating that reason
       word for word. And "Anchors it" / "Lifts it" is the wear order, which
       the two numbered rows underneath spell out with the bottle named.

       So the bridge chip stands down whenever the caption already carried the
       note, the role chip stands down always, and what is left is capped at
       two: the chips are the things the page has NOT otherwise said - the
       clock, the season, and whether it is a pairing we chose ourselves. ---- */
    chips = chips.filter((c) => c.k !== 'role' && !(c.k === 'bridge' && seamNote));
    chips.slice(0, 2).forEach((c, i) => stamp.appendChild(order(el('span', 'dt-tag', c.t), i + 1)));
    /* THE SHELF, SAID OUT LOUD. A partner the gate had to reach across the
       shelf for arrives flagged by the engine, and this room never shows it
       without saying so (owner: "it must explicitly let the customer know"). */
    if (item.cross) {
      stamp.appendChild(order(el('span', 'dt-tag dt-tag--cross',
        ((JE.LABEL && JE.LABEL[item.shelf]) || cap(item.shelf || '')) + ' scent'), 4));
    }
    rev.appendChild(stamp);

    const nameLine = el('h2', 'dt-pairname');
    nameLine.appendChild(document.createTextNode(anchor.t + ' '));
    nameLine.appendChild(el('em', null, '+'));
    nameLine.appendChild(document.createTextNode(' ' + p.t));
    rev.appendChild(nameLine);

    /* the quiz's read-back: what was asked for, in the shopper's own chips */
    if (S.came === 'quiz' && QSAID.length) {
      rev.appendChild(el('p', 'dt-fine dt-said', 'You asked for: ' + QSAID.slice(0, 8).join(' · ')));
    }

    const whyBits = [];
    if (item.cross && item.crossNote) whyBits.push(item.crossNote);
    if (RELAXED) whyBits.push(relaxSay(S.vibe));
    whyBits.push(item.why && item.why.long ? item.why.long : item.reason);
    rev.appendChild(el('p', 'dt-lede dt-why', whyBits.join(' ')));

    /* Jennie's line, then her door */
    const jn = el('div', 'dt-jn');
    const head = el('div', 'dt-jn__head');
    head.appendChild(el('span', 'dt-jn__dot'));
    head.appendChild(el('span', 'dt-jn__who', 'Jennie'));
    jn.appendChild(head);
    const sayEl = el('p', 'dt-jn__say is-thinking');
    const dots = el('span', 'dt-jn__dots');
    for (let i = 0; i < 3; i++) dots.appendChild(el('i'));
    sayEl.appendChild(dots);
    jn.appendChild(sayEl);
    /* WHAT IS ABOUT TO BE SENT, PRINTED (D12). A shopper is entitled to read
       what travels about them before it travels, and it is also the reason to
       press the button - "Send this to Jennie" over a list of their own
       choices asks for a tap in a way "Continue with Jennie" never did. */
    const jchips = duetChips();
    if (jchips.length) {
      const ul = el('ul', 'dt-jn__ctx');
      jchips.slice(0, 8).forEach((c) => {
        const li = el('li', c.no ? 'is-no' : null);
        /* A PASS READS AS A PASS WITHOUT THE RULE THROUGH IT. The line is the
           whole signal on screen, and a line is not read aloud and does not
           survive being copied out - so the word is carried, hidden, beside
           it. Same treatment as the finder's own chip list. */
        if (c.no) li.appendChild(el('span', 'visually-hidden', 'Passed on '));
        li.appendChild(document.createTextNode(c.t));
        ul.appendChild(li);
      });
      jn.appendChild(ul);
    }
    const goJn = el('a', 'dt-jn__go', jchips.length ? 'Send this to Jennie' : 'Ask Jennie');
    goJn.href = HELP;
    /* BOTH HOOKS, ON PURPOSE. data-chat-open is the theme's own seam and is
       what opens the panel when this controller is not running at all;
       data-dt-hand is the action, and the handler stops the event so the
       generic seam cannot open the panel a second time behind it. */
    goJn.setAttribute('data-chat-open', '');
    goJn.setAttribute('data-dt-hand', '');
    /* no data-chat-prompt: she opens clean and the hand-off block does the
       talking (owner, 2026-08-26) */
    jn.appendChild(goJn);
    rev.appendChild(jn);
    jennieNote(anchor, p, sayEl, item);

    /* how to wear it: two beats on one spine (duet.css, THE WEAR ORDER IS A
       SEQUENCE). The fourth field is the tag - WHERE the spray goes - which
       used to be buried in the middle of the sentence beside it. It is the
       one thing on the row a person actually acts on, so it stands on its
       own. */
    const wear = el('div', 'dt-wear');
    const rows = dir.flexible
      ? [[first, 'Either goes on first', 'Same weight on both sides, so the order is yours.', 'On the skin'],
         [second, 'The other over the top', 'One spray fewer of whichever goes second.', 'Over the top']]
      : [[first, first.t + ' goes on first', 'It anchors the pair and holds it down.', 'Chest and wrists'],
         [second, second.t + ' over the top', 'One spray fewer than you think. It is the part people meet first.', 'Over the top']];
    rows.forEach((r, i) => {
      const row = order(el('div', 'dt-wear__row'), i);
      row.appendChild(el('span', 'dt-wear__n', '0' + (i + 1)));
      const sh = btn('dt-wear__shot');
      sh.setAttribute('data-dt-info', r[0].h);
      sh.setAttribute('aria-label', 'About ' + r[0].t);
      const im = el('img'); im.src = shot(r[0]); im.alt = ''; im.decoding = 'async';
      sh.appendChild(im);
      /* 0.94 tall in a box that is finally the bottle's own shape, so the
         figure fills it instead of being fitted to a square */
      fitShot(im, 0.94, 0.9);
      row.appendChild(sh);
      const w = el('span', 'dt-wear__w');
      w.appendChild(el('span', 'dt-wear__k', r[1]));
      w.appendChild(el('span', 'dt-wear__c', r[2]));
      if (r[3]) w.appendChild(el('span', 'dt-wear__t', r[3]));
      row.appendChild(w);
      wear.appendChild(row);
    });
    rev.appendChild(wear);

    /* ---- THE TAIL - one ruled ladder under the answer, rebuilt 2026-08-21.
       Owner on the old one: the fold, three tiny bottles in a three-across
       grid and two loose words at the bottom left, all sitting in the same
       uniform gap. So the alternatives out-shouted the pair the page had just
       spent a screen arguing for, and the only navigation on the reveal was
       two 12px words with nothing framing them.

       It ranks by hairline now, which is this room's own grammar: the fold,
       then the alternates as ruled rows carrying a real 46px packshot and the
       reason they qualify, then a ruled foot row for the two ways back. The
       money stays where it was - the buy bar over the floor - and it is the
       only filled control on the screen, so what is primary is not in doubt.
       Copy is hard-coded here, not schema: this text is derived from the pair
       and has no meaning outside it. ---- */
    const tail = el('div', 'dt-tail');

    /* what holds them - a fold that opens IN the page, never over it */
    tail.appendChild(whyFold(anchor, p, item));

    /* the other doors this bottle could open */
    const alts = RANKED.filter((it) => it !== item).slice(0, 3);
    if (alts.length) {
      const wrap = el('section', 'dt-alts');
      wrap.appendChild(el('h3', 'dt-alts__k', 'Other ways to wear ' + anchor.t));
      const list = el('div', 'dt-alts__list');
      alts.forEach((it, i) => {
        const a = order(btn('dt-alt'), i);
        a.setAttribute('data-dt-alt', it.p.h);
        const sh = el('span', 'dt-alt__shot');
        const im = el('img'); im.src = shot(it.p); im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        sh.appendChild(im);
        fitShot(im, 0.86, 0.92);
        a.appendChild(sh);
        const w = el('span', 'dt-alt__w');
        w.appendChild(el('span', 'dt-alt__name', it.p.t));
        w.appendChild(el('span', 'dt-alt__why', tinyWhy(it)));
        a.appendChild(w);
        a.appendChild(el('span', 'dt-alt__a'));
        list.appendChild(a);
      });
      wrap.appendChild(list);
      tail.appendChild(wrap);
    }

    /* the two ways back, on a rule of their own so they are framed like
       everything else instead of floating off the bottom left */
    const links = el('nav', 'dt-revlinks');
    links.setAttribute('aria-label', 'Change this answer');
    const aboutA = btn('dt-word', 'About ' + anchor.t);
    aboutA.setAttribute('data-dt-info', anchor.h);
    links.appendChild(aboutA);
    const aboutB = btn('dt-word', 'About ' + p.t);
    aboutB.setAttribute('data-dt-info', p.h);
    links.appendChild(aboutB);
    const revibe = btn('dt-word dt-word--back', 'Change the vibe');
    revibe.setAttribute('data-dt-revibe', '');
    links.appendChild(revibe);
    const again = btn('dt-word', 'Start over');
    again.setAttribute('data-dt-again', '');
    links.appendChild(again);
    tail.appendChild(links);
    rev.appendChild(tail);

    scroll.appendChild(rev);
    box.appendChild(scroll);

    /* the buy bar fills once the bag has been read: what the door decided
       this page is selling, minus whatever is already in it */
    const bar = el('div', 'dt-buy');
    box.appendChild(bar);
    readBag().then((bag) => fillBuy(bar, anchor, p, bag));

    /* the pair is settled, so the brief on the root is too - the swap path
       calls buildDuet directly and must repaint the same way an arrival does */
    paintDuetJennie();

    slot.appendChild(box);
    return box;
  }

  /* ---- the money: mode-aware, bag-aware, the 10ml as the quiet second
     way in. The rule the owner set (2026-08-20): never offer to add what
     is already in the bag - the pair collapses to the missing half, and
     when nothing is missing the only action is opening the bag. ---- */
  function soloActs(rec) {
    const acts = [];
    if (rec.vid) acts.push({
      k: 'Add ' + rec.t, s: '50ml eau de parfum', pc: rec.pc,
      items: addItems(rec, '50'), main: true, h: rec.h, hands: [rec.h]
    });
    if (rec.mid) acts.push({
      k: 'Try it first', s: rec.t + ' in 10ml', pc: rec.mpc,
      items: addItems(rec, '10'), main: false, h: rec.h, hands: [rec.h]
    });
    return acts;
  }
  function fillBuy(bar, anchor, p, bag) {
    if (!bar.isConnected) return;
    bar.replaceChildren();
    const ownA = bag.handles.has(anchor.h);
    const ownB = bag.handles.has(p.h);
    let note = '';
    let acts = [];

    /* ---- THE PAIR IS ALWAYS BUYABLE (owner, 2026-08-28: "on new layering
       combo, button to add both") ----

       "I already have one" used to mean the bar could ONLY sell the partner:
       a shopper who walked in owning nothing, or who liked the pair enough to
       want a second bottle of the anchor, had no way to buy both from this
       screen at all. The mode still sets the hierarchy - the partner is the
       main action, because that is what they came for - and the pair rides
       under it as the quiet second yes, exactly as it does on the other side
       of the door. It is offered only when the anchor is not already in the
       bag, because the standing rule here is never to offer to add what is
       already in it. */
    if (S.mode === 'own') {
      if (ownB) note = p.t + ' is already in your cart.';
      else {
        acts = soloActs(p);
        if (!ownA && anchor.vid && p.vid) {
          acts.splice(1, 0, {
            k: 'Take the pair', s: 'Both in 50ml, testers in the box', pc: anchor.pc + p.pc,
            items: tagSet(addItems(anchor, '50').concat(addItems(p, '50')), 'The duet'),
            main: false, h: p.h, hands: [anchor.h, p.h]
          });
        }
      }
    } else if (ownA && ownB) {
      note = 'Both of these are already in your cart.';
    } else if (ownA) {
      note = anchor.t + ' is already in your cart.';
      acts = soloActs(p);
    } else if (ownB) {
      note = p.t + ' is already in your cart.';
      acts = soloActs(anchor);
    } else {
      if (anchor.vid && p.vid) acts.push({
        k: 'Take the pair', s: 'Both in 50ml, testers in the box', pc: anchor.pc + p.pc,
        items: tagSet(addItems(anchor, '50').concat(addItems(p, '50')), 'The duet'),
        main: true, h: p.h, hands: [anchor.h, p.h]
      });
      if (anchor.mid && p.mid) acts.push({
        k: 'Taste the duet', s: 'Both in 10ml', pc: anchor.mpc + p.mpc,
        items: tagSet(addItems(anchor, '10').concat(addItems(p, '10')), 'The duet'),
        main: false, h: p.h, hands: [anchor.h, p.h]
      });
      else if (anchor.vid && p.mid) acts.push({
        k: 'Start smaller', s: anchor.t + ' 50ml with ' + p.t + ' 10ml', pc: anchor.pc + p.mpc,
        items: tagSet(addItems(anchor, '50').concat(addItems(p, '10')), 'The duet'),
        main: false, h: p.h, hands: [anchor.h, p.h]
      });
    }

    if (note) bar.appendChild(el('p', 'dt-buy__note', note));
    acts.forEach((a) => {
      const b = btn('dt-buy__act' + (a.main ? '' : ' dt-buy__act--ghost'));
      b.setAttribute('data-dt-add', a.h);
      b._dtItems = a.items;
      b._dtHands = a.hands;
      const w = el('span', 'dt-buy__k');
      w.appendChild(el('span', null, a.k));
      w.appendChild(el('span', 'dt-buy__s', a.s));
      b.appendChild(w);
      b.appendChild(el('span', 'dt-buy__p', money(a.pc)));
      bar.appendChild(b);
    });
    /* THE FLOOR IS THE BAR'S OWN HEIGHT, MEASURED. .dt-rev used to reserve a
       flat 120px for it, which was two actions' worth; a third would have
       been laid straight over the last thing the shopper was reading. */
    window.setTimeout(() => {
      if (!bar.isConnected) return;
      const h = bar.offsetHeight;
      if (h) bar.parentElement.style.setProperty('--dt-buyh', h + 'px');
    }, 0);
    if (!acts.length) {
      if (ownA || ownB) {
        const b = btn('dt-buy__act');
        b.setAttribute('data-dt-bagopen', '');
        const w = el('span', 'dt-buy__k');
        w.appendChild(el('span', null, 'Open your cart'));
        w.appendChild(el('span', 'dt-buy__s', 'The pair is waiting in it'));
        b.appendChild(w);
        bar.appendChild(b);
      } else {
        const a = el('a', 'dt-buy__act', '');
        a.href = p.u;
        const w = el('span', 'dt-buy__k');
        w.appendChild(el('span', null, 'See ' + p.t));
        w.appendChild(el('span', 'dt-buy__s', 'Sizes on its own page'));
        a.appendChild(w);
        bar.appendChild(a);
      }
    }
  }
  /* a 50ml brings its own free 3ml tester, exactly as every other surface
     bundles it; a 10ml travels alone */
  function addItems(rec, size) {
    const items = [];
    if (size === '50' && rec.vid) {
      items.push({ id: Number(rec.vid), quantity: 1 });
      if (rec.sid) items.push({ id: Number(rec.sid), quantity: 1, properties: SAMPLE_PROPS });
    } else if (size === '10' && rec.mid) {
      items.push({ id: Number(rec.mid), quantity: 1 });
    }
    return items;
  }
  function addDuet(b) {
    if (b.classList.contains('is-working') || b.classList.contains('is-added')) return;
    const items = (b._dtItems || []).slice();
    if (!items.length) return;
    b.classList.add('is-working');
    const ticket = nextCartTicket();
    cartAdd(items)
      .then((res) => {
        /* if only a tester failed, the bottles still stand */
        if (res && res.status && res.status !== 200) {
          const bottles = items.filter((it) => !it.properties || !it.properties._free_sample);
          if (bottles.length && bottles.length !== items.length) return cartAdd(bottles);
        }
        return res;
      })
      .then((res) => {
        if (!res || (res.status && res.status !== 200)) {
          return Promise.reject(new Error((res && res.description) || 'add failed'));
        }
        b.classList.remove('is-working');
        b.classList.add('is-added');
        const k = b.querySelector('.dt-buy__k > span');
        if (k) k.textContent = 'In the cart';
        (b._dtHands || []).forEach((h) => BAG.handles.add(h));
        swapDrawer(res.sections, ticket);
        if (typeof res.item_count === 'number') setCartCount(cartBottles(res));
        openDrawerConfirm();
        spotlightLine(b.getAttribute('data-dt-add'));
      })
      .catch(() => {
        b.classList.remove('is-working');
        cartFail('We could not add that just now. Please try again.');
      });
  }

  /* ---- Jennie's one-liner. Her server first, the model's own words the
     moment she is off, slow or unreachable. The reveal never waits. ---- */
  /* ---- SHE MUST NOT SAY WHAT THE PAGE HAS ALREADY SAID (2026-08-31,
     owner: "Avoid text duplicate and text that doesn't have any value").

     The fallback was "Mystic Haze first, Coastal Daze over it. Bergamot
     bridges them." On a reveal that already prints "Both carry Bergamot"
     under the bottles, the same fact in the reason line, and "01 Mystic Haze
     goes on first" in a numbered row eighty pixels below, that is the fourth
     and fifth telling. Nobody reads a voice that repeats the page.

     So she says the thing nothing else on the screen does: WHEN to wear it,
     and how much. All of it is read off the same model - the clock the pair
     covers and its combined presence - so it is no less honest than the line
     it replaces, and it is the only sentence on the reveal a shopper cannot
     get from the diagram beside it. ---- */
  function localLine(anchor, p, item) {
    const dir = item.directions || {};
    const flexible = !!dir.flexible;
    const wa = wearSide(anchor), wb = wearSide(p);
    const day = wa === 'day' || wa === 'both' || wb === 'day' || wb === 'both';
    const night = wa === 'night' || wa === 'both' || wb === 'night' || wb === 'both';
    const clock = day && night ? 'It carries from the morning through to the evening.'
      : night ? 'Keep this one for the evening.'
      : day ? 'This is a daytime pairing.'
      : '';
    const order = flexible
      ? 'Either order works, so start with whichever you reach for.'
      : 'Two sprays of the first, one of the second, and let it settle before you decide.';
    return clock ? clock + ' ' + order : order;
  }
  function jennieNote(anchor, p, sayEl, item) {
    const seq = ++jnSeq;
    const land = (text) => {
      if (seq !== jnSeq || !sayEl.isConnected) return;
      sayEl.classList.remove('is-thinking');
      sayEl.classList.add('is-in');
      sayEl.textContent = text;
    };
    /* the local line waits a beat: buildDuet has not appended the panel yet
       when this runs, and a thought that lands mid-entrance reads as noise */
    if (!JN_EP) {
      window.setTimeout(() => land(localLine(anchor, p, item)), reduceMotion.matches ? 60 : 900);
      return;
    }
    if (jnAbort) jnAbort.abort();
    jnAbort = new AbortController();
    const timer = window.setTimeout(() => { if (jnAbort) jnAbort.abort(); }, 7000);
    fetch(JN_EP + '/api/client/layer-note?a=' + encodeURIComponent(anchor.h)
        + '&b=' + encodeURIComponent(p.h) + '&mode=' + encodeURIComponent(S.mode),
      { headers: { Accept: 'application/json' }, signal: jnAbort.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        window.clearTimeout(timer);
        land(d && d.note ? String(d.note) : localLine(anchor, p, item));
      })
      .catch(() => {
        window.clearTimeout(timer);
        land(localLine(anchor, p, item));
      });
  }

  /* ---- the why sheet: the seam, drawn ---- */
  const TIERS = ['top', 'heart', 'base'];
  function tierOf(rec, n) {
    if (!rec || !rec.tiers) return '';
    if (rec.tiers.base.indexOf(n) !== -1) return 'base';
    if (rec.tiers.heart.indexOf(n) !== -1) return 'heart';
    if (rec.tiers.top.indexOf(n) !== -1) return 'top';
    return '';
  }
  /* ONE RAIL, TWO MARKS (2026-08-31 - duet.css, THE SEAM, REDRAWN). `ta` is
     the tier the anchor seats the note in and `tb` the partner's; each cell
     lights the anchor's filled disc, the partner's open ring, or both
     concentric where the two bottles meet. A tier the model does not know
     simply lights nothing, which is the honest reading of "we cannot say". */
  function rail(ta, tb) {
    const l = el('span', 'dt-seam__rail');
    l.setAttribute('aria-hidden', 'true');
    TIERS.forEach((t) => {
      let cls = '';
      if (t === ta) cls += 'is-a';
      if (t === tb) cls += (cls ? ' ' : '') + 'is-b';
      l.appendChild(el('i', cls));
    });
    return l;
  }
  /* the same rail with the three tiers named, once, above the matrix */
  function railHead() {
    const l = el('span', 'dt-seam__rail');
    TIERS.forEach((t) => l.appendChild(el('i', null, t)));
    return l;
  }
  /* said in words for anyone the marks do not reach - and it is what a screen
     reader gets, because the rail itself is aria-hidden */
  function seamSay(ta, tb) {
    if (!ta && !tb) return 'Shared, tier unknown on both.';
    if (ta && tb && ta === tb) return 'Both at the ' + ta + '.';
    if (ta && tb) return ta.charAt(0).toUpperCase() + ta.slice(1) + ' and ' + tb + '.';
    return 'Seated at the ' + (ta || tb) + ' on one side only.';
  }
  function whyFold(anchor, p, item) {
    const rep = item.report || {};
    const d = el('details', 'dt-fold');
    const s = el('summary');
    s.appendChild(el('span', null, 'Why these two hold'));
    s.appendChild(el('span', 'dt-fold__i'));
    d.appendChild(s);
    const inn = el('div', 'dt-fold__in');
    /* NOT item.why.long: that sentence is already the reason line printed at
       the top of the reveal, and a fold that opens onto what you have just
       read is a fold nobody opens twice. The matrix below IS the answer to
       "why these two hold". */
    const shared = (rep.shared || []).slice(0, 4);
    if (shared.length) {
      const seam = el('div', 'dt-seam');
      const head = el('div', 'dt-seam__head');
      head.appendChild(el('span'));
      head.appendChild(railHead());
      seam.appendChild(head);
      shared.forEach((name) => {
        const n = JE.norm(name);
        const ta = tierOf(anchor, n), tb = tierOf(p, n);
        const row = el('div', 'dt-seam__row');
        const w = el('span');
        w.appendChild(el('span', 'dt-seam__note', cap(String(name).toLowerCase())));
        w.appendChild(el('span', 'visually-hidden', ' - ' + seamSay(ta, tb)));
        row.appendChild(w);
        row.appendChild(rail(ta, tb));
        seam.appendChild(row);
      });
      const key = el('div', 'dt-seam__key');
      key.appendChild(el('span', null, anchor.t));
      key.appendChild(el('span', null, p.t));
      seam.appendChild(key);
      inn.appendChild(seam);
      inn.appendChild(el('p', 'dt-fine',
        'Where each bottle carries the notes they share. Two marks on one cell is a note they meet on exactly, and the deeper down they meet, the longer the pair holds.'));
    } else {
      inn.appendChild(el('p', 'dt-fine', 'No note in common - the families and the weights are what hold these two together.'));
    }
    /* NOT the confidence label. "High confidence." is the model grading its
       own working, and the verdict at the top of the reveal ("Made for each
       other" / "Wears well") is that same reading in words a shopper can use.
       Two gradings of one pair, and only one of them means anything. */
    d.appendChild(inn);
    return d;
  }

  /* ---- one delegated ear for the whole room ---- */
  root.addEventListener('click', (e) => {
    /* D12 - HER DOOR IS AN ACTION. stopPropagation because it carries
       data-chat-open as well: that is the seam initJennie binds at document
       level, and it is the right behaviour when this controller is not
       running. With the controller running it would open the panel a second
       time behind the one openDuetJennie just opened. */
    const hand = e.target.closest('[data-dt-hand]');
    if (hand) { e.preventDefault(); e.stopPropagation(); openDuetJennie(hand); return; }

    const door = e.target.closest('[data-dt-mode]');
    if (door) {
      if (!PREP) return;                     /* static level: the anchor jump stands */
      e.preventDefault();
      S.mode = door.dataset.dtMode === 'own' ? 'own' : 'fresh';
      if (askEl) askEl.textContent = S.mode === 'own' ? 'Which one is yours?' : 'Which one pulls you?';
      if (stepword) stepword.textContent = S.mode === 'own' ? 'Your bottle' : 'The bottle';
      door.classList.add('is-going');
      window.setTimeout(() => {
        /* create-a-new-pair opens the QUIZ; the deck stays one tap inside
           it, and stays the whole no-script level (this branch never runs
           without PREP) */
        go(S.mode === 'fresh' ? 'qshelf' : 'pick', 1);
        door.classList.remove('is-going');
      }, reduceMotion.matches ? 0 : 300);
      return;
    }
    /* the peek - about one bottle, over the room. BEFORE the tile branch.
       The dot sits beside the tile's anchor now rather than inside it (see
       the deck's info dots, 2026-09-10), so a tap on it can no longer reach
       the tile - the order is kept anyway, so a dot never reads as a pick. */
    const dinfo = e.target.closest('[data-dt-info]');
    if (dinfo) {
      e.preventDefault();
      e.stopPropagation();
      const rec = PREP && PREP.byHandle[dinfo.getAttribute('data-dt-info')];
      if (rec) productPeek(rec, root);
      return;
    }

    const tile = e.target.closest('[data-dt-pick]');
    if (tile) {
      if (!PREP) return;                     /* static level: it is a product link */
      e.preventDefault();
      S.anchor = tile.dataset.dtPick;
      cameVia = 'deck';
      S.vibe = ''; RANKED = null; CURRENT = null; S.came = ''; PASSED = [];
      poolFor = { h: '', list: [] };
      tile.classList.add('is-picked');
      deck.classList.add('is-taken');
      window.setTimeout(() => {
        go('vibe', 1);
        window.setTimeout(() => {
          tile.classList.remove('is-picked');
          deck.classList.remove('is-taken');
        }, 700);
      }, reduceMotion.matches ? 0 : 340);
      return;
    }
    /* ---- the quiz's own gestures ---- */
    const qopt = e.target.closest('[data-dt-qpick]');
    if (qopt) {
      const key = qopt.dataset.dtQpick;
      const val = qopt.dataset.dtQval || '';
      if (qopt.hasAttribute('data-dt-qclear')) Q[key] = Array.isArray(Q[key]) ? [] : '';
      else if (Array.isArray(Q[key])) {
        const at2 = Q[key].indexOf(val);
        if (at2 === -1) Q[key].push(val); else Q[key].splice(at2, 1);
      } else Q[key] = Q[key] === val ? '' : val;
      /* AND NOTHING ELSE. Answering is answering; the panel is left where it
         is and the button below it is the way on (see THE LAST QUESTION ENDS
         ON A BUTTON in buildQuiz). */
      paintQuiz(qopt.closest('.dt-panel'));
      paintDuetJennie();
      return;
    }
    const qnext = e.target.closest('[data-dt-qnext]');
    if (qnext) { go(qnext.getAttribute('data-dt-qnext'), 1); return; }
    /* the quiz's last button: it does not go to a panel, it spends the brief */
    const qrun = e.target.closest('[data-dt-qrun]');
    if (qrun) {
      qrun.classList.add('is-going');
      window.setTimeout(runQuiz, reduceMotion.matches ? 0 : 300);
      return;
    }
    const qdeck = e.target.closest('[data-dt-qdeck]');
    if (qdeck) { go('pick', 1); return; }

    const chip = e.target.closest('[data-dt-fam]');
    if (chip) {
      fam = chip.dataset.dtFam || '';
      root.querySelectorAll('[data-dt-fam]').forEach((c) => c.classList.toggle('is-on', c === chip));
      sift();
      return;
    }
    const vibe = e.target.closest('[data-dt-vibe]');
    if (vibe) {
      S.vibe = vibe.dataset.dtVibe;
      RANKED = null; CURRENT = null;
      vibe.classList.add('is-going');
      window.setTimeout(() => go('duet', 1), reduceMotion.matches ? 0 : 300);
      return;
    }
    const alt = e.target.closest('[data-dt-alt]');
    if (alt) {
      const next = (RANKED || []).filter((it) => it.p.h === alt.dataset.dtAlt)[0];
      if (!next) return;
      /* the one leaving the stage is one they looked at and moved past */
      const passed = CURRENT && CURRENT.p ? CURRENT.p.h : '';
      pass(CURRENT);
      CURRENT = next;
      const cur = roomEl.querySelector('.dt-panel--duet');
      const fresh = buildDuet();
      if (cur && fresh) {
        /* the anchor is unchanged, so this is a swap and not an arrival:
           .is-swap holds side A still and re-reads the page in one beat */
        fresh.classList.add('is-swap');
        fresh.style.setProperty('--dir', '1');
        cur.classList.remove('is-on');
        cur.classList.add('is-out');
        cur.inert = true;
        window.setTimeout(() => cur.remove(), reduceMotion.matches ? 0 : 680);
        void fresh.offsetWidth;
        fresh.classList.add('is-on');
        if (whereEl) whereEl.textContent = whereWord();
        syncUrl();
        /* a swap is a reveal too, and the one that left is a soft no */
        sayReveal('alt', passed);
      }
      return;
    }
    const add = e.target.closest('[data-dt-add]');
    if (add && add.tagName === 'BUTTON') { e.preventDefault(); addDuet(add); return; }
    const bagOpen = e.target.closest('[data-dt-bagopen]');
    if (bagOpen) { e.preventDefault(); openDrawer(); return; }
    const revibe = e.target.closest('[data-dt-revibe]');
    if (revibe) { go('vibe', -1); return; }
    const again = e.target.closest('[data-dt-again]');
    if (again) {
      S.anchor = ''; S.vibe = ''; RANKED = null; CURRENT = null; S.came = ''; PASSED = [];
      go('cover', -1);
      return;
    }
    const bk = e.target.closest('[data-dt-back], [data-dt-edgeback]');
    if (bk) { back(); return; }
  });

  root.addEventListener('submit', (e) => {
    const form = e.target.closest('.dt-find');
    if (!form || !PREP) return;              /* no index: let it post to /search */
    e.preventDefault();
    const open = tiles.filter((t) => !t.hidden)[0];
    if (open) open.click();
  });

  /* ---- boot ---- */
  function readUrl() {
    let q;
    try { q = new URLSearchParams(location.search); } catch (err) { return {}; }
    return {
      scent: q.get('scent') || '',
      partner: q.get('with') || '',
      vibe: q.get('vibe') || '',
      mode: q.get('mode') || ''
    };
  }
  const want = readUrl();
  if (want.mode === 'own') S.mode = 'own';
  /* ---- A LINK THAT POINTS AT A STAGE LANDS ON IT (2026-08-31, owner: "on
     page load at spesific stage it must immediatelly show that stage we
     don't want layout lag, glitching and shifting and catchup and stuff").

     The deep link is resolved inside the index promise below, which is the
     only place it CAN be resolved - the room cannot know a handle is real
     until it holds the catalogue. What it does not have to do is arrive
     twice: the cover painted, then a few hundred milliseconds later the
     whole room slid 680ms sideways to the stage the URL had named all
     along. That second arrival is the "catchup".

     So the room is told NOW, synchronously, off the URL alone, that this
     load is pointed somewhere. `.is-jump` (duet.css / rooms-v2.css) turns
     off the panel transition and every entrance animation under it, so the
     landing is one frame with nothing moving, and the class is dropped on
     the frame after it so every step the shopper takes themselves animates
     exactly as before. ---- */
  const jumping = !!want.scent || location.hash === '#duet-pick';
  if (jumping) root.classList.add('is-jump');
  function landed() {
    if (!jumping) return;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      root.classList.remove('is-jump');
    }));
  }

  /* The range measures itself once, and it does it HERE rather than up with
     the deck sort: the fit cache is a `const` further down this function, so
     a call from above it lands in the temporal dead zone and takes the whole
     controller with it.

     The two cover bottles go first - they are the only figures on screen -
     and the other 41 follow half a second later, once the cover has settled.
     This was a requestIdleCallback and that was wrong twice over: a hidden
     tab is never idle in the sense rIC means, so nothing was measured at all
     until the page was looked at, and even on a busy visible page the callback
     can be starved past the tap that opens the deck. fitShot is idempotent
     and reads a cache, so warmDeck is also called when the deck first opens -
     whichever happens first wins and the second is free. */
  /* 0.70 on the cover, not the deck's 0.78: at 0.80 the two bottles ran into
     each other by 51px, and two glass figures blending `darken` through half
     a bottle of each other reads as one muddy object rather than two scents
     meeting. At 0.70 they overlap by about 18px - a kiss, which is the
     composition the cover was always described as making. */
  Array.from(root.querySelectorAll('.dt-hero img')).forEach((im) => fitShot(im, 0.70, 0.86));
  let deckWarm = false;
  /* THE RANGE STANDS DOWN A LITTLE, 2026-08-25 (owner, on "THE BOTTLE / Which
     one pulls you?": "on product selection, reduce the bottle size a bit").
     0.78 / 0.86 filled almost the whole square cell, so at two and three
     columns the bottles came within a few pixels of each other and the deck
     read as a wall rather than as a shelf. 0.66 / 0.74 is the same figure
     with air around it: about 15% smaller, which is a bit, not a redesign.
     The CSS fallback (--z, 1.14 in duet.css) is derived from the same ratio
     so the pre-measure frame agrees with the measured one. */
  function warmDeck() {
    if (deckWarm) return;
    deckWarm = true;
    tiles.forEach((t) => fitShot(t.querySelector('img'), 0.66, 0.74));
  }
  window.setTimeout(warmDeck, 500);

  juceIndex().then((prep) => {
    if (!prep || !prep.list.length) {
      say('We could not load the pairing brain just now, so every bottle above opens its own page instead.');
      return;
    }
    PREP = prep;
    CTX = JE.contextAt ? JE.contextAt() : null;
    root.classList.add('is-live');
    /* the deck's info dots - live-level only, because the peek reads the
       index.

       ---- BESIDE THE TILE'S LINK, NOT INSIDE IT (2026-09-10, audit T11) ----
       They used to be a span with role=button appended INTO the tile's <a>,
       because a real <button> there is invalid HTML. The role did not make it
       any less nested: a control inside a link is two controls sharing one
       name, a screen reader reads the link and never reaches the button in
       browse mode, and it took a hand-rolled Enter/Space handler to press it.

       So each tile gets a cell: a div that takes the tile's role="listitem"
       (the <a> goes back to being a link), holds the tile, and holds the dot
       as a real <button> next to it. The cell is the deck's grid item now and
       is a grid itself, so the tile still stretches to the row exactly as it
       did; position: relative on the cell puts the dot on the same corner of
       the same box (duet.css, .dt-cell). No transform, opacity or z-index on
       the cell - the light field's no-stacking-context law above the
       packshot holds. Done here, after the A-to-Z sort has already placed the
       tiles, and sift() keeps writing `hidden` on the tile: the cell follows
       it with :has() in duet.css. */
    tiles.forEach((t) => {
      const cell = el('div', 'dt-cell');
      cell.setAttribute('role', 'listitem');
      t.removeAttribute('role');
      t.parentNode.insertBefore(cell, t);
      cell.appendChild(t);
      const dot = el('button', 'dt-bottle__info', 'i');
      dot.type = 'button';
      dot.setAttribute('data-dt-info', t.dataset.dtPick);
      dot.setAttribute('aria-label', 'About ' + t.dataset.title);
      dot.setAttribute('data-loom', 'x.peek');
      dot.setAttribute('data-loom-props', JSON.stringify({ handle: t.dataset.dtPick, surface: 'duet' }));
      cell.appendChild(dot);
    });
    /* THE COUNT IS THE DECK, NOT THE INDEX. prep.list is the catalogue
       index, and the catalogue contains the gift card - so the cover said
       "42 bottles" over a deck of 41 (owner, 2026-08-22). The deck is
       rendered from products that actually carry top notes, which is the
       same test "a bottle" means here, so it is the honest number. */
    const bottles = tiles.length || prep.list.length;
    if (countEl) countEl.textContent = bottles + ' bottles. Every pairing measured, live.';
    /* the cover is the room's first panel and is never reached by go(), so
       its arrival is announced here (a deep link then adds its own) */
    root.dataset.at = S.at;
    loomSay('x.duet.step', { subject: S.at, from: '', dir: 0, brief: loomBrief() });
    if (S.mode === 'own' && askEl) askEl.textContent = 'Which one is yours?';

    /* a shared link opens where it points */
    if (want.scent && PREP.byHandle[want.scent]) {
      S.anchor = want.scent;
      cameVia = 'link';
      if (VIBE_K[want.vibe]) S.vibe = want.vibe;
      if (want.partner && PREP.byHandle[want.partner]) {
        if (!S.vibe) S.vibe = 'one';
        RANKED = choose(PREP.byHandle[S.anchor], S.vibe);
        CURRENT = RANKED.filter((it) => it.p.h === want.partner)[0] || null;
        if (CURRENT) { go('duet', 1); landed(); return; }
        go('vibe', 1);
        landed();
        say('We could not vouch for that exact pair, so pick the vibe and we will show the strongest ones.');
        return;
      }
      go('vibe', 1);
      landed();
      return;
    }
    /* the static :target flow may already be standing on the deck */
    if (location.hash === '#duet-pick') { go('pick', 1); landed(); }
    else landed();
  });
}

/* ============================================================
   FIND YOUR SCENT - THE CONSULTATION (/pages/find-your-scent).

   REBUILT 2026-08-21, and it is initDuet's sibling. What this replaces
   mounted a second front door: sections/scent-finder.liquid painted a
   whole stacked finder, then mountQuiz() appended a full-screen "Find
   yours." cover over it the moment juceIndex() resolved. Owner: "Is also
   weird it shows this ... And then this pops up."

   SO: ONE SYSTEM, FROM FIRST PAINT. The section server-renders the cover
   and all five questions AS PANELS of one room; this function ADOPTS them
   and builds only the chapter that cannot exist without the catalogue -
   the answer. Nothing mounts late. `.is-live` only arms interception and
   lights the live counts; it never changes what is already on screen.

   Two doors, one brief. A shopper who can say what they want types it on
   the cover; a shopper who cannot taps five questions. Both write to the
   SAME state object, which is why a sentence and a question can never
   disagree, and why the whole brief fits in the URL.

   NONE OF THE THINKING IS HERE. Every match, every percentage and every
   sentence comes back from JuceEngine.readBrief / matchBrief, off the same
   catalogue index the search popup, the shop filter and the layering room
   read. This collects answers, asks the engine, and paints.

   ONE SCENT (owner, 2026-08-25). The answer used to be a PAIR - briefDuo, two
   bottles on a seam, a wear order and "Take the pair" as the primary button -
   which answered a question nobody on this page had asked. Layering is the
   layering room's job and the answer now carries a DOOR into it with the
   scent already chosen. Layering is also why the run is one matchBrief call
   over the whole pool rather than a duo over four: the full result list is
   what the live note counts are derived from.

   FOUR THINGS IT REFUSES TO DO, because a finder is only worth using if
   it can be believed twice:
     · It never hides a miss. The answer prints what the scent does NOT
       do, above the buy bar rather than below it.
     · It never pretends a want was honoured. When holding one empties the
       catalogue the engine relaxes it and the page says which one.
     · It never shows a control that cannot act. The answer is built into
       an empty slot, the note toggles are hidden until the index lands,
       and NOTHING is intercepted until PREP exists - every option stays
       the real shop link it was rendered as. With no engine or no index
       the room flattens (.is-flat) back into that document.
     · It never draws a bottle from custom.main_product_image. Every
       figure here asks the index for `pimg`, the sellable variant's own
       packshot, exactly like layerShotSrc and the PDP plate.

   The whole brief lives in the URL, so an answer is shareable, Back
   leaves the page rather than unwinding a wizard one step at a time, and
   a shopper who returns picks up where they left off.
   ============================================================ */
/* ============================================================
   THE PEEK - one product, told quickly, over whichever room asked.
   (owner, 2026-08-26: "Info CTA on products (popup) - a quick popup that
   tells about the product", asked for on the layering page AND the finder.)

   One implementation for both rooms, and everything in it is DERIVED from
   the prepared index record - the shelf, the pyramid, how far it carries,
   the seasons, the money. Nothing here is written copy, so it can never
   disagree with the engine that recommended the bottle.

   IT NEVER NAMES ANOTHER HOUSE. This sheet used to carry an echo line -
   "In the spirit of Aventus by Creed", built from the `dp`/`br` fields -
   and it was the last surface in the shop that still said it out loud
   after quick search dropped the same two sentences (owner, 2026-08-28:
   "remove all references of inspired by and able to search by brand
   name"). The fields stay in the row and stay scored, so typing a
   designer's name still finds the bottle; nothing PRINTS them. There is
   no replacement line, because a euphemism is the same reference with
   better manners.

   A native <dialog>, shown modal: the top layer escapes the panels' own
   transforms (a fixed sheet inside a transformed panel is clipped to it),
   focus is trapped for free, and Esc already works. It is appended INSIDE
   the room's own section so the measurement ladder still reads the right
   surface off its ancestors, and the Add buttons ride the ordinary
   [data-pair-add] delegate - same items, same tester promise, same drawer
   confirmation as every other add in the theme.
   ============================================================ */
let peekOpenEl = null;
function peekShotSrc(rec, w) {
  const src = String((rec && (rec.png || rec.pimg || rec.img)) || '');
  return src ? src.replace(/width=\d+/, 'width=' + w) : '';
}
function productPeek(rec, mount) {
  if (!rec || !window.JuceEngine) return;
  const JEx = window.JuceEngine;
  const mk = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  const capWord = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  if (peekOpenEl) {
    try { if (peekOpenEl.open) peekOpenEl.close(); else peekOpenEl.remove(); }
    catch (err) { /* already gone */ }
    peekOpenEl = null;
  }

  const d = mk('dialog', 'pv');
  d.setAttribute('aria-label', 'About ' + rec.t);

  const inner = mk('div', 'pv__in');

  const head = mk('div', 'pv__head');
  const shot = mk('span', 'pv__shot');
  const src = peekShotSrc(rec, 480);
  if (src) {
    const im = mk('img');
    im.src = src; im.alt = ''; im.decoding = 'async';
    im.width = 120; im.height = 150;
    shot.appendChild(im);
  }
  head.appendChild(shot);
  const hw = mk('div', 'pv__hw');
  hw.appendChild(mk('h3', 'pv__name', rec.t));
  const tags = mk('div', 'pv__tags');
  const shelf = JEx.shelfOf ? JEx.shelfOf(rec) : (rec.g || 'gender neutral');
  tags.appendChild(mk('span', 'pv__tag', (JEx.LABEL && JEx.LABEL[shelf]) || capWord(shelf)));
  if (rec.raw && rec.raw.c) tags.appendChild(mk('span', 'pv__tag', String(rec.raw.c)));
  hw.appendChild(tags);
  /* how far it carries - the profile model's own read, in the finder's words */
  const pres = JEx.presence ? JEx.presence(rec) : 0.5;
  hw.appendChild(mk('p', 'pv__fine', pres < 0.42 ? 'Sits close to the skin'
    : pres > 0.66 ? 'Projects, and stays all day' : 'Present without shouting'));
  head.appendChild(hw);
  const x = mk('button', 'pv__x');
  x.type = 'button';
  x.setAttribute('aria-label', 'Close');
  head.appendChild(x);
  inner.appendChild(head);

  const pyr = mk('div', 'pv__pyr');
  [['top', 'Opens on'], ['heart', 'At the heart'], ['base', 'Rests on']].forEach((row) => {
    const names = (rec.tiers && rec.tiers[row[0]]) || [];
    if (!names.length) return;
    const r = mk('div', 'pv__row');
    r.appendChild(mk('span', 'pv__k', row[1]));
    r.appendChild(mk('span', 'pv__n', names.slice(0, 4).map(capWord).join(', ')));
    pyr.appendChild(r);
  });
  inner.appendChild(pyr);

  const when = [];
  (rec.se || []).forEach((s) => when.push(capWord(s)));
  (rec.ti || []).forEach((t) => when.push(t === 'night' ? 'Evening' : 'Daytime'));
  if (when.length) {
    const chips = mk('div', 'pv__chips');
    when.slice(0, 5).forEach((w) => chips.appendChild(mk('span', 'pv__chip', w)));
    inner.appendChild(chips);
  }

  const buy = mk('div', 'pv__buy');
  if (rec.vid) {
    const add = mk('button', 'pv__act', 'Add ' + rec.t + (rec.pc > 0 ? ' · ' + JEx.money(rec.pc) : ''));
    add.type = 'button';
    add.setAttribute('data-pair-add', rec.h);
    add.setAttribute('data-pair-open', '');
    add.setAttribute('data-pair-size', '50ml');
    add.setAttribute('data-variant-id', rec.vid);
    if (rec.sid) add.setAttribute('data-sample-id', rec.sid);
    buy.appendChild(add);
  }
  if (rec.mid && rec.mpc > 0) {
    const mini = mk('button', 'pv__act pv__act--ghost', 'Try the 10ml · ' + JEx.money(rec.mpc));
    mini.type = 'button';
    mini.setAttribute('data-pair-add', rec.h);
    mini.setAttribute('data-pair-open', '');
    mini.setAttribute('data-pair-size', '10ml');
    mini.setAttribute('data-variant-id', rec.mid);
    buy.appendChild(mini);
  }
  inner.appendChild(buy);

  const words = mk('div', 'pv__words');
  words.appendChild(Object.assign(mk('a', 'pv__word', 'Open its full page'), { href: rec.u || '#' }));
  inner.appendChild(words);

  d.appendChild(inner);
  /* a tap on the backdrop is a tap on the dialog element itself */
  d.addEventListener('click', (e) => { if (e.target === d || e.target === x) dialogShut(d); });
  d.addEventListener('close', () => {
    if (peekOpenEl === d) peekOpenEl = null;
    setTimeout(() => d.remove(), 60);
  });
  (mount || document.body).appendChild(d);
  peekOpenEl = d;
  try { d.showModal(); } catch (err) { d.setAttribute('open', ''); }
}

function initScentFinder() {
  const root = document.querySelector('[data-finder]');
  if (!root) return;

  const qs = (s) => root.querySelector(s);
  const qsa = (s) => Array.from(root.querySelectorAll(s));

  /* ---- the exit: Back on this site, home when Back is the other room
     (wireRoomExit above). Wired before anything can bail, because the way
     out of a room that owns the whole screen must never depend on the
     catalogue loading. ---- */
  wireRoomExit(root, qs('[data-fx-exit]'));

  const roomEl = qs('[data-fx-room]');
  const slot = qs('[data-fx-slot]');
  const textIn = qs('[data-fx-text]');
  const status = qs('[data-fx-status]');
  const noteList = qs('[data-fx-notelist]');
  const noteFind = qs('[data-fx-notefind]');
  const noteNone = qs('[data-fx-notenone]');
  const noteMore = qs('[data-fx-notemore]');
  const nebEl = qs('[data-fx-neb]');
  const nebField = qs('[data-fx-nebfield]');
  const nebPicks = qs('[data-fx-nebpicks]');
  const nebFind = qs('[data-fx-nebfind]');
  const jennie = qs('[data-fx-jennie]');
  const trailEl = qs('[data-fx-trail]');
  const whereEl = qs('[data-fx-where]');
  const backBtn = qs('[data-fx-back]');
  const edgeBtn = qs('[data-fx-edgeback]');
  const countEl = qs('[data-fx-count]');
  if (!roomEl || !slot) return;

  /* the Duet's ruling, one room along - see A PANEL THAT IS NOT SHOWING IS
     INERT in initDuet. The flattening below un-inerts every panel again,
     which is right: a flat room is a document and all of it is real. */
  roomEl.querySelectorAll('.sf-panel').forEach((p) => { p.inert = !p.classList.contains('is-on'); });

  /* THE FLATTENING. A room is only honest while it can answer. With no
     engine or no catalogue the panels stop being slides and the page falls
     back to what it always was underneath: a document of five questions in
     which every option is the shop link it was rendered as, with the site
     chrome back around it (assets/finder.css, .sf.is-flat). Nothing is
     removed and nothing dead is left on screen. */
  function flatten(msg) {
    root.classList.remove('is-live');
    root.classList.add('is-flat');
    syncInRoom();
    qsa('.sf-panel').forEach((p) => { p.classList.remove('is-out', 'is-rest'); p.inert = false; });
    armAnswers(false);
    say(msg || '');
  }
  /* ---- AN ANSWER IS A CHECKBOX ONLY WHILE IT ACTS LIKE ONE (2026-09-10,
     audit T11) ----
     Each answer is a real link into the collection, and the click handler leaves it
     one until PREP exists. From the moment the room is live a tap toggles it
     in place instead, which is a checkbox by any reading - so that is the role
     it wears then, with aria-checked kept by reflect(). A flattened room hands
     every option back to being a link. Space is wired below, because a link
     does not answer Space the way a checkbox must. */
  function armAnswers(on) {
    qsa('[data-fx-pick], [data-fx-toggle]').forEach((n) => {
      if (on) n.setAttribute('role', 'checkbox');
      else { n.removeAttribute('role'); n.removeAttribute('aria-checked'); }
    });
  }

  const LIMIT = Math.max(2, parseInt(root.dataset.limit, 10) || 3);
  const MIN_FIT = Math.max(0, parseInt(root.dataset.minFit, 10) || 0);
  const HELP = root.dataset.help || '/pages/help';
  const NOTES_SHOWN = 14;
  const TIER_K = { top: 'Top', heart: 'Heart', base: 'Base' };
  /* the five questions, in order, and the word the running head uses. There
     is no cover any more (owner, 2026-08-26: "just start with the quiz
     immediately") - the room opens ON the first question. */
  /* ---- FOUR QUESTIONS, AND NOTES IS A DETOUR OFF THE LAST ONE
     (2026-08-31, owner: "we want to omit the notes part from the wizard, and
     then have a cool animating glass button next to Show me my scent that
     says Refine through notes").

     The note field is the richest surface in this room and it was sitting in
     the MIDDLE of a walk that is meant to take ten seconds - a hundred and
     twenty-one words offered between "when do you wear it" and "how much room
     should it take up". Most shoppers want the four questions and an answer;
     the ones who want to say more want to say a lot more.

     So `notes` leaves STEPS. It keeps its panel, its trail behaviour and every
     control it had - `go('notes')` still works and is still where the nebula
     mounts - it is simply no longer on the way to anything. It is reached from
     the last question and it RETURNS there rather than continuing, which is
     what `noteFrom` remembers. ---- */
  const STEPS = ['for', 'character', 'when', 'presence'];
  const ORDER = STEPS.concat(['answer']);
  /* where the notes detour was entered from, so its own way out goes back */
  let noteFrom = 'presence';
  /* THE RUNNING HEAD'S WORD FOR EACH QUESTION. Owner, 2026-08-28: "Find your
     scent remove The character and The shelf terms". Both were house jargon
     wearing a definite article - "The shelf" is how the warehouse talks about
     Feminine / Masculine / Gender Neutral, and "The character" is a
     perfumer's word for what a shopper simply calls the style. Neither told
     anybody where they were. The same rename runs through GAVE_SAY below, so
     the relax sentence cannot go on saying a word this rail no longer uses. */
  const WHERE = {
    for: 'Preference', character: 'Style', when: 'When you wear it',
    notes: 'Notes', presence: 'Presence'
  };
  /* ---- AUTO-ADVANCE IS GONE (owner, 2026-09-02: "the quiz do not
     automatically go to the next step") ----

     THE TEN-SECOND WALK (owner, 2026-08-25: "a easy to use wizard that takes
     like 10 seconds") was built on it: a single-choice group that was the
     LAST control on its panel advanced 260ms after the tap, so there was no
     Continue to find and no dead beat between one question and the next. The
     panel declared itself with data-fx-auto, and a 260ms pause let the ink
     fill get visibly underway before the push.

     IT ONLY EVER APPLIED TO ONE PANEL, and that is the whole story. `for` is
     multi-select so it could never qualify; `character` is multi-select too;
     `presence` gave it up on 2026-08-31 the moment it grew a second door. So
     `when` was the ONE question in a four-question walk that moved on its
     own - and a wizard where three panels wait and the fourth jumps is not a
     fast wizard, it is an inconsistent one. A shopper who taps Evening to see
     what it does is moved off the panel before they can look at the count
     that just changed, and going Back is the only way to read their own
     answer. The 260ms that was meant to feel like one gesture is exactly long
     enough to start reading and not long enough to finish.

     Continue was already on the panel the whole time (it is how anyone who
     skipped the question got out), so nothing had to be built to replace
     this - the attribute came off the markup and this came out with it. ---- */
  /* THE LAYERING ROOM'S OWN URL CONTRACT (theme.js, initDuet: `?scent=<handle>`
     opens on the vibe question, `&mode=own` makes the reveal sell only the
     partner). Written once, here, so the finder can never drift from it.

     ---- AND IT MUST NOT SAY "own" (2026-08-27) ----
     (owner: "I did the quiz and then selected layer. Then at the end I could
     only add the new layer suggestion, not both.")

     This carried `&mode=own`, and own means one thing to the room: the shopper
     tapped "One of these is mine" on the cover, so the reveal sells them the
     PARTNER and nothing else (fillBuy's first branch, which never even looks
     at whether the anchor is in the bag). But nobody had said they owned
     anything. The finder had recommended that bottle thirty seconds earlier
     and this door is the next tap after reading about it, so the room was
     being told a fact about the shopper that the shopper had never stated -
     and the one thing they actually needed, both bottles, was unreachable.

     Nothing is added on the way through either, so `fresh` is simply true:
     they hold neither bottle. The reveal's own else-branch then offers "Take
     the pair" with "Taste the duet" under it, and readBag() still collapses
     that to whatever is genuinely missing if they add one half first. */
  function layerHref(handle) {
    return '/pages/layering?scent=' + encodeURIComponent(handle) + '&mode=fresh';
  }

  if (!JE) {
    flatten('We could not load the range just now, so the options below will take you into the shop instead.');
    return;
  }

  /* the moment the shopper is in: the southern-hemisphere season. It tags
     the matching season chip "Now" and lets a result say "In season now" -
     a fact beside the answer, never a want we invented for them. */
  const CTX = JE.contextAt ? JE.contextAt() : null;

  /* One object, both doors. Multi-value keys are arrays; everything else is
     a single value, and '' always means "not answered". The SHELF is an
     array now (owner, 2026-08-26: multiple preferences at once), and the
     spend key is gone with its question - an old link's pmax is ignored. */
  const S = { text: '', for: [], character: [], when: '', season: '', love: [], avoid: [], avoidFam: [], presence: '', gift: false };
  const URL_KEYS = { q: 'text', for: 'for', character: 'character', when: 'when', season: 'season', love: 'love', avoid: 'avoid', nofam: 'avoidFam', presence: 'presence' };
  const MULTI = { for: 1, character: 1, love: 1, avoid: 1, avoidFam: 1 };
  /* an option whose value is '' ("Not sure", "Both", "All year") is a real
     answer meaning no preference, so it only reads as chosen once that
     question has actually been touched */
  const touched = {};
  let PREP = null;
  /* THE DECK, NOT THE INDEX. ?section_id=search-index loops
     collections.all.products with no non-scents filter, so the catalogue index
     this page reads carries the gift card - 42 rows for 41 scents. The Duet hit
     the same thing on its cover and answered it the same way (owner, 2026-08-22:
     the cover said "42 bottles" over a deck of 41).

     Filtering HERE rather than in search-index.liquid is deliberate: that
     section feeds the search popup, the shop filter, the PDP lane, the layering
     room and the cart cross-sell, and changing it would move all of them and
     need a JUCE_INDEX_KEY bump on top. The test is the honest one - a scent has
     notes and the gift card is the only row in the shop with none - and it fixes
     more than the number. The gift card has g:'' , which p.g || 'gender neutral'
     turned into gender neutral, so it survived every audience filter and scored
     100% fit against a gender-neutral brief. It could be the answer. */
  let SCENTS = [];
  let DECK = [];            /* the same deck as handles - what opts.pool takes */
  let NOTEKEYS = null;      /* handle -> canonical note keys, built once */
  let RES = null;
  let at = 'for';
  let token = 0;
  let notesFolded = true;
  let noteRows = null;      /* the note list, cached at boot and never re-sorted */

  function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function anch(cls, href, text) { const a = el('a', cls, text); a.href = href || '#'; return a; }
  function btn(cls, text) { const b = el('button', cls, text); b.type = 'button'; return b; }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  /* THE ROOM MAKES ROOM FOR WHAT IT SAYS (2026-08-28). The status bar used
     to float over the bottom of the panel and land on the Continue button;
     it stands on its own floor now, and the floor only exists while there is
     something to say. The height is MEASURED rather than assumed, because a
     two-line message is a different bar from a one-line one, and it is
     written back onto the room as --sf-say so .sf-panel's own padding lifts
     off it. It clears itself, so a room nobody is talking to spends nothing.

     One caveat worth keeping: a message written during the same tick that
     rebuilt the panel would measure zero, so the read is deferred one frame
     with the same setTimeout the nebula uses (a frame callback does not fire
     in a background tab). */
  let sayTimer = 0;
  function say(msg) {
    if (!status) return;
    status.textContent = msg || '';
    status.hidden = !msg;
    root.style.setProperty('--sf-say', '0px');
    if (sayTimer) { window.clearTimeout(sayTimer); sayTimer = 0; }
    /* long enough to read twice, short enough to be a remark rather than a
       fixture. See the Duet's own say() and rooms-v2.css. */
    if (msg) sayTimer = window.setTimeout(() => { sayTimer = 0; say(''); }, 5500);
  }
  function noteKeyOf(node) { return node ? JE.canonicalNote(node.dataset.name || '') : ''; }

  /* THE PACKSHOT, NEVER THE LIFESTYLE FILE. `img` on an index row is the
     merchant's custom.main_product_image override, a 3413x2160 LANDSCAPE
     file on 40 of 42 products; `pimg` is the sellable variant's own bottle
     on the F2F2F2 bed (snippets/search-row.liquid preserves it precisely
     for this). Every figure on this page is a bottle, so every figure asks
     for pimg by name - same rule as layerShotSrc and the PDP plate. */
  function shotSrc(rec, w) {
    /* png first, same order as the layering surfaces. The finder draws its
       bottles over the room's own ground, so a bedded plate arrives with its
       plate. pimg stays the fallback for a size with no cutout. */
    const src = String((rec && (rec.png || rec.pimg || rec.img)) || '');
    if (!src) return '';
    return src.replace(/width=\d+/, 'width=' + w);
  }
  function shotImg(rec, w, h, lazy) {
    const src = shotSrc(rec, w * 2);
    if (!src) return null;
    const im = el('img');
    im.src = src;
    im.alt = '';
    im.decoding = 'async';
    if (lazy) im.loading = 'lazy';
    im.width = w; im.height = h;
    return im;
  }
  /* THE PHOTOGRAPH, NOT THE CUTOUT (owner, 2026-08-26: "show the product
     shot not the png on the left"). The answer presents the bottle the way
     the shop photographs it - on its bed, with its real shadow - and the
     bed melts into the room via the darken blend, so no plate ever shows.
     The zoom-crop in finder.css tightens the packshot's generous bed the
     same way the duet's measured fit does, statically. */
  function photoImg(rec, w, h, lazy) {
    const src = String((rec && (rec.pimg || rec.img)) || '');
    if (!src) return shotImg(rec, w, h, lazy);
    const im = el('img');
    im.src = src.replace(/width=\d+/, 'width=' + w * 2);
    im.alt = '';
    im.decoding = 'async';
    if (lazy) im.loading = 'lazy';
    im.width = w; im.height = h;
    return im;
  }

  /* ---- state <-> engine ---- */
  function brief() {
    return {
      audience: S.for.slice(), season: S.season, time: S.when,
      families: S.character, notes: S.love, avoid: S.avoid,
      avoidFamilies: S.avoidFam,
      intensity: S.presence, gift: S.gift, text: S.text
    };
  }
  function answered() {
    return !!(S.text || S.for.length || S.character.length || S.when || S.season ||
      S.love.length || S.avoid.length || S.avoidFam.length || S.presence || S.gift);
  }

  /* ---- what LOOM is told (see loomSay) ----
     The brief as it STANDS, sent with every panel change and with the answer.
     A tap is a gesture; this is the settled state after it, which is the
     only thing "what did people pick" can honestly be counted from - a
     shopper who tapped Feminine and tapped it off again picked nothing.
     `touched` and `skipped` are carried so an untouched question, a skipped
     one and one answered "anything" can be told apart. Free text never
     travels - only that there was some. */
  const skipped = {};
  function loomBrief() {
    const keys = (o) => Object.keys(o).filter((k) => o[k]);
    return {
      for: S.for.slice(0, 3),
      character: S.character.slice(0, 12),
      when: S.when || '', season: S.season || '', presence: S.presence || '',
      love: S.love.slice(0, 16), out: S.avoid.slice(0, 16), outfam: S.avoidFam.slice(0, 8),
      gift: !!S.gift, typed: !!S.text,
      touched: keys(touched), skipped: keys(skipped)
    };
  }
  /* a note or family after the cycle has run: the STATE it ended in, read
     off S rather than guessed from which tap it was */
  function sayNote(kind, key, via) {
    if (!key) return;
    const state = kind === 'family'
      ? (S.avoidFam.indexOf(key) !== -1 ? 'out' : 'clear')
      : (S.love.indexOf(key) !== -1 ? 'love' : S.avoid.indexOf(key) !== -1 ? 'out' : 'clear');
    loomSay('x.finder.note', { subject: kind + ':' + key, state: state, via: via });
  }
  /* the answer as shown: the scent, how well it fit, what stood beside it,
     how many wants had to give way, and which door the room was reached by */
  function sayAnswer(via, moot) {
    const lead = RES && RES.lead;
    const match = RES && RES.match;
    const props = {
      via: via || 'done',
      fit: lead && lead.fit != null ? lead.fit : undefined,
      band: lead ? lead.band : undefined,
      alts: RES && RES.alts ? RES.alts.slice(0, 4).map((a) => a.p.h) : [],
      relaxed: match && match.relaxed ? match.relaxed.length : 0,
      brief: loomBrief()
    };
    if (lead) props.subject = lead.p.h;
    if (moot) props.moot = true;
    loomSay('x.finder.answer', props);
    /* AN ANSWER OPENED FROM A LINK IS NOT A WALK FINISHED HERE (2026-09-22,
       lane 8, tracking audit). go('answer', 1, 'link') runs on every load of
       an answer address - a reload, a Back that was not restored from the
       cache, a link somebody shared or Jennie handed over - and each one
       completed the funnel again for somebody who may never have seen a
       question. x.finder.answer above still records it, with via: 'link'. */
    if (via === 'link') return;
    if (lead) {
      shopifySay('juce_finder_result', {
        scent: lead.p.h, fit: props.fit, band: props.band, via: props.via,
        alternatives: props.alts, brief: props.brief
      });
    }
    /* THE FUNNEL'S OWN COMPLETION, for the one path the tracker cannot see.
       juce-loom.js files finder.complete off the Show-me button, and the
       presence panel is skipped by the room whenever it could not change the
       answer - so a walk that finished through Continue on the third
       question never completed. The bridge is allowlisted there and `once`
       still holds, so a walk that reaches the answer twice completes once. */
    if (lead) {
      try {
        document.dispatchEvent(new CustomEvent('juce:loomview', {
          detail: { name: 'finder.complete', subject: lead.p.h }
        }));
      } catch (err) { /* never the room's problem */ }
    }
  }

  /* ---- the URL is the state, so an answer is a link ---- */
  function writeUrl() {
    if (!window.history || !history.replaceState) return;
    const p = new URLSearchParams();
    Object.keys(URL_KEYS).forEach((key) => {
      const v = S[URL_KEYS[key]];
      if (Array.isArray(v)) { if (v.length) p.set(key, v.join(',')); }
      else if (v) p.set(key, v);
    });
    if (S.gift) p.set('gift', '1');
    const query = p.toString();
    history.replaceState(null, '', location.pathname + (query ? '?' + query : ''));
  }
  function readUrl() {
    const p = new URLSearchParams(location.search);
    Object.keys(URL_KEYS).forEach((key) => {
      const raw = p.get(key);
      if (raw == null) return;
      const slot2 = URL_KEYS[key];
      S[slot2] = MULTI[slot2] ? raw.split(',').map((x) => x.trim()).filter(Boolean) : raw;
      touched[slot2] = true;
    });
    S.gift = p.get('gift') === '1';
    if (textIn && S.text) textIn.value = S.text;
  }

  /* ---- reflect the state back onto the controls that carry it ---- */
  function reflect() {
    paintNoteDoor();
    qsa('[data-fx-pick]').forEach((node) => {
      const key = node.dataset.fxPick;
      const value = node.dataset.fxValue || '';
      const held = S[key];
      /* the clear option on a MULTI group ("Show me everything") is the
         answer "none of them", so it lights when the group was touched and
         holds nothing - the same rule the single-choice '' option follows */
      const on = Array.isArray(held)
        ? (node.hasAttribute('data-fx-clear')
          ? !!touched[key] && held.length === 0
          : held.indexOf(value) !== -1)
        : held === value && (value !== '' || !!touched[key]);
      node.classList.toggle('is-on', !!on);
      /* aria-checked, not aria-pressed (2026-09-10, audit T11): these are
         links, and a pressed state is a button's. armAnswers() gives them
         role="checkbox" once the room is live; until then, or once it has
         flattened, they are the plain shop links they were rendered as and
         carry no state at all. Every answer now says whether it is chosen -
         the single-choice rows (when, presence) never did. */
      if (node.getAttribute('role') === 'checkbox') node.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    qsa('[data-fx-toggle]').forEach((node) => {
      const on = !!S[node.dataset.fxToggle];
      node.classList.toggle('is-on', on);
      if (node.getAttribute('role') === 'checkbox') node.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    qsa('[data-fx-note]').forEach((row) => {
      const key = noteKeyOf(row);
      const loved = S.love.indexOf(key) !== -1;
      const out = S.avoid.indexOf(key) !== -1;
      row.classList.toggle('is-loved', loved);
      row.classList.toggle('is-out', out);
      const love = row.querySelector('[data-fx-love]');
      const avoid = row.querySelector('[data-fx-avoid]');
      if (love) love.setAttribute('aria-pressed', loved ? 'true' : 'false');
      if (avoid) avoid.setAttribute('aria-pressed', out ? 'true' : 'false');
    });
  }

  /* ---- picking ---- */
  function pick(key, value, multi) {
    touched[key] = true;
    if (multi) {
      const at2 = S[key].indexOf(value);
      if (at2 === -1) S[key].push(value); else S[key].splice(at2, 1);
    } else {
      S[key] = S[key] === value ? '' : value;
    }
    changed();
  }
  function toggleNote(slotKey, other, node) {
    const key = noteKeyOf(node);
    if (!key) return;
    const at2 = S[slotKey].indexOf(key);
    if (at2 === -1) {
      /* A DISLIKE IS ALWAYS TAKEN (owner, 2026-08-28: "able to dislike
         something regardless"). It used to be refused whenever it would
         empty the running, which meant the one note a shopper felt
         strongly enough about to rule out was the one the room would not
         hear. narrow() copes: it lets the cheapest want go first and a
         refusal is nearly the last thing it will give up, so the dislike
         stands and run() names whatever moved instead. */
      S[slotKey].push(key);
      const clash = S[other].indexOf(key);
      if (clash !== -1) S[other].splice(clash, 1);   /* loved and refused cannot both stand */
    } else S[slotKey].splice(at2, 1);
    changed();
    sayNote('note', key, 'list');
  }
  function changed() { reflect(); writeUrl(); run(); }

  /* ---- the run. One engine call, then everything that reads it. ----

     IT USED TO BE briefDuo, AND THAT WAS THE WHOLE PROBLEM. briefDuo answers
     with a PAIR: it ranks the shop, then runs the layering model over the
     winner and hands back lead + partner. The finder drew both, sold both, and
     made "Take the pair" the primary button - a shopper who came to find one
     scent was answered with two and a wear order (owner, 2026-08-25: "the
     layering is for the layer page ... the goal is to help them find their
     exact scent super accurate").

     So it is matchBrief now, and the limit is the WHOLE pool rather than four.
     That is not extra work - matchBriefV4 already scores every live record and
     only slices at the end - and it buys the one thing the old call could not
     give: `results` at full length IS the live candidate set, which is what the
     note counts have to be derived from (D11). minFit is applied here instead,
     to the alternates only, so the top answer is never withheld for being a
     stretch - the same rule the engine documents. */
  function run() {
    const mine = ++token;
    if (!PREP) return;
    /* THE CANDIDATE SET LEADS (owner, 2026-08-27: "the note section doesnt
       really influence the leading product at all"). The lead used to come
       off the engine's own ranking over the WHOLE deck, and that ranking is
       deliberately forgiving - it only ever filters on stock, the shelf and
       a refusal, and scores everything else. So a shopper could love a note
       the leader does not carry and watch the same bottle keep winning,
       while the running head said "3 of 41 still fit" about a set the
       leader was not even in. The candidate set is narrowed FIRST now and
       the engine ranks INSIDE it, so the answer always carries every note
       you loved (until narrow() itself has to give a want up, which it
       says out loud). The counts, the dead options and the lead can no
       longer disagree - they are readings of one set. */
    SANS = null;
    CAND = narrow(S, SCENTS);
    /* HANDLES, NOT RECORDS. opts.pool is a list of handles - matchBriefV4
       runs it through compactUnique() and then index.byHandle[...], and
       compactUnique turns an object into `value.name || value.handle ||
       value.title`. A prepared record has none of the first two and its
       `title` is the NORMALISED lowercase title, so every multi-word scent
       resolved to undefined and was dropped. Measured: passing records left a
       pool of 8 of 41 - the eight scents whose one-word title happens to equal
       their handle - and the whole answer was drawn from those eight. */
    const match = JE.matchBrief(PREP, brief(), {
      limit: SCENTS.length, minFit: null, availableOnly: true,
      pool: (CAND.live.length ? CAND.live : SCENTS).map((p) => p.h)
    });
    if (mine !== token) return;
    const found = match.results || [];
    RES = {
      match: match,
      lead: found[0] || null,
      alts: found.slice(1).filter((r) => r.fit == null || r.fit >= MIN_FIT).slice(0, LIMIT),
      live: found.map((r) => r.p)
    };
    /* WHAT HAD TO GIVE WAY, SAID OUT LOUD (reworded 2026-08-28). A shared
       link could always ask for more than the shop makes; a TAP can now do
       it too, because ruling a note out is no longer vetoed when it would
       empty the running (owner: "able to dislike something regardless").
       narrow() drops the cheapest want first and a refusal is nearly the
       last thing it will let go of, so a dislike almost always stands and
       something else moves - which is exactly what this line names. */
    if (CAND.gave.length) {
      say('Nothing we make does all of that at once, so we set aside ' +
        joinSay(CAND.gave.map((k) => GAVE_SAY[k] || k)) + '.');
    } else if (SAID_GAVE) { say(''); }
    SAID_GAVE = CAND.gave.length > 0;
    paintPeek();
    paintNotes();
    nebPaint();
    paintViability();
    paintMoot();
    paintJennie();
    whereSay();
    if (at === 'answer') buildAnswer();
  }

  /* ============================================================
     THE CANDIDATE SET - what is genuinely still on the table.
     (owner, 2026-08-26: "why can I select everything? it doesnt make
     sense, it needs to hide the ones that is no longer applicable based
     on what you selected and our catalog".)

     THE BUG THIS REPLACES was not the painting, it was the SET being
     painted from. The old pass measured every option against
     RES.live - the engine's own running - and the engine's running is
     deliberately forgiving: matchBrief only ever FILTERS on stock, the
     shelf and a refused note, and scores everything else as a ratio of
     stated wants. So a shopper could hold Fresh AND Woody AND Leather
     and nothing ever went dead, because nothing was ever removed. The
     only option that could die was one no scent in the whole shop
     answers ("Sea air"), which is exactly what the screenshots showed.

     So the finder keeps its own set beside the engine's, and it ANDs:
     a candidate is a scent that answers EVERY question already
     answered. That set is the truth behind three things at once -
     which options are still offerable, what the note counts mean, and
     the "still fit" number in the running head.

     TWO RULES KEEP IT HONEST:

       · An option is offered only if TAKING IT would leave something.
         Which means the set can never be emptied by a tap, which means
         a shopper can never build a brief this shop cannot answer.
         (A shared link CAN arrive over-constrained, so narrow() gives
         constraints up from the cheapest end until something stands
         and reports what it let go.)

       · Nothing is measured against a want the shopper has not stated.
         Every constraint below is switched on by an answer.
     ============================================================ */
  const GAVE_SAY = {
    for: 'your preference', character: 'the style', when: 'when you wear it',
    season: 'the season', presence: 'how much room it takes',
    love: 'the notes you loved',
    avoid: 'the notes you ruled out', avoidFam: 'the families you ruled out'
  };
  function joinSay(list) {
    if (list.length < 2) return list[0] || '';
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }
  const PRESENT = JE.WEIGHTS.finder.family.present;
  const BAND = JE.WEIGHTS.finder.intensity.band;
  const famParts = new Map();
  function partsOf(entry) {
    let p = famParts.get(entry);
    if (!p) { p = String(entry).split('|'); famParts.set(entry, p); }
    return p;
  }
  /* the mass a scent carries of a character option, which may name
     several engine families ("fresh|aquatic") - the same reading
     groupBest does, without the allocation */
  function famMass(p, entry) {
    const parts = partsOf(entry);
    let best = 0;
    for (let i = 0; i < parts.length; i++) {
      const v = (p.profile && p.profile.mass[parts[i]]) || 0;
      if (v > best) best = v;
    }
    return best;
  }
  /* ---- HOW PRESENT A FAMILY HAS TO BE, MEASURED AGAINST THIS HOUSE ----

     Owner, 2026-08-28: "selected masc and gender neutral, and then on the
     next step it crossed out Sea air, which is inaccurate because saltwater
     dreams is a gender neutral scent."

     It was crossed out honestly and it was still wrong. `present` is 0.12 -
     the share of a scent's fingerprint a family needs before it counts as
     being in the scent at all - and NOTHING in this catalogue reaches 0.12
     aquatic except wild-child at 0.126, which is feminine. So the moment the
     feminine shelf was deselected the option had nothing left and went dead,
     for every shopper, on every brief. Measured on the real index:

       aquatic mass, whole house:  wild-child .126 (fem), miss-jasmine .108
       (fem), dark-rebellion .076, summer-escape .076, royal-bloom .076,
       wave-daze .070, temptation .067, saltwater-dreams .064, elysium .062

     Saltwater Dreams is the shop's sea-air scent - ocean salt in the notes,
     "sea-kissed air" in its own story - and it scores .064 because its twelve
     notes spread the mass thin and its declared category is Fresh. The number
     is right; using it as a wall is not. A fixed bar only works for families
     some scent in the shop actually leads with, and aquatic is not one.

     So the bar for an option is the LOWER of `present` and half of the best
     score any stocked scent in this house gets for it. A family nothing here
     leads cannot be required to lead. Measured over all four shelf
     selections, eleven of the twelve options move by nothing at all - their
     ceilings are far above 0.24, so min() picks 0.12 exactly as before - and
     "Sea air" goes from one scent house-wide to eight, from dead to five on
     masculine + gender neutral, and to Saltwater Dreams alone on gender
     neutral. Fresh and clean gains miss-jasmine, Green gains wave-daze.

     IT IS USED BY THE FILTER AND THE PROBE ALIKE, which is the point: if the
     probe said yes and the filter still said no, tapping the option would
     empty the room and narrow() would announce that it had thrown the answer
     away. One reading, both places.

     Lazy and cached: it needs SCENTS, which does not exist until the index
     lands, and the answer is fixed for the life of a page. */
  const FAM_BAR = new Map();
  function famBar(entry) {
    let bar = FAM_BAR.get(entry);
    if (bar === undefined) {
      let best = 0;
      for (let i = 0; i < SCENTS.length; i++) {
        if (SCENTS[i].a === false) continue;
        const v = famMass(SCENTS[i], entry);
        if (v > best) best = v;
      }
      bar = Math.min(PRESENT, best * 0.5);
      FAM_BAR.set(entry, bar);
    }
    return bar;
  }
  /* "does this scent answer this character" - the one reading, everywhere. */
  function famIn(p, entry) { return famMass(p, entry) >= famBar(entry); }
  function hasNote(p, key) { return !!(p.noteSet && p.noteSet.has(key)); }
  function nearPresence(p, value) {
    const target = JE.WEIGHTS.finder.intensity[value];
    return target == null ? true : Math.abs(JE.presence(p) - target) <= BAND;
  }
  /* one test per answered question, cheapest to give up LAST in the list */
  function consOf(st) {
    const out = [];
    out.push({ key: 'stock', test: (p) => p.a !== false });
    if (st.for.length) out.push({ key: 'for', test: (p) => JE.audienceAdmits(st.for, p.g) });
    if (st.avoid.length) out.push({ key: 'avoid', test: (p) => !st.avoid.some((k) => hasNote(p, k)) });
    if (st.avoidFam.length) out.push({ key: 'avoidFam', test: (p) => !st.avoidFam.some((f) => famIn(p, f)) });
    if (st.love.length) out.push({ key: 'love', test: (p) => st.love.every((k) => hasNote(p, k)) });
    if (st.character.length) out.push({ key: 'character', test: (p) => st.character.every((f) => famIn(p, f)) });
    if (st.when) out.push({ key: 'when', test: (p) => p.ti.indexOf(st.when) !== -1 });
    if (st.season) out.push({ key: 'season', test: (p) => p.se.indexOf(st.season) !== -1 });
    if (st.presence) out.push({ key: 'presence', test: (p) => nearPresence(p, st.presence) });
    return out;
  }
  function applyCons(list, cons) {
    return list.filter((p) => {
      for (let i = 0; i < cons.length; i++) if (!cons[i].test(p)) return false;
      return true;
    });
  }
  /* narrow, and if a link arrived holding more than the shop can answer,
     let the cheapest wants go - from the tail - until something stands.
     `cons` comes back as the constraints that actually survived, which is
     what every probe below has to be measured against: probing against a
     want that is not being applied would kill every option on the panel. */
  function narrow(st, pool) {
    let cons = consOf(st);
    let live = applyCons(pool, cons);
    const gave = [];
    while (!live.length && cons.length > 1) {
      gave.push(cons[cons.length - 1].key);
      cons = cons.slice(0, -1);
      live = applyCons(pool, cons);
    }
    if (!live.length) { cons = []; live = pool.slice(); }
    return { live: live, cons: cons, gave: gave };
  }
  let CAND = null;          /* { live, cons, gave } - the running truth */
  let SAID_GAVE = false;    /* so the relax line clears itself once it no longer holds */
  let SANS = null;          /* per-key bases, built lazily: see probe() */

  /* THE PROBE. "If this option were taken, would anything be left?"

     A multi-select want ANDs onto what is already held, so it is measured
     against the candidate set itself. A single-choice want REPLACES the
     answer it would overwrite, so it is measured against the set narrowed
     by everything EXCEPT that question - otherwise every Sunday option
     would read dead the moment Saturday was chosen. The shelf is a union
     for the same reason: picking a second shelf widens. */
  function baseFor(key) {
    if (!CAND) return SCENTS;
    if (key !== 'for' && key !== 'when' && key !== 'season' && key !== 'presence') return CAND.live;
    if (!SANS) SANS = Object.create(null);
    if (!SANS[key]) {
      SANS[key] = applyCons(SCENTS, CAND.cons.filter((c) => c.key !== key));
    }
    return SANS[key];
  }
  function anyIn(list, test) {
    for (let i = 0; i < list.length; i++) if (test(list[i])) return true;
    return false;
  }
  function testFor(key, value) {
    if (key === 'character') return (p) => famIn(p, value);
    if (key === 'when') return (p) => p.ti.indexOf(value) !== -1;
    if (key === 'season') return (p) => p.se.indexOf(value) !== -1;
    if (key === 'presence') return (p) => nearPresence(p, value);
    if (key === 'for') return (p) => JE.audienceAdmits([value], p.g);
    if (key === 'love') return (p) => hasNote(p, value);
    return null;
  }
  function viable(key, value) {
    const test = testFor(key, value);
    if (!test) return true;
    return anyIn(baseFor(key), test);
  }
  /* THE REFUSAL IS AN ANSWER TOO, and it is now the LOUDEST one. Ruling a
     note out is the one pick that narrows without being offered on a panel.
     It used to be vetoed whenever it would empty the running - which meant
     the note somebody felt strongly enough about to refuse was the one note
     the room would not hear (owner, 2026-08-28: "able to dislike something
     regardless"). It is always taken now. narrow() lets the cheapest want go
     first and a refusal is second only to stock in what it will keep, so the
     dislike stands and run() names whatever moved to make room for it. */
  /* stateClone / stands / canRefuse / canRefuseFam lived here and asked
     "would this refusal empty the shop?" so the room could decline the tap.
     It does not decline it any more (owner, 2026-08-28), so the question has
     no caller and the four of them came out whole rather than being left
     dormant. narrow() answers the same question better, after the fact, by
     letting the cheapest want go and naming it. */

  /* the paint. Dead is dimmed and struck and REFUSED - a control that
     cannot lead anywhere must not act, and until now these were still
     live <a> links straight out of the room and into the shop. A held
     option is never dead (letting go of it is always allowed) and the
     "no preference" rows cannot die by definition. */
  function paintViability() {
    if (!PREP || !CAND) return;
    qsa('[data-fx-pick]').forEach((node) => {
      const key = node.dataset.fxPick;
      const val = node.dataset.fxValue || '';
      const dead = !!val && !node.classList.contains('is-on') && !viable(key, val);
      node.classList.toggle('is-dead', dead);
      if (dead) node.setAttribute('aria-disabled', 'true');
      else node.removeAttribute('aria-disabled');
    });
  }

  /* ============================================================
     D13 - THE LAST QUESTION IS SKIPPED WHEN IT CANNOT CHANGE THE ANSWER.
     (owner, 2026-09-02: "if the options of balanced, lean in and whatever is
     the same product it is pointless to show the same product so that can be
     skipped", then, on a first cut that dropped options one at a time: "the
     weight of is waaay too much based on previous questions, what I meant
     should all of them lets be Coastal Daze then skip that question
     completely".)

     SO IT IS ALL OR NOTHING, and the correction is worth keeping because the
     first version was defensible and still wrong. It ran each answer through
     and hid any whose scent another answer already produced - which on a
     feminine + evening brief left two rows where there had been three, close
     and balanced having met on Nightcap. Every row it removed was arguably
     redundant, and the panel still read as a live question with a suspiciously
     short list. Presence is the shopper describing THEMSELVES, and two people
     who would both end up with Nightcap do not both want to be told they are
     "balanced". A question is either worth asking or it is not: if the three
     answers lead to three-or-two different scents it is asked in full, and if
     they all lead to one scent it is not asked at all.

     HOW IT IS ASKED OF THE ROOM. Each option is run the whole way through -
     narrow(), then matchBrief over the pool it leaves, which is exactly what
     run() does - and what comes back is the scent that answer would actually
     produce. All three the same, and the question is moot.

     AND A MOOT QUESTION IS NOT A PLACE. go() forwards straight past it in
     both directions, so Continue on `when` lands on the answer and Back off
     the answer lands on `when`. Nothing is drawn and nothing flashes: the
     panel is never made current.

     WHICH LEAVES ONE THING TO CARRY. "Refine through notes" is the only door
     into the nebula in the whole room and it hangs off the presence panel, so
     skipping that panel would take the note field away from exactly the
     shoppers whose brief is narrow enough to reach it. `when` holds a second
     copy of that door, quiet, shown only while presence is being skipped -
     see data-fx-moot in scent-finder.liquid.

     A HELD ANSWER STOPS THE SKIP. A shopper who arrived on a link carrying
     presence=bold has an answer on this panel, and a panel holding an answer
     has to be reachable to change it.

     THE COST IS THREE RANKINGS, memoised against everything in the brief
     EXCEPT presence itself - which is the one thing on this panel a shopper
     can change while standing on it.
     ============================================================ */
  let mootKey = '';
  let mootWas = false;
  function presenceMoot() {
    if (!PREP || !CAND || S.presence) return false;
    const opts = qsa('[data-fx-pick="presence"]').filter((n) => n.dataset.fxValue);
    if (opts.length < 2) return false;
    const values = opts.map((n) => n.dataset.fxValue);
    const key = JSON.stringify([S.for, S.character, S.when, S.season, S.love,
      S.avoid, S.avoidFam, S.gift, S.text, values]);
    if (mootKey === key) return mootWas;
    let one = null;
    let same = true;
    values.forEach((v) => {
      if (!same) return;
      const cand = narrow(Object.assign({}, S, { presence: v }), SCENTS);
      const m = JE.matchBrief(PREP, Object.assign(brief(), { intensity: v }), {
        limit: SCENTS.length, minFit: null, availableOnly: true,
        pool: (cand.live.length ? cand.live : SCENTS).map((p) => p.h)
      });
      const top = (m.results || [])[0];
      const h = top ? top.p.h : '';
      if (one === null) one = h;
      else if (h !== one) same = false;
    });
    mootKey = key;
    mootWas = same && !!one;
    return mootWas;
  }
  /* the spare notes door on `when`, up only while presence is being skipped */
  function paintMoot() {
    const moot = presenceMoot();
    qsa('[data-fx-moot]').forEach((n) => { n.hidden = !moot; });
  }

  /* ============================================================
     D11 - THE NOTE LIST UPDATES, AND NOTHING MOVES.
     (owner, 2026-08-25: "must dynamically update based on the currently
     possible products and change as the product set changes - but must NOT
     shift the rows around, so the shopper does not get confused.")

     The two halves fight each other, and the resolution is a rule rather than
     a trick: ORDER IS DECIDED ONCE, CONTENT IS DECIDED EVERY RUN.

       orderNotes() runs exactly once, at boot, and sorts by how many scents
         really carry each note - which is also "start with notes that are
         likely already in the products". It is the ONLY caller of
         replaceChildren, and it is never called again.
       paintNotes() runs on every change and only ever writes a number into a
         span the server already rendered, and toggles one class. No node is
         created, moved, removed or hidden, so the row under a thumb is still
         under that thumb.

     A note nothing left in the running carries goes .is-dead - dimmed, struck,
     still tappable. Hiding it would be the one change that moves every row
     below it, which is exactly what the rule forbids.
     ============================================================ */
  function paintNotes() {
    if (!noteRows || !CAND) return;
    /* the CANDIDATE set, not the engine's running: a count here is the
       answer to "how many of the scents still on the table carry this",
       and the engine's running never leaves anything off the table */
    const live = CAND.live;
    const counts = Object.create(null);
    for (let i = 0; i < live.length; i++) {
      const keys = NOTEKEYS && NOTEKEYS.get(live[i].h);
      if (!keys) continue;
      for (let j = 0; j < keys.length; j++) counts[keys[j]] = (counts[keys[j]] || 0) + 1;
    }
    for (let i = 0; i < noteRows.length; i++) {
      const r = noteRows[i];
      const n = counts[r.key] || 0;
      if (r.n !== n) {
        r.n = n;
        if (r.num) r.num.textContent = n ? String(n) : '';
        r.row.dataset.live = String(n);
        r.row.classList.toggle('is-dead', n === 0);
      }
    }
  }

  /* THE PEEK - who is winning while the questions are still open. Nobody
     should have to answer five questions blind, and it is never a promise:
     it carries the live fit and it links to the scent itself. */
  function paintPeek() {
    const lead = RES && RES.lead;
    qsa('[data-fx-peek]').forEach((peek) => {
      if (!lead || !answered()) { peek.hidden = true; peek.replaceChildren(); return; }
      peek.href = lead.p.u;
      peek.setAttribute('data-fx-peek-info', lead.p.h);
      peek.setAttribute('aria-label', 'Leading so far: ' + lead.p.t + '. Opens what is in it.');
      const frag = document.createDocumentFragment();
      const shot = el('span', 'sf-peek__shot');
      const im = shotImg(lead.p, 34, 42, true);
      if (im) shot.appendChild(im);
      frag.appendChild(shot);
      const w = el('span', 'sf-peek__w');
      w.appendChild(el('span', 'sf-peek__k', 'Leading so far'));
      w.appendChild(el('span', 'sf-peek__n', lead.p.t));
      frag.appendChild(w);
      /* NO PERCENTAGE WHILE THE QUESTIONS ARE OPEN (owner, 2026-10-22, on
         the rooms' confusing bits). The fit is a ratio of the wants stated so
         far, so one answer in it read "100%" and then fell as more were
         given - a number that drops the more you tell us is not a promise
         anybody can read. The answer still carries its match; the running
         head still carries how many fit. */
      peek.replaceChildren(frag);
      peek.hidden = false;
    });
  }

  /* ============================================================
     D12 - JENNIE ARRIVES ALREADY HOLDING THE BRIEF.
     (owner, 2026-08-25: "she must have the context of what was selected, liked
     and not liked. Not just a message that opens the chat - an ACTION that
     opens it with those things already sent.")

     What was here typed a sentence into the composer and left. Worse, the
     sentence was built out of match.applied, so with a shelf picked it read
     "I am looking for a fragrance: for her." - which was both the banned
     phrase and almost no information.

     Now the brief travels as STRUCTURE, on the section root, where
     jennie.js's pageContext() reads it (finderContext) and hands it to the
     server inside page.finder alongside the cart and the shelf. Two
     consequences worth keeping:

       it rides EVERY message from this page, not only the first, so she
         still has it three questions into the conversation;
       it obeys the panel's own "share what I am looking at" switch, because
         it lives in exactly the same object that switch governs. A shopper
         who turns context off sends none of this.

     The prompt attribute stays as the no-server fallback and as the words the
     composer would show, but it is no longer where the meaning lives.
     ============================================================ */
  function finderBrief() {
    const said = (RES && RES.match && RES.match.applied) || [];
    const lead = RES && RES.lead;
    const out = {
      /* the surface names itself, because the layering room writes the same
         attribute and one reader in jennie.js places both (surfaceBrief) */
      surface: 'finder',
      /* one shelf travels as the word, several as the list - chat.ts
         accepts both shapes and phrases them the same way */
      shelf: S.for.length ? (S.for.length === 1 ? S.for[0] : S.for.slice(0, 3)) : undefined,
      character: S.character.length ? S.character.map((f) => String(f).split('|')[0]) : undefined,
      when: S.when || undefined,
      season: S.season || undefined,
      presence: S.presence || undefined,
      gift: S.gift || undefined,
      words: S.text || undefined,
      said: said.length ? said.slice(0, 12) : undefined
    };
    /* loved and refused go as the labels the shopper pressed, not as slugs */
    const label = (key) => {
      const row = noteRows && noteRows.filter((r) => r.key === key)[0];
      return row ? row.row.dataset.name : key;
    };
    if (S.love.length) out.liked = S.love.slice(0, 12).map(label);
    if (S.avoid.length) out.refused = S.avoid.slice(0, 12).map(label);
    /* AN ANSWER ONLY EXISTS ONCE THE ROOM HAS GIVEN IT.
       RES.lead is live from the first question - it is the running lead of a
       ranking that re-runs on every tap, which is what the pool counter is
       drawn from - so sending it while the shopper is still on "Which shelf?"
       shipped a provisional guess in the field named `answer`. brain/
       context.ts then said "the finder answered them with X, and that answer
       is on the screen behind this panel" while the screen held a question
       and four untouched rows.

       Only this file knows what is on that screen, so the gate belongs here
       rather than in a sentence downstream: the answer travels when the room
       is showing it, and until then the honest shape is no answer at all -
       which chat.ts already phrases as "they came to you partway through".
       Everything they actually pressed still travels on every step. */
    if (lead && at === 'answer') {
      out.answer = lead.p.t;
      out.answer_handle = lead.p.h;
      if (lead.fit != null) out.fit = lead.fit;
      if (RES.alts.length) out.runners_up = RES.alts.slice(0, 3).map((a) => a.p.t);
    }
    Object.keys(out).forEach((k) => { if (out[k] === undefined) delete out[k]; });
    return out;
  }
  /* The same brief as chips - what the panel draws, and what is printed on the
     button so nothing travels unseen.

     READ OFF THE BRIEF THAT IS SENT, not off the DOM. This used to walk
     [data-fx-pick].is-on and read each control's own label, on the argument
     that the words on screen are the honest ones. Driven, it printed neither:

       the season chip is a bare <a> with no .sf-opt__k, so the label fell
         through to node.textContent and swept up the "Now" tag the boot pass
         appends to whichever season it is outside - printing "WinterNow",
         a word about today that nobody chose;
       the character answer was missing from the list altogether while
         travelling in the payload's said[], so the one list whose entire job
         is to show what is about to be sent was quietly shorter than it.

     RES.match.applied is the engine saying back the brief it was given, in
     its own labels, and it is exactly what finderBrief() sends as `said`. So
     print that: the list and the payload cannot drift, because they are now
     the same array. This is the layering room's fix one room along - it read
     the reveal's own QSAID for the same reason after the same "Now" bug. */
  function briefChips() {
    const said = (RES && RES.match && RES.match.applied) || [];
    /* the same cut the payload takes, so print and send stay identical */
    const chips = said.slice(0, 12).map((t) => ({
      t: String(t),
      /* THE ENGINE ALREADY SAYS THE REFUSAL IN WORDS ("No vanilla",
         "Nothing woody" - briefSayV4). The class only draws the rule; there
         is nothing to add for a screen reader, because the word is there. */
      no: /^(no|nothing) /i.test(String(t)),
    }));
    /* the one thing the engine does not say back: their own sentence */
    if (S.text) chips.push({ t: '"' + S.text + '"' });
    return chips;
  }
  function paintJennie() {
    try { root.dataset.chatContext = JSON.stringify(finderBrief()); }
    catch (err) { delete root.dataset.chatContext; }
    /* NO PREFILL (owner, 2026-08-26: "don't put in a pre-select message
       just do the call"). The brief travels as STRUCTURE - the hand-off
       block on the wire and page.finder on every message - so words waiting
       in the composer would only duplicate what she already holds. The
       attribute is stripped rather than rewritten, so even the generic
       data-chat-open seam opens her clean while this controller is up. */
    qsa('[data-fx-jennie]').forEach((node) => node.removeAttribute('data-chat-prompt'));
  }

  /* THE ACTION. Not a link to the chat with a sentence in the box: a module
     line in the log saying what was handed over, an event on the wire, and a
     bubble on this page confirming it - the same three beats an Add to bag
     has, which is the confirmation the owner pointed at. */
  function openJennie(seam) {
    const chips = briefChips();
    /* The engine's own words already carry the refusal, so nothing is
       re-prefixed here - "no no vanilla" was what that cost. */
    const line = chips.map((c) => c.t).join(', ');
    const lead = at === 'answer' ? (RES && RES.lead) : null;
    /* The bubble is raised by handOffToJennie once the panel has taken it,
       never here: at this point nothing has been handed over yet. */
    handOffToJennie({
      source: 'finder',
      /* no composer prefill, ever - the hand-off block IS the call */
      prompt: '',
      fallback: (seam && seam.getAttribute('href')) || HELP,
      /* the one hand-off slot every guided room uses - the layering duet
         sends the same three fields through it (initDuet, openDuetJennie) */
      handoff: {
        surface: 'finder',
        line: line || '',
        answer: lead ? lead.p.t : ''
      }
    }, 'Jennie has what you picked', 'Opening Jennie');
  }

  /* ---- the room: panels push, the trail says where ---- */
  function panelOf(step) { return roomEl.querySelector('[data-fx-panel="' + step + '"]'); }
  function trail() {
    if (!trailEl) return;
    /* the detour holds the question it was opened from lit: it is a place in
       the room, not a step through it, so the trail must not go blank on it */
    const key = at === 'notes' ? noteFrom : at;
    const i = at === 'answer' ? STEPS.length : STEPS.indexOf(key);
    Array.from(trailEl.children).forEach((t, j) => {
      t.classList.toggle('is-done', i > j);
      t.classList.toggle('is-live', i === j);
    });
  }
  /* the running head: the chapter, and how much of the range still fits.
     The pool is a FACT about what is left, never a score. */
  function whereSay() {
    if (!whereEl) return;
    if (at === 'answer') {
      const lead = RES && RES.lead;
      whereEl.textContent = lead ? lead.p.t : 'The answer';
      return;
    }
    let line = WHERE[at] || '';
    /* the deck, not the index - the gift card is not one of the scents that
       could still fit, so it is not in the number they are measured against.
       The number is the CANDIDATE set: what answers everything said so far,
       which is the same set the dead options and the note counts come from.
       match.pool used to say this, and it counted the engine's running - a
       number that barely moved however much the shopper narrowed. */
    const left = CAND ? CAND.live.length : 0;
    if (SCENTS.length && left && left < SCENTS.length) {
      line += ' · ' + left + ' of ' + SCENTS.length + ' still fit';
    }
    whereEl.textContent = line;
  }
  function backable() {
    const on = at !== 'for';
    /* disabled as well as dimmed (2026-09-10, audit T11): .is-usable only ever
       changed the paint, so on the first question Back was still a focusable
       button announcing itself as available and doing nothing. The markup
       ships it disabled; this is the only thing that lifts it. */
    if (backBtn) { backBtn.classList.toggle('is-usable', on); backBtn.disabled = !on; }
    if (edgeBtn) edgeBtn.classList.toggle('is-usable', on);
  }
  function go(step, dir, why) {
    if (root.classList.contains('is-flat')) return;
    const prev = at;
    const asked = step;
    /* D13 - a question that cannot change the answer is not a place. Forwards
       it becomes the answer; backwards it becomes the question before it. */
    if (step === 'presence' && presenceMoot()) step = dir < 0 ? 'when' : 'answer';
    const moot = step !== asked;
    const next = step === 'answer' ? buildAnswer() : panelOf(step);
    if (!next) return;
    const cur = roomEl.querySelector('.sf-panel.is-on');
    dir = dir == null ? 1 : dir;
    next.style.setProperty('--dir', String(dir));
    if (cur && cur !== next) {
      cur.style.setProperty('--dir', String(dir));
      cur.classList.remove('is-on');
      cur.classList.add('is-out');
      cur.inert = true;
      restLater(cur);
    }
    window.clearTimeout(next._restT);
    next.classList.remove('is-out', 'is-rest');
    /* THE SLIDE'S FROM-STATE IS FIXED BY A STYLE FLUSH, NOT A LAYOUT
       (2026-09-22, lane 8). `void next.offsetWidth` laid out the whole room
       in the tap's own frame - every panel, the hidden ones included - only
       to make the transition start from where the panel was. Reading one
       computed style commits that from-state just the same, and panels at
       rest are not laid out at all now (finder.css, A PANEL AT REST). */
    void getComputedStyle(next).opacity;
    next.classList.add('is-on');
    next.inert = false;
    at = step;
    root.dataset.at = step;
    /* WHAT NOBODY SEES WAITS FOR THE FRAME (2026-09-22, lane 8). The records
       of the step and the answer, the moot check (up to three engine passes)
       and Jennie's brief used to run inside the tap, in front of the first
       frame of the slide. They run once that frame is on screen: the brief is
       still written before anyone can press Jennie's door, and every event
       still carries the brief as it stood at this step. */
    const fromStep = prev || '';
    const stepBrief = loomBrief();
    afterFrame(() => {
      /* every arrival, with the brief as it stands - the ladder, the flow and
         the "what did they pick" counts are all read off these (see loomSay) */
      loomSay('x.finder.step', {
        subject: step, from: fromStep, dir: dir, moot: moot || undefined, brief: stepBrief
      });
      if (step === 'answer') sayAnswer(moot ? 'moot' : why, moot);
    });
    /* the nebula can only measure itself once its panel has a box */
    if (step === 'notes' && NEB && (!nebLaid || nebStale)) { nebTries = 0; window.setTimeout(() => nebFlow(true), 0); }
    /* REPAINTED ON THE MOVE, NOT ONLY ON THE TAP. run() paints the brief
       while the room is still on the question that was just answered, so the
       attribute lagged one step - and now that the answer only travels once
       the room is showing it, the lag was the difference between an answer
       on the screen and no answer in the payload. Also picks up the answer
       panel buildAnswer has just rebuilt, whose own Jennie door is a new
       node needing the same data-chat-prompt as the old one. */
    trail(); whereSay(); backable(); say('');
    afterFrame(() => { if (at === step) { paintMoot(); paintJennie(); } });
    const head = next.querySelector('.sf-q, .sf-pairname');
    if (head) { head.tabIndex = -1; head.focus({ preventScroll: true }); }
  }
  /* A PANEL THAT HAS FINISHED LEAVING GOES TO REST. It keeps .is-out (its
     position is the one it left by) and gains .is-rest once the slide is
     over, which is what lets finder.css stop laying it out. Its scroll goes
     back to the top here, off the tap, instead of on the way in. */
  function restLater(p) {
    window.clearTimeout(p._restT);
    p._restT = window.setTimeout(() => {
      if (p.classList.contains('is-on') || !p.isConnected) return;
      const sc = p.querySelector('.sf-panel__scroll');
      if (sc && sc.scrollTop) sc.scrollTop = 0;
      p.classList.add('is-rest');
    }, reduceMotion.matches ? 0 : 700);
  }
  function back() {
    /* the detour is not in ORDER, so Back off it means "out of the detour" */
    if (at === 'notes') { go(noteFrom || 'presence', -1); return; }
    const i = ORDER.indexOf(at);
    if (i > 0) go(ORDER[i - 1], -1);
  }

  /* ============================================================
     THE ANSWER. Built here because with no catalogue there is nothing
     honest to put in it. It is shaped like the layering room's reveal on
     purpose: the finder does not sell one bottle, it sells a scent and
     the one it wears with, so the two meet on a seam and the wear order
     is part of the answer rather than a footnote.
     ============================================================ */
  function bandSay(band) {
    if (band === 'perfect') return 'answers everything you asked for';
    if (band === 'strong') return 'a strong match';
    if (band === 'good') return 'a fair match';
    return 'the closest we make';
  }
  /* WHERE A NOTE GOES (2026-08-31, owner: "Make thse clickable and nicey the
     notes"). Every note in the fold is a real link into the shelf filtered by
     that one note. Nothing new is built for it: the collection filter has
     carried a `notes` facet since it was written and reads `?notes=` off the
     URL on the way in (hydrate(), the legacy deep link), so this is an
     existing door rather than a new endpoint, it opens in a new tab like any
     link, and it lands correctly with no script at the other end.

     THE TWO VOCABULARIES AGREE, WHICH IS WHAT MAKES IT LAND. The shelf writes
     its keys with Liquid's `handleize` and reads them back through cfKey
     (srchNorm, then spaces to hyphens); both turn "Pink peppercorn" into
     "pink-peppercorn". A note whose name needed anything more than that would
     arrive as an unknown key, and the shelf's own seed() drops unknown keys
     rather than emptying the grid - so the failure, if it ever comes, is a
     shelf with no filter on it, never a shelf with nothing in it. */
  function noteHref(name) {
    const base = root.dataset.shop || '/collections/all?';
    return base + 'notes=' + encodeURIComponent(
      String(name).trim().toLowerCase().replace(/\s+/g, '-'));
  }
  function pyramid(rec, wanted) {
    const wrap = el('div', 'sf-pyr');
    ['top', 'heart', 'base'].forEach((tier) => {
      const names = rec.tiers[tier];
      if (!names.length) return;
      const row = el('div', 'sf-pyr__row');
      row.appendChild(el('span', 'sf-pyr__k', TIER_K[tier]));
      const list = el('span', 'sf-pyr__n');
      names.forEach((n) => {
        const asked = wanted.indexOf(n) !== -1;
        /* THE ONE THEY ASKED FOR SAYS SO IN WORDS TOO. It is filled rather
           than outlined, and a fill is a signal on screen and nowhere else -
           it is not read aloud and it does not survive being copied out. Same
           treatment, and the same reason, as the refusals on Jennie's card. */
        const a = anch('sf-pyr__i' + (asked ? ' is-asked' : ''), noteHref(n), cap(n));
        if (asked) {
          a.insertBefore(el('span', 'visually-hidden', 'You asked for '), a.firstChild);
        }
        list.appendChild(a);
      });
      row.appendChild(list);
      wrap.appendChild(row);
    });
    return wrap;
  }
  function fold(word, build) {
    const d = el('details', 'sf-fold');
    const sum = el('summary', null, word);
    d.appendChild(sum);
    const body = el('div', 'sf-fold__body');
    build(body);
    d.appendChild(body);
    return d;
  }
  /* the money. Flat-labelled on purpose: addPairToCart swaps a button's
     textContent for Adding / Added, so a two-line label would be eaten the
     first time anything was added. */
  function buyAct(cls, label, cents) {
    return btn(cls, cents > 0 ? label + ' · ' + JE.money(cents) : label);
  }

  function noAnswerPanel(box) {
    const scroll = el('div', 'sf-panel__scroll');
    const out = el('div', 'sf-out');
    out.appendChild(el('h2', 'sf-pairname', 'Nothing we make answers that yet.'));
    out.appendChild(el('p', 'sf-why',
      'Nothing we make does all of that at once. Change one answer and we will try again, or ask Jennie - she knows what is coming in.'));
    const acts = el('div', 'sf-buy');
    const again = btn('sf-go sf-go--ghost', 'Change an answer');
    again.setAttribute('data-fx-step', 'for');
    acts.appendChild(again);
    out.appendChild(acts);
    out.appendChild(jennieBlock('I asked the finder for something it could not answer. Can you help?'));
    const words = el('div', 'sf-words');
    const reset2 = btn('sf-word sf-word--u', 'Start over');
    reset2.setAttribute('data-fx-reset', '');
    words.appendChild(reset2);
    words.appendChild(anch('sf-word sf-word--u', root.dataset.shop ? root.dataset.shop.replace(/[?&]$/, '') : '/collections/all', 'Browse every scent'));
    out.appendChild(words);
    scroll.appendChild(out);
    box.appendChild(scroll);
    return box;
  }

  /* Jennie, IN the flow. The floating hail is stood down on this page
     (finder.css), so this is her door - and it HANDS OVER the brief rather
     than merely mentioning it (D12). The chips are what is about to be sent,
     printed: a shopper is entitled to read what travels about them before it
     travels, and it is also the reason to press the button. */
  function jennieBlock(prompt) {
    const jn = el('div', 'sf-jn');
    const head = el('div', 'sf-jn__head');
    head.appendChild(el('span', 'sf-jn__dot'));
    head.appendChild(el('span', 'sf-jn__who', 'Jennie'));
    jn.appendChild(head);
    const chips = briefChips();
    jn.appendChild(el('p', 'sf-jn__say', chips.length
      ? 'I can take it from here with everything you just told the finder.'
      : 'Tell me what you have worn before and what you did not like about it, and I will narrow it with you.'));
    if (chips.length) {
      const ul = el('ul', 'sf-jn__ctx');
      /* EVERY CHIP, NOT THE FIRST TEN. This list is the promise that a
         shopper can read what travels about them before it travels, and the
         payload carries twelve - a cut here would hide the last two. */
      chips.forEach((c) => {
        const li = el('li', c.no ? 'is-no' : null);
        /* A REFUSAL READS AS A REFUSAL WITHOUT THE STRIKE-THROUGH. The rule
           through the word is a signal on screen and nowhere else - it is not
           read aloud and it does not survive being copied out. Nothing is
           added for it here because the engine's own label already says the
           word: "No vanilla", "Nothing woody". */
        li.appendChild(document.createTextNode(c.t));
        ul.appendChild(li);
      });
      jn.appendChild(ul);
    }
    /* a real anchor with a real href, like every other seam in the theme:
       no script, no module, no endpoint and it still goes somewhere useful */
    /* ONE LABEL WHEN THERE IS NOTHING TO SEND (2026-08-31, owner: "change the
       btn Talk it through with Jennie btn on both"). The rooms had two: the
       finder said "Talk it through with Jennie" and the Duet said "Continue
       with Jennie", for the same button in the same state. "Ask Jennie" is
       what the room's own foot has always called this door, it is short
       enough not to wrap in the card, and it is honest when there is no
       brief to hand over. The full-brief label is unchanged in both. */
    const go2 = anch('sf-jn__go', HELP, chips.length ? 'Send this to Jennie' : 'Ask Jennie');
    /* BOTH HOOKS, ON PURPOSE. data-chat-open is the theme's own seam and is
       what opens the panel when this controller is not running at all;
       data-fx-hand is the action, and the handler below stops the event so
       the generic seam cannot open the panel a second time behind it.

       NOT data-fx-ask, which is already taken: the five question headings
       carry it as the focus target the room moves to on every step, so
       sharing the name made a tap on "Which shelf?" open the chat. */
    go2.setAttribute('data-chat-open', '');
    go2.setAttribute('data-fx-jennie', '');
    go2.setAttribute('data-fx-hand', '');
    /* no data-chat-prompt: she opens clean and the hand-off does the talking */
    jn.appendChild(go2);
    return jn;
  }

  let building = false;
  function buildAnswer() {
    if (!PREP || building) return slot.querySelector('[data-fx-panel="answer"]');
    building = true;
    if (!RES) run();
    building = false;
    const res = RES;
    const old = slot.querySelector('[data-fx-panel="answer"]');
    const box = el('section', 'sf-panel sf-panel--out');
    box.setAttribute('data-fx-panel', 'answer');
    box.dataset.fxBuilt = '1';
    if (old && old.classList.contains('is-on')) box.classList.add('is-on');

    if (!res || !res.lead) {
      noAnswerPanel(box);
    } else {
      const lead = res.lead;
      const match = res.match;
      const wanted = (match.brief.notes || []).slice();
      const scroll = el('div', 'sf-panel__scroll');

      /* THE STAGE IS ONE BOTTLE. It used to be two meeting on a seam, because
         the answer used to be a pair; the answer is a scent now. */
      /* THE ANSWER'S OWN BOTTLE OPENS (owner, 2026-08-28). It was decoration
         with an "About this scent" word four rows below it; it is the first
         thing anybody taps, so it is the peek - what is in it, both sizes,
         Add - and the word below stays for anyone who reads before tapping. */
      const stage = el('div', 'sf-stage');
      const shotA = btn('sf-stage__shot');
      shotA.setAttribute('data-fx-peek-info', lead.p.h);
      shotA.setAttribute('aria-label', 'About ' + lead.p.t);
      shotA.setAttribute('data-loom', 'x.peek');
      shotA.setAttribute('data-loom-props', JSON.stringify({ handle: lead.p.h, surface: 'finder' }));
      /* 900 SQUARE, and both numbers were wrong before (2026-08-31).

         THE RATIO. It was 620x775 - 4:5 - and the 50ml packshots are SQUARE
         files, 1240x1240 every one of them. The attributes are only an aspect
         hint (finder.css sets `height: auto`), so the wrong ratio bought a
         layout shift on every answer and nothing else.

         THE SIZE. photoImg asks the CDN for double, so this is an 1800px file.
         The window shows 62.4% of it after the zoom-crop, and the box is drawn
         up to 720px wide on a big screen: 1800 * 0.624 = 1123 source pixels
         for a 720px box, which is honest at 1x and within a whisker at 2x. The
         old pair (1240 asked, 76% window, 620px box) was already the softest
         figure on the page and the tighter crop would have made it softer. */
      const imA = photoImg(lead.p, 900, 900, false);
      if (imA) shotA.appendChild(imA);
      stage.appendChild(shotA);
      scroll.appendChild(stage);

      const out = el('div', 'sf-out');

      /* the stamp: the fit, the band it lands in, then the facts */
    /* ---- THE NUMBER OR THE WORDS, NEVER BOTH (2026-08-31). "83% fit"
       beside "a fair match" is one fact said twice, and the two halves
       disagree about it: 83 sounds good and "fair" sounds like a shrug. The
       band only earns its place where it says something the number cannot -
       that the scent answers EVERYTHING asked for, or that it is the nearest
       thing we make rather than a real match. In between, the number is
       enough. ---- */
      const stamp = el('div', 'sf-stamp');
      if (lead.fit != null) stamp.appendChild(el('span', 'sf-stamp__f', lead.fit + '% match'));
      if (lead.fit == null || lead.band === 'perfect' || lead.band === 'weak') {
        stamp.appendChild(el('span', 'sf-tag', bandSay(lead.band)));
      }
      if (!S.season && CTX && lead.p.se.indexOf(CTX.season) !== -1) {
        stamp.appendChild(el('span', 'sf-tag sf-tag--now', 'In season now'));
      }
      out.appendChild(stamp);

      out.appendChild(el('h2', 'sf-pairname', lead.p.t));

      const why = (lead.why && lead.why.long) || lead.reason || '';
      if (why) out.appendChild(el('p', 'sf-why', why));

      /* every want we could not hold, named. Never silent. */
      (match.relaxed || []).forEach((r) => out.appendChild(el('p', 'sf-relax', r.text)));

      /* what it does NOT do. A finder that only ever flatters is one
         nobody comes back to, and this is the cheapest trust the page can
         buy - so it sits above the buy bar, not below it. */
      if (lead.gaps && lead.gaps.length) {
        const gap = el('p', 'sf-gap');
        gap.appendChild(el('b', null, 'Worth knowing'));
        gap.appendChild(document.createTextNode(lead.gaps.map((g) => g.text).join('. ') + '.'));
        out.appendChild(gap);
      }

      /* ---- THE DOOR INTO THE LAYERING ROOM ----
         (owner, 2026-08-25: "add a CTA to take them to the layering with the
         selected product instead of suggesting a layering combo from the
         bat".) A real anchor on the contract initDuet documents, carrying the
         scent that was just found and mode=own so the room opens knowing they
         already have this one and only has to find its partner. No engine call
         here at all - the layering model belongs to the layering page. */
      const door = anch('sf-layer', layerHref(lead.p.h));
      /* the hook juce-loom.js files the door under (x.finder.tap layer:<h>) */
      door.setAttribute('data-fx-layer', lead.p.h);
      const dw = el('span', 'sf-layer__w');
      dw.appendChild(el('span', 'sf-layer__k', 'Layer ' + lead.p.t + ' with something'));
      dw.appendChild(el('span', 'sf-layer__c',
        'Take it into the layering room and we will find what it wears with.'));
      door.appendChild(dw);
      door.appendChild(el('span', 'sf-layer__a'));
      out.appendChild(door);

      /* what we read, and what is in the bottle - a fold that opens IN the
         panel, never over it */
      /* ---- THE FOLD IS THE BOTTLE, AND ONLY THE BOTTLE (2026-08-31).

         It held three things and two of them were already on the screen. The
         reasons line rebuilt `why.long` out of the same clauses - measured on
         Miss Jasmine, "Mostly florals. Present without shouting. A daytime
         scent." printed word for word under the name AND inside the fold. And
         "You said: Feminine, Daytime, Florals, Balanced" is the same four
         words as the chips on Jennie's card two rows below, which have a job
         this one does not: they say what is about to be sent to her.

         What is left is what the fold is named after and what nothing else on
         the page carries - the notes, tier by tier, with the ones the shopper
         asked for picked out. ---- */
      out.appendChild(fold('What is in it', (body) => {
        body.appendChild(pyramid(lead.p, wanted));
      }));

      /* the other honest answers - each with its own way to be told about
         (the peek), beside the way to be visited */
      const alts = res.alts;
      if (alts.length) {
        const wrap = el('div', 'sf-alts');
        wrap.appendChild(el('p', 'sf-sub', 'Also worth smelling'));
        const row = el('div', 'sf-alts__row');
        alts.forEach((a) => {
          const cell = el('div', 'sf-alt');
          /* the tile keeps the PDP as its href - a real link, openable in a
             new tab and honest with no script - but a plain tap opens the
             peek, which is where the prices and the Add are. The peek's own
             "Open its full page" is the way through to the href. */
          const tile = anch('sf-alt__go', a.p.u);
          tile.setAttribute('data-fx-peek-info', a.p.h);
          const sh = el('span', 'sf-alt__shot');
          /* THE CUT-OUT, NOT THE BEDDED PLATE (owner, 2026-08-27: "also worth
             smelling we want to use the pngs"). shotImg reads png first and
             falls back to pimg/img, so a size with no cutout still draws. The
             hero above deliberately stays on photoImg - that one was asked for
             as the photograph on 2026-08-26 and has not changed. */
          /* 300, not 150: .sf-alts__row is grid-auto-columns minmax(8.5rem,1fr)
             inside a half-width panel, so on a 1900px screen these tiles draw
             at about 270px - measured. shotImg asks the CDN for double, so 150
             was fetching a 300px file for a 270px box and going soft on any
             2x screen. 4:5 to match .sf-alt__shot's aspect-ratio. */
          const im = shotImg(a.p, 300, 375, true);
          if (im) sh.appendChild(im);
          tile.appendChild(sh);
          tile.appendChild(el('span', 'sf-alt__n', a.p.t));
          tile.appendChild(el('span', 'sf-alt__w',
            (a.fit != null ? a.fit + '% · ' : '') + (a.reason || (a.why && a.why.short) || '')));
          cell.appendChild(tile);
          const info = btn('sf-info', 'i');
          info.setAttribute('data-fx-peek-info', a.p.h);
          info.setAttribute('aria-label', 'About ' + a.p.t);
          info.setAttribute('data-loom', 'x.peek');
          info.setAttribute('data-loom-props', JSON.stringify({ handle: a.p.h, surface: 'finder' }));
          cell.appendChild(info);
          row.appendChild(cell);
        });
        wrap.appendChild(row);
        out.appendChild(wrap);
      }

      out.appendChild(jennieBlock(
        'The finder gave me ' + lead.p.t + '. Is that the right call for me?'));

      /* THE MONEY. One scent is the answer, so one bottle is the button; the
         10ml is the quieter way in. */
      const buy = el('div', 'sf-buy');
      const one = buyAct('sf-buy__act', 'Add ' + lead.p.t, lead.p.pc);
      one.setAttribute('data-pair-add', lead.p.h);
      one.setAttribute('data-pair-open', '');
      if (lead.p.vid) one.setAttribute('data-variant-id', lead.p.vid);
      if (lead.p.sid) one.setAttribute('data-sample-id', lead.p.sid);
      buy.appendChild(one);
      /* the smaller yes: the same answer in 10ml. Only when the mini and
         its price are genuinely on the record. */
      if (lead.p.mid && lead.p.mpc > 0) {
        /* not "Try Bold Ambition in 10ml": the scent is named in the heading,
           in the layering door, in the primary action and in the link below,
           and the ghost sits directly under the one that already says it */
        const mini = buyAct('sf-buy__act sf-buy__act--ghost', 'Try the 10ml first', lead.p.mpc);
        mini.setAttribute('data-fx-mini', lead.p.h);
        buy.appendChild(mini);
      }
      out.appendChild(buy);

      const words = el('div', 'sf-words');
      const about = btn('sf-word sf-word--u', 'About this scent');
      about.setAttribute('data-fx-peek-info', lead.p.h);
      about.setAttribute('data-loom', 'x.peek');
      about.setAttribute('data-loom-props', JSON.stringify({ handle: lead.p.h, surface: 'finder' }));
      words.appendChild(about);
      const change = btn('sf-word sf-word--u', 'Change an answer');
      change.setAttribute('data-fx-step', 'for');
      words.appendChild(change);
      const again2 = btn('sf-word sf-word--u', 'Start over');
      again2.setAttribute('data-fx-reset', '');
      words.appendChild(again2);
      words.appendChild(anch('sf-word sf-word--u', lead.p.u, 'Open its full page'));
      out.appendChild(words);

      scroll.appendChild(out);
      box.appendChild(scroll);
    }

    if (old) old.replaceWith(box); else slot.appendChild(box);
    return box;
  }

  function reset() {
    Object.keys(S).forEach((k) => {
      S[k] = Array.isArray(S[k]) ? [] : (typeof S[k] === 'boolean' ? false : '');
    });
    Object.keys(touched).forEach((k) => { delete touched[k]; });
    if (textIn) textIn.value = '';
    const built = slot.querySelector('[data-fx-panel="answer"]');
    if (built) built.remove();
    at = 'for';                                /* so the run does not rebuild what was just cleared */
    reflect();
    writeUrl();
    run();
    go('for', -1);
    say('Cleared. Start again whenever you like.');
  }

  /* ---- the note list: 121 of them, so the useful ones come first ----

     THIS IS THE ONLY THING THAT EVER SORTS THIS LIST, and it runs once, at
     boot, before the shopper has ever seen it. Everything after that is
     paintNotes writing a number into a span. See the D11 note above run().

     Ordering by real carrier count is also what "start with notes that are
     likely already in the products" means: the fourteen shown unfolded are
     the fourteen the shop actually builds with. */
  function orderNotes() {
    if (!noteList || !PREP) return;
    const rows = Array.from(noteList.children);
    rows.forEach((row) => {
      const meta = PREP.noteMap[noteKeyOf(row)];
      row.dataset.carriers = meta ? meta.count : 0;
    });
    rows.sort((a, b) => (b.dataset.carriers - a.dataset.carriers) ||
      a.dataset.name.localeCompare(b.dataset.name));
    noteList.replaceChildren.apply(noteList, rows);
    /* the row handles, resolved once. noteKeyOf() runs canonicalNote on every
       call and paintNotes touches all 121 rows on every keystroke. */
    noteRows = rows.map((row) => ({
      row: row,
      key: noteKeyOf(row),
      num: row.querySelector('[data-fx-noten]'),
      n: -1
    }));
    foldNotes(true);
  }
  function foldNotes(fold2) {
    if (!noteList || !noteMore) return;
    notesFolded = fold2;
    const rows = Array.from(noteList.children);
    rows.forEach((row, i) => {
      /* a note already chosen never folds away - it is part of the answer */
      row.hidden = fold2 && i >= NOTES_SHOWN &&
        !row.classList.contains('is-loved') && !row.classList.contains('is-out');
    });
    noteMore.hidden = rows.length <= NOTES_SHOWN;
    noteMore.textContent = fold2 ? 'Show all ' + rows.length + ' notes' : 'Show fewer';
  }
  function findNote(term) {
    if (!noteList) return;
    const q = JE.norm(term || '');
    const rows = Array.from(noteList.children);
    if (!q) {
      foldNotes(notesFolded);
      if (noteNone) noteNone.hidden = true;
      return;
    }
    let hits = 0;
    rows.forEach((row) => {
      const on = JE.norm(row.dataset.name).indexOf(q) !== -1;
      row.hidden = !on;
      if (on) hits++;
    });
    if (noteNone) noteNone.hidden = hits > 0;
    if (noteMore) noteMore.hidden = true;
  }

  /* ============================================================
     THE NEBULA (owner, 2026-08-26): the note vocabulary as a field of
     drifting glass discs. Families first, sized by how much of the RUNNING
     each covers; a tapped family blooms into its own notes around it; a
     note cycles love -> ruled out -> clear on repeated taps, exactly the
     Apple-Music grammar the owner pointed at.

     Rules it holds to:
       everything is DERIVED - a disc's size is its live coverage, its
         count is the number of scents still in the running that carry it,
         and a disc nothing carries goes dead rather than disappearing;
       transform and opacity only - the drift, the bloom and the dock all
         move with the compositor, never with filter or layout, per
         steering/06 (the scroll-performance ruling);
       state lives in S.love / S.avoid, the same two arrays the flat list,
         the URL and the engine already read - the nebula is a VIEW of the
         brief, never a second store.
     ============================================================ */
  const FAM_WORD = {
    citrus: 'Citrus', fresh: 'Fresh', aquatic: 'Sea air', green: 'Green',
    aromatic: 'Herbs', fruity: 'Fruity', floral: 'Floral', powdery: 'Powdery',
    musky: 'Musk', spicy: 'Spice', woody: 'Woods', gourmand: 'Sweet',
    resinous: 'Resins', leather: 'Leather', amber: 'Amber', other: 'More'
  };
  /* each family's cast, mixed INTO the glass at low strength by the CSS -
     desaturated and close in value on purpose (owner: "not alot of bright
     colors tho"), so the field reads as tinted glass, never as candy */
  const FAM_TINT = {
    citrus: 'hsl(52 46% 58%)', fresh: 'hsl(180 20% 60%)', aquatic: 'hsl(204 32% 58%)',
    green: 'hsl(110 18% 50%)', aromatic: 'hsl(140 16% 48%)', fruity: 'hsl(8 40% 60%)',
    floral: 'hsl(335 30% 64%)', powdery: 'hsl(280 14% 66%)', musky: 'hsl(30 12% 56%)',
    spicy: 'hsl(20 45% 48%)', woody: 'hsl(28 32% 44%)', gourmand: 'hsl(35 48% 54%)',
    resinous: 'hsl(15 35% 42%)', leather: 'hsl(20 25% 36%)', amber: 'hsl(38 52% 50%)',
    other: 'hsl(0 0% 55%)'
  };
  /* ============================================================
     THE FIELD, REWRITTEN AS A MORPH (owner, 2026-08-26: "rework this
     bubble system to be a cool morph effect that is more robust and
     cooler").

     WHAT IS NEW, AND WHY EACH PIECE IS THE WAY IT IS:

       · THE FIELD IS A PICTURE OF WHAT IS LEFT. A disc's size is its
         LIVE coverage - how many candidates still carry it - so every
         pick visibly re-proportions the whole field. A family or a note
         nothing left carries does not sit there greyed out: it shrinks
         to a bead, keeps its bearing out of the field and goes. That
         departure IS the morph, and it is the same fact the dead options
         on the other panels are painted from.

       · PLACEMENT ORDER IS STATIC, SIZE IS LIVE. The packer is walked in
         a fixed order (total carriers, which never changes), so a repack
         moves discs a little rather than reshuffling the field. A morph
         a shopper cannot follow is just a flicker.

       · NOTHING LAYS OUT DURING MOTION. Position rides the `translate`
         property, size rides `scale` on the glass, press and drift ride
         `transform` on the layer between them - three independent
         transform properties, so all three compose without fighting and
         none of them is left/top/width/height. The old field transitioned
         four LAYOUT properties on up to thirty discs at once, which is
         where the jank came from.

       · THE BLOOM IS ENTERING THE BUBBLE, not standing beside it. The
         tapped family swells to the core seat, the rest of the field
         leaves outward, the family's own cast washes the whole field,
         and its notes are born INSIDE the core and swim out. Closing
         reverses every one of those moves - the same nodes travel, which
         is the difference between a morph and a cut.
     ============================================================ */
  let NEB = null;           /* { fams: [{key, word, notes[], el, live, held}], byNote: Map } */
  let nebMode = 'field';    /* field | bloom | find */
  let nebOpen = '';         /* the family in bloom */
  let nebQ = '';            /* the live search term */
  let nebLaid = false;
  let nebTries = 0;
  /* set when the field was asked to re-pack while its panel was not on
     screen; the walk onto the notes panel packs it then (see nebFlow) */
  let nebStale = false;
  let nebCloud = null;      /* the layer the note orbs mount into */
  let nebCore = null;       /* the core seat - where notes are born and die */
  let nebHits = [];         /* the search's own note list */
  let nebShown = [];        /* the note objects currently mounted */
  let nebSig = '';          /* what the field is already showing, so a repack only happens when it must */
  /* THE MARK. Love is the default and the field behaves exactly as it did;
     on 'out' a tap refuses what it lands on, and a tap on a FAMILY refuses
     the whole family - the one preference this page could not say at all
     (owner, 2026-08-26: "also option to dislike if possible tho?"). A mode
     with a strong default and a visible switch, rather than a second tap
     nobody finds. */
  let nebMark = 'love';
  /* .sf-orb's own box in finder.css. The orb is drawn once at this size and
     scaled to every other one, so keep the two numbers together. */
  const ORB_BASE = 140;

  function nebNoteName(key) {
    const meta = PREP && PREP.noteMap[key];
    return meta ? meta.name : cap(key);
  }
  function famWord(key) { return FAM_WORD[key] || cap(key); }
  /* WHAT A TAP MEANS. Love leaves the field exactly as it was; Rule out
     turns every disc into a refusal, families included. */
  function setMark(value) {
    nebMark = value === 'out' ? 'out' : 'love';
    qsa('[data-fx-mark]').forEach((b) => {
      const on = b.dataset.fxMark === nebMark;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (nebEl) nebEl.classList.toggle('has-outmark', nebMark === 'out');
    nebNumbers();
  }
  function buildNeb() {
    if (!nebEl || !nebField || !PREP) return;
    armSwell();
    const fams = new Map();
    PREP.noteList.forEach((meta) => {
      if (!meta.count) return;
      const famKey = meta.family && JE.KB.families[meta.family] ? meta.family : 'other';
      if (!fams.has(famKey)) fams.set(famKey, { key: famKey, word: FAM_WORD[famKey] || cap(famKey), notes: [] });
      fams.get(famKey).notes.push({ key: meta.norm, name: meta.name, carriers: meta.count, live: meta.count });
    });
    const list = Array.from(fams.values());
    list.forEach((f) => {
      f.notes.sort((a, b) => b.carriers - a.carriers || a.name.localeCompare(b.name));
      f.carriers = f.notes.reduce((a, n) => a + n.carriers, 0);
      f.live = f.carriers;
      f.solo = null;              /* a runtime reading - see nebCount */
    });
    list.sort((a, b) => b.carriers - a.carriers);
    /* note key -> family key, for the live family counts and the tints */
    const byNote = new Map();
    list.forEach((f) => f.notes.forEach((n) => byNote.set(n.key, f.key)));
    NEB = { fams: list, byNote: byNote };
    /* EVERY NOTE ROW LEARNS ITS FAMILY (2026-08-31, owner: "each note has
       like a color vibe tho and or category"). The map above is the only
       place in the theme that knows which family a note belongs to, and it
       is a closure in here. rooms-v2.js paints the room's liquid from the
       families being chosen and had no way to see a NOTE's family at all, so
       loving "Bergamot" moved nothing while loving "Citrus" moved the whole
       room. One attribute closes that: it is written once, it is derived
       from the same map the nebula draws itself with, and it is inert to
       everything that does not look for it. */
    qsa('[data-fx-note]').forEach((row) => {
      const fam = byNote.get(noteKeyOf(row));
      if (fam) row.dataset.fam = fam;
    });
    nebCloud = el('div', 'sf-neb__cloud');
    nebField.appendChild(nebCloud);
    nebEl.hidden = false;
    root.classList.add('has-neb');
    nebPaint();
  }

  /* ---- the orb. Three nested layers because three separate things move
     it and each one gets a transform property of its own:
       .sf-orb      translate  - where it is        (the glide)
       .sf-orb__d   transform  - drift and lean     (the life)
                    scale      - press and hover    (the touch)
       .sf-orb__g   scale      - how big it is      (the breath)
     The label rides the drift layer, NOT the glass, so it never scales
     with the disc and never needs a counter-scale to stay legible. ---- */
  function nebOrb(kind, word, tint) {
    const b = btn('sf-orb sf-orb--' + kind);
    if (tint) b.style.setProperty('--tint', tint);
    const d = el('span', 'sf-orb__d');
    /* THE SKIN AND THE GLASS ARE TWO LAYERS because a liquid body needs
       TWO stretch axes. One of them alone is an ellipse of fixed
       orientation, which reads as a squashed ball; two of them, turning
       at different rates, give an ellipse whose eccentricity AND axis
       both wander - and that is the fundamental oscillation mode of a
       real droplet. Both are pure transforms, so the whole thing still
       lives on the compositor. The skin also carries the SIZE, which is
       why placeOrb writes its scale there now. */
    const k = el('span', 'sf-orb__s');
    const g = el('span', 'sf-orb__g');
    g.appendChild(el('span', 'sf-orb__spec'));
    const t = el('span', 'sf-orb__t');
    t.appendChild(el('span', 'sf-orb__k', word));
    const n = el('b', 'sf-orb__n');
    t.appendChild(n);
    k.appendChild(g);
    d.appendChild(k);
    d.appendChild(t);
    b.appendChild(d);
    b._s = k; b._g = g; b._n = n;
    return b;
  }
  /* the transition delay, on both layers that carry a transition - the
     glide is on the orb and the swell is on the skin, and a wave that
     only half arrives is worse than no wave */
  function nebDelay(orb, ms) {
    const v = (reduceMotion.matches ? 0 : Math.round(ms)) + 'ms';
    orb.style.transitionDelay = v;
    if (orb._s) orb._s.style.transitionDelay = v;
  }
  function placeOrb(orb, x, y, r) {
    const d = Math.max(8, Math.round(r * 2));
    orb.style.translate = x.toFixed(1) + 'px ' + y.toFixed(1) + 'px';
    orb._s.style.scale = (d / ORB_BASE).toFixed(4);
    orb.style.setProperty('--d', d + 'px');
    orb.style.setProperty('--depth', Math.min(1.5, r / 58).toFixed(2));
    /* the label steps between three sizes rather than tracking the disc
       continuously: a font-size that moves is a layout on every frame */
    orb.classList.toggle('is-xs', d < 54);
    orb.classList.toggle('is-sm', d >= 54 && d < 78);
    orb.classList.toggle('is-lg', d >= 120);
    orb._x = x; orb._y = y; orb._r = r;
  }
  /* leaving: keep the bearing out of the field and shrink to a bead, so
     the eye reads a departure rather than a deletion */
  function retireOrb(orb, cx, cy, push) {
    if (!orb || orb.classList.contains('is-gone')) return;
    const x = orb._x == null ? cx : orb._x;
    const y = orb._y == null ? cy : orb._y;
    const dx = x - cx, dy = y - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const k = push || 1.45;
    nebDelay(orb, 0);
    orb.classList.add('is-gone');
    /* out of the field is also out of the tab order: a retired disc is
       still in the DOM so it can come back, and a keyboard would otherwise
       walk through seven invisible families to reach the eighth */
    orb.inert = true;
    placeOrb(orb, cx + (dx / len) * len * k, cy + (dy / len) * len * k, Math.max(6, (orb._r || 20) * 0.35));
  }

  /* ---- geometry. Radii first: area-shared with a compressed exponent so
     "Powdery, one carrier" is a disc and not a full stop; then a
     centre-seeking packer - every candidate ring is walked at many angles
     and the spot CLOSEST to the focus wins, which is what holds the field
     in one cluster instead of a spiral arm with stragglers. ---- */
  /* A SMALL FIELD MUST NOT BALLOON. The area share is what makes the field
     look full at any size, but taken literally it means that narrowing from
     twenty discs to eight doubles every survivor - which throws them all
     somewhere new, and a morph nobody can follow is just a shuffle. The
     share is damped by how many are left, so the survivors mostly close
     ranks instead. */
  function nebShare(base, n, full) {
    return base * Math.min(1, 0.5 + (0.5 * n) / full);
  }
  function nebSizes(items, W, H, share, minR, maxFrac) {
    const total = items.reduce((a, b) => a + Math.pow(Math.max(1, b.v), 0.72), 0) || 1;
    const K = Math.sqrt((W * H * share) / (Math.PI * total));
    const rMax = Math.min(W, H) * maxFrac;
    items.forEach((it) => {
      it.r = Math.max(minR, Math.min(rMax, K * Math.pow(Math.max(1, it.v), 0.36)));
    });
  }
  /* THE ORDER IS THE CALLER'S. It used to sort by radius here, which meant
     one count changing could re-order the whole placement and throw every
     disc somewhere new. The caller passes a stable order (total carriers)
     and only the sizes move. */
  function nebPackOnce(items, W, H, opts) {
    const gap = opts.gap != null ? opts.gap : 7;
    /* THE FIELD HAS EDGES AND THEY ARE NOT THE FRAME. A disc allowed to
       graze the top of the box sits against the search bar with nothing
       between them - and then drifts a few pixels closer. The pack works
       inside an inset rectangle instead. */
    const pad = opts.pad != null ? opts.pad : 16;
    /* and MORE room at the top, because that edge has the search bar
       against it: a disc allowed to fill the box to its ceiling sits 25px
       under the bar and then drifts a few closer (owner, 2026-08-26:
       "just check spacing with search") */
    const padT = opts.padTop != null ? opts.padTop : pad + 14;
    const cx = opts.cx != null ? opts.cx : W / 2;
    const cy = opts.cy != null ? opts.cy : H / 2;
    const placed = opts.seed ? [opts.seed] : [];
    let failed = 0;
    items.forEach((it, idx) => {
      if (!placed.length) { it.x = cx; it.y = cy; placed.push(it); return; }
      let best = null;
      for (let rad = 0; !best && rad <= Math.max(W, H); rad += 7) {
        for (let k = 0; k < 26; k++) {
          const ang = (k / 26) * Math.PI * 2 + idx * 0.71;
          const x = cx + Math.cos(ang) * rad * 1.12;
          const y = cy + Math.sin(ang) * rad * 0.9;
          if (x - it.r < pad || x + it.r > W - pad || y - it.r < padT || y + it.r > H - pad) continue;
          let ok = true;
          for (let p = 0; p < placed.length; p++) {
            const dx = x - placed[p].x, dy = y - placed[p].y, m = placed[p].r + it.r + gap;
            if (dx * dx + dy * dy < m * m) { ok = false; break; }
          }
          if (!ok) continue;
          const d2 = (x - cx) * (x - cx) + (y - cy) * (y - cy) * 1.35;
          if (!best || d2 < best.d2) best = { x: x, y: y, d2: d2 };
        }
      }
      if (best) { it.x = best.x; it.y = best.y; }
      else { it.x = cx; it.y = cy; failed++; }
      placed.push(it);
    });
    return failed;
  }
  /* pack, and if the field genuinely cannot hold everyone at these sizes,
     shrink EVERY disc a step and try again - the fallback used to drop the
     stragglers onto the centre, where they stacked into a phantom pile
     behind whichever disc owned it */
  function nebPack2(items, W, H, opts) {
    opts = opts || {};
    for (let pass = 0; pass < 7; pass++) {
      const failed = nebPackOnce(items, W, H, opts);
      if (!failed) return items;
      items.forEach((it) => { it.r = Math.max(18, it.r * 0.9); });
    }
    nebPackOnce(items, W, H, opts);
    return items;
  }
  /* CENTRE WHAT WAS PACKED, not what it was packed AROUND. The packer grows
     a cluster outward from a focus, and where that cluster ends up is
     whatever the shapes allowed - with seven discs in a wide field it
     settles high and to one side, which reads as the field having drifted
     away from the bar above it. Measuring the cluster and moving it once
     costs nothing and makes the air around it even at every count.
     Returns the shift, because in bloom the core is part of the cluster. */
  function nebCentre(items, W, H, seed) {
    const all = seed ? items.concat([seed]) : items;
    if (!all.length) return { dx: 0, dy: 0 };
    let l = Infinity, r = -Infinity, t = Infinity, b = -Infinity;
    all.forEach((it) => {
      l = Math.min(l, it.x - it.r); r = Math.max(r, it.x + it.r);
      t = Math.min(t, it.y - it.r); b = Math.max(b, it.y + it.r);
    });
    const dx = (W - (l + r)) / 2;
    const dy = (H - (t + b)) / 2;
    all.forEach((it) => { it.x += dx; it.y += dy; });
    return { dx: dx, dy: dy };
  }
  function nebGeom() {
    const W = nebField ? nebField.clientWidth : 0;
    const H = nebField ? nebField.clientHeight : 0;
    return W && H ? { W: W, H: H } : null;
  }

  /* ---- one pass over the candidate set: what each family and each note
     is worth right now. Everything the field draws comes from here. ---- */
  /* THE SHELF POOL - everything the chosen shelf admits, before any other
     answer narrows it. It is the honest denominator for a note that is NOT
     in the running: "nothing left carries oud" is true and useless, while
     "twelve scents carry oud, none of them still in" is a door. The shelf
     itself is never widened past what was asked for, so a masculine shopper
     is never steered at a feminine bottle. */
  function shelfPool() {
    if (!PREP) return [];
    if (!S.for.length) return SCENTS.filter((p) => p.a !== false);
    return SCENTS.filter((p) => p.a !== false && JE.audienceAdmits(S.for, p.g));
  }
  function tally(list) {
    const note = Object.create(null);
    const fam = Object.create(null);
    for (let i = 0; i < list.length; i++) {
      const keys = NOTEKEYS && NOTEKEYS.get(list[i].h);
      if (!keys) continue;
      const seen = new Set();
      for (let j = 0; j < keys.length; j++) {
        const k = keys[j];
        note[k] = (note[k] || 0) + 1;
        const f = NEB.byNote.get(k);
        if (f && !seen.has(f)) { seen.add(f); fam[f] = (fam[f] || 0) + 1; }
      }
    }
    return { note: note, fam: fam };
  }
  /* how many notes that are not in the running the bloom will still carry -
     shared, because nebCount has to reason about the same set nebFlow draws */
  const AWAY_NOTES = 5;
  const AWAY_FAMS = 6;
  /* THE FADED DISCS SAY WHAT THEY ARE (owner, 2026-10-22: "improve the note
     system ... things that might be confusing"). An away disc carried a bare
     "-> 4" and nothing on the page said what the arrow or the number meant.
     One quiet line under the field now does, whenever one is showing. */
  const AWAY_SAY = 'Faded ones are in other scents. Tap one to steer the answer toward it.';
  function awayLine(list) { return list.some((x) => x._away) ? AWAY_SAY : ''; }
  /* ============================================================
     THE FIELD HAS A SEAT COUNT, AND IT IS THE BOX THAT SAYS SO.
     (owner, 2026-09-02: "improve the bubbles on mobile", and "make the
     refine through notes not so hectic, cleaner".)

     WHAT IT LOOKED LIKE. Opening Woods on a 375 phone handed the packer
     eighteen discs and a 317x276 field with a 132px core sitting in the
     middle of it. nebPack2 shrank every disc seven times looking for a
     seating, hit its own floor, and dropped the stragglers on the core -
     where they stacked into a pile the core then covered, one label
     ("...houli") sticking out from behind it. The ones it did seat came out
     at 36px carrying words like "Cashmere Wood". Measured, not guessed: the
     four unseated discs all reported the identical scale, and two of the
     retiring family discs were 57px past the right edge.

     The desktop field is more than twice the area and seats all eighteen
     comfortably, which is why this only ever showed up on a phone.

     SO THE CAP IS DERIVED, NOT TYPED. A disc stops being legible below the
     `is-xs` threshold the stylesheet already draws at (54px, r 27), so the
     honest question is how many discs of that size the box can seat once
     the core has taken its share. 0.58 is the packing fraction that matches
     what nebPackOnce actually achieves - it grows a ring at a time round a
     focus rather than solving a packing, so it never approaches the
     theoretical 0.9. Measured against it: 12 seats at 375, 30 at 1280,
     which is why nothing about the desktop field changes.

     NOTHING IS EVER SILENTLY DROPPED. What comes off is said under the
     field, in the same caption that already names the greyed-out notes, and
     the search bar above reaches every note by name whatever the field is
     showing.
     ============================================================ */
  function nebCoreR(g, phone) {
    /* the core is the shopper's way back out, so it never goes under a
       thumb - but on a phone it was eating a fifth of the field */
    return Math.max(phone ? 46 : 52, Math.min(g.W, g.H) * (phone ? 0.2 : 0.24));
  }
  function nebSeats(g, coreR, rMin) {
    const box = Math.max(0, g.W - 32) * Math.max(0, g.H - 46);
    const free = box * 0.58 - Math.PI * coreR * coreR;
    return Math.max(4, Math.floor(free / (Math.PI * rMin * rMin)));
  }
  /* held first and always - a loved or refused note has to stay takeable
     back from the field it was picked on - then the rest in the order the
     caller ranked them. Returns what was left behind so it can be said. */
  function nebSeat(list, seats) {
    if (list.length <= seats) return { shown: list, cut: 0 };
    const held = list.filter((n) => n.loved || n.out);
    const rest = list.filter((n) => !n.loved && !n.out);
    const shown = held.concat(rest.slice(0, Math.max(0, seats - held.length)));
    return { shown: shown, cut: list.length - shown.length };
  }
  /* "Musk" against Musk, and "Fresh Notes" against Fresh: one word is the
     other, or contains it. Anything else is a genuinely different name. */
  function sameWord(a, b) {
    const x = JE.norm(a || ''), y = JE.norm(b || '');
    if (!x || !y) return false;
    return x === y || x.indexOf(y) !== -1 || y.indexOf(x) !== -1;
  }
  function nebCount() {
    if (!NEB) return;
    const live = (CAND && CAND.live) || SCENTS;
    const t = tally(live);
    const w = tally(shelfPool());
    const note = t.note, fam = t.fam;
    NEB.fams.forEach((f) => {
      f.live = fam[f.key] || 0;
      f.wide = w.fam[f.key] || 0;
      /* a refused family carries none of the running by definition, so it
         would leave the field - and a pick must always stay where it was
         made, or there is no way to take it back */
      f.out = S.avoidFam.indexOf(f.key) !== -1;
      f.held = f.out;
      if (f.solo) f.held = f.held || S.love.indexOf(f.solo.key) !== -1 ||
        S.avoid.indexOf(f.solo.key) !== -1;
      f.notes.forEach((n) => {
        n.live = note[n.key] || 0;
        n.wide = w.note[n.key] || 0;
        n.loved = S.love.indexOf(n.key) !== -1;
        n.out = S.avoid.indexOf(n.key) !== -1;
        if (n.loved || n.out) f.held = true;
      });
      /* A BUBBLE INSIDE A BUBBLE, BOTH SAYING MUSK (owner, 2026-08-28:
         "musk, musk doesnt make sense tho, it should then just be liked or
         disliked").

         Musk has five notes in the catalogue and, on a narrowed shelf, one
         showable one - itself. So the family opened onto a room containing
         only its own door: MUSK, and inside it Musk. Fresh and Powdery do the
         same with "Fresh Notes" and "Powdery Notes".

         It is a RUNTIME question, not a static one - which is why it is
         answered here, against the same set nebFlow is about to draw. Where
         the bloom would hold exactly one disc bearing the family's own word,
         the family IS that note: it never opens, and a tap loves it, rules it
         out and clears it exactly as a note disc does. A family whose one
         showable note is called something else still opens, because naming it
         tells the shopper something. */
      const shown = f.notes.filter((n) => n.live > 0 || n.loved || n.out);
      const away = f.notes.filter((n) => !n.live && !n.loved && !n.out && n.wide > 0);
      const set = shown.concat(away.slice(0, AWAY_NOTES));
      f.solo = (set.length === 1 && sameWord(set[0].name, f.word)) ? set[0] : null;
    });
  }
  /* the numbers and the states, written onto whatever is mounted. An AWAY
     disc says what it would OPEN rather than what it holds - that number is
     the whole reason it is on the field. */
  function nebNumbers() {
    if (!NEB) return;
    NEB.fams.forEach((f) => {
      if (f.el && f.el.isConnected) {
        const away = !!f._away;
        const solo = f.solo;
        f.el.classList.toggle('is-solo', !!solo);
        f.el._n.textContent = away ? String(f.wide || '')
          : solo ? (solo.live ? String(solo.live) : '')
          : (f.live ? String(f.live) : '');
        f.el.classList.toggle('is-away', away);
        f.el.classList.toggle('is-loved', !!(solo && solo.loved));
        f.el.classList.toggle('is-out', solo ? !!solo.out : !!f.out);
        /* these are the ONLY copy a screen reader gets off the field, so
           they take the same plain words the page uses - "on this shelf" was
           this file's own name for the preference the shopper chose */
        f.el.setAttribute('aria-label', solo
          ? (solo.loved ? f.word + ': loved. Tap to rule it out.'
            : solo.out ? f.word + ': ruled out. Tap to clear.'
            : away ? f.word + ': nothing still in the running carries it. ' + f.wide +
                ' others do. Tap to steer to them.'
            : f.word + ', ' + f.live + ' scents. Tap to love it.')
          : f.out ? f.word + ': ruled out. Tap to allow it again.'
          : away ? f.word + ': nothing still in the running is. ' + f.wide +
              ' others are. Open its notes to steer there.'
          : f.word + ', ' + f.live + ' scents. ' + (nebMark === 'out' ? 'Tap to rule the family out.' : 'Open its notes.'));
      }
      f.notes.forEach((n) => {
        if (!n.el || !n.el.isConnected) return;
        const away = !!n._away;
        const none = !!n._none;
        n.el._n.textContent = none ? '' : away ? String(n.wide || '') : (n.live ? String(n.live) : '');
        n.el.classList.toggle('is-away', away);
        n.el.classList.toggle('is-none', none);
        n.el.classList.toggle('is-loved', !!n.loved);
        n.el.classList.toggle('is-out', !!n.out);
        n.el.setAttribute('aria-label', n.loved ? n.name + ': loved. Tap to rule it out.'
          : n.out ? n.name + ': ruled out. Tap to clear.'
          : none ? n.name + ': we do not make anything with it.'
          : away ? n.name + ': nothing still in the running carries it. ' + n.wide +
              ' others do. Tap to steer to them.'
          : n.name + ', ' + n.live + ' scents. Tap to love it.');
      });
    });
  }

  /* ---- THE FLOW. One function decides what belongs on the field for the
     mode we are in, packs it, and glides everything to where it goes.
     Nodes persist; only their translate and scale change, which is why a
     pick reads as the field re-proportioning itself rather than a
     redraw. ---- */
  /* THE GREYED ONES, EXPLAINED IN THE SHOPPER'S OWN WORDS (owner,
     2026-08-28: "say No Masculine / Femine whatever they selected fragrances
     with this note ... in a very clean and sophysticated way").

     One quiet line under the field, naming the shelf THEY chose and the notes
     it has none of. It is a caption, not an alert: no colour, no icon, and it
     is gone the moment the search is. */
  let nebWordEl = null;
  /* `rest` is the second thing the caption can have to say: how many notes
     the field could not seat (THE FIELD HAS A SEAT COUNT). Both, either or
     neither - the element is one line either way and goes when it is empty. */
  function nebWord(list, rest) {
    if (!nebEl) return;
    let say = '';
    if (list.length) {
      const names = list.map((n) => n.name.toLowerCase());
      const shelf = shelfWord();
      let what;
      if (names.length === 1) what = names[0];
      else if (names.length <= 3) what = names.slice(0, -1).join(', ') + ' or ' + names[names.length - 1];
      else what = names.slice(0, 2).join(', ') + ' or ' + (names.length - 2) + ' more like them';
      say = shelf
        ? 'Greyed out: no ' + shelf + ' scent we make carries ' + what + '.'
        : 'Greyed out: we do not make anything with ' + what + ' yet.';
    }
    if (rest) say = say ? say + ' ' + rest : rest;
    if (!say) {
      if (nebWordEl) { nebWordEl.hidden = true; nebWordEl.textContent = ''; }
      return;
    }
    if (!nebWordEl) {
      nebWordEl = el('p', 'sf-neb__word');
      nebEl.insertBefore(nebWordEl, nebPicks && nebPicks.parentElement === nebEl ? nebPicks : null);
    }
    nebWordEl.textContent = say;
    nebWordEl.hidden = false;
  }
  /* the standing set, then the best of what is not standing. `_away` rides
     the object so the sizer, the paint and the label all read one flag. */
  function nebWithAway(live, away, cap) {
    live.forEach((x) => { x._away = false; x._none = false; });
    away.forEach((x) => { x._away = true; x._none = false; });
    away.sort((a, b) => (b.wide - a.wide) || String(a.key).localeCompare(String(b.key)));
    return live.concat(away.slice(0, Math.max(0, cap)));
  }
  function nebFlow(force) {
    if (!NEB || !nebField) return;
    /* THE FIELD IS PACKED WHERE IT CAN BE SEEN (2026-09-22, lane 8). Every
       answer on every other question re-packed the nebula behind the scenes
       - fifteen to twenty-eight discs measured, placed and flushed inside
       the tap - for a detour most walks never take. Measured on a throttled
       phone that was most of a pick's own cost. Off screen it now only
       notes that it is out of date, and go('notes') packs it on arrival,
       the same path its very first visit has always taken. */
    const np = nebField.closest('.sf-panel');
    if (np && !np.classList.contains('is-on') && !root.classList.contains('is-flat')) { nebStale = true; return; }
    nebStale = false;
    const g = nebGeom();
    if (!g) {
      /* a TIMER, not a frame. requestAnimationFrame does not fire while the
         tab is in the background, and a field that only exists once someone
         looks at the tab is not a field. */
      if (nebTries++ < 40) window.setTimeout(() => nebFlow(force), 30);
      return;
    }
    nebTries = 0;
    nebLaid = true;
    const phone = g.W < 560;
    let fams = [], notes = [], core = null;
    if (nebMode === 'find') {
      notes = nebHits;
      /* `_away` is "on this shelf, not in the running"; `_none` is "not on
         this shelf at all". A held note is neither - a pick always reads as
         itself, wherever the running has got to. */
      notes.forEach((n) => {
        n._away = !n.live && !n.loved && !n.out && n.wide > 0;
        n._none = !n.live && !n.loved && !n.out && !n.wide;
      });
      nebWord(notes.filter((n) => n._none), awayLine(notes));
    } else if (nebMode === 'bloom') {
      core = NEB.fams.filter((f) => f.key === nebOpen)[0] || null;
      /* a note that is loved or refused stays on the field whatever the
         running says - it is a pick, and a pick must always be takeable
         back from where it was made */
      /* THE ONES THAT WOULD STEER IT SOMEWHERE ELSE (owner, 2026-08-28:
         "add in other notes that migth steer the fragrance into a different
         note ... we want the notes to be inpactful if relevant tho").

         Only notes the running still carries used to be drawn, so the moment
         a brief narrowed, the field held nothing but confirmations of what
         the shopper had already said. The interesting notes - the ones that
         would take the answer somewhere else - were exactly the ones missing.

         They are on the field now, as AWAY discs: smallest size, quiet glass,
         and their number is how many scents on the chosen shelf carry them.
         Tapping one is a real move - narrow() lets the cheapest want go and
         run() says which - so the field is a way to change your mind rather
         than only a way to agree with yourself. Capped, and ranked by what
         they would open, so the field stays a field. */
      notes = core ? nebWithAway(core.notes.filter((n) => n.live > 0 || n.loved || n.out),
        core.notes.filter((n) => !n.live && !n.loved && !n.out && n.wide > 0), AWAY_NOTES) : [];
      /* AND THEN THE BOX HAS ITS SAY - see THE FIELD HAS A SEAT COUNT. */
      const seated = nebSeat(notes, nebSeats(g, nebCoreR(g, phone), phone ? 27 : 27));
      notes = seated.shown;
      nebWord([], [awayLine(notes), seated.cut && core
        ? seated.cut + ' more note' + (seated.cut === 1 ? '' : 's') + ' in ' +
          core.word + ' - search for ' + (seated.cut === 1 ? 'it' : 'one') + ' by name.'
        : ''].filter(Boolean).join(' '));
    } else {
      fams = nebWithAway(NEB.fams.filter((f) => f.live > 0 || f.held),
        NEB.fams.filter((f) => !f.live && !f.held && f.wide > 0), AWAY_FAMS);
      nebWord([], awayLine(fams));
    }
    const sig = nebMode + '|' + Math.round(g.W) + 'x' + Math.round(g.H) + '|' +
      (core ? core.key : '') + '|' +
      fams.map((f) => f.key + ':' + f.live + (f._away ? 'a' : '')).join(',') + '|' +
      notes.map((n) => n.key + ':' + n.live + (n._away ? 'a' : '')).join(',');
    if (!force && sig === nebSig) return;
    nebSig = sig;

    const cx = g.W / 2, cy = g.H / 2;
    /* --- the core seat, when a family is in bloom --- */
    if (core) {
      nebCore = { x: cx, y: cy, r: nebCoreR(g, phone) };
      nebEl.style.setProperty('--wash', FAM_TINT[core.key] || FAM_TINT.other);
    } else if (nebMode === 'find') {
      nebCore = { x: cx, y: cy, r: 0 };
    } else {
      nebCore = null;
    }

    /* --- the families. Anything arriving is put down as a bead first and
       collected; the whole batch is flushed once and released together, so
       one forced layout buys every entrance in the field. --- */
    const rising = [];
    /* THE REPACK ARRIVES AS A RIPPLE. Everything setting off at the same
       instant reads as a diagram redrawing itself; the same moves, let
       out of the middle a few milliseconds apart, read as the field
       flowing into its new shape. Distance from the focus, normalised
       against the field's own reach. */
    const reach = Math.max(60, Math.hypot(g.W, g.H) / 2);
    const wave = (x, y, cx2, cy2) => 130 * Math.min(1, Math.hypot(x - cx2, y - cy2) / reach);
    if (fams.length) {
      const items = fams.map((f) => ({ v: f._away ? 0.5 : Math.max(1, f.live), f: f }));
      nebSizes(items, g.W, g.H, nebShare(phone ? 0.5 : 0.46, fams.length, 13), phone ? 30 : 38, 0.23);
      nebPack2(items, g.W, g.H, { gap: 8 });
      nebCentre(items, g.W, g.H, null);
      items.forEach((it, i) => {
        const f = it.f;
        if (!f.el) {
          f.el = nebOrb('fam', f.word, FAM_TINT[f.key] || FAM_TINT.other);
          f.el.setAttribute('data-neb-fam', f.key);
          f.el.style.setProperty('--i', i);
          f.el.classList.add('is-born');
          nebField.insertBefore(f.el, nebCloud);
          placeOrb(f.el, it.x, it.y, 6);
        }
        f.el.classList.remove('is-core');
        f.el.removeAttribute('data-neb-close');
        f.el.setAttribute('aria-expanded', 'false');
        if (f.el.classList.contains('is-born') || f.el.classList.contains('is-gone')) {
          nebArm(f.el, i);
          rising.push([f.el, it.x, it.y, it.r]);
        } else {
          nebDelay(f.el, wave(it.x, it.y, cx, cy));
          placeOrb(f.el, it.x, it.y, it.r);
        }
      });
    }
    /* whatever is not on the field leaves it */
    NEB.fams.forEach((f) => {
      if (!f.el || fams.indexOf(f) !== -1) return;
      if (core === f) {
        f.el.classList.remove('is-gone');
        f.el.inert = false;
        f.el.classList.add('is-core');
        f.el.setAttribute('data-neb-close', '');
        f.el.setAttribute('aria-expanded', 'true');
        f.el.setAttribute('aria-label', 'Close ' + f.word);
        nebDelay(f.el, 0);
        placeOrb(f.el, nebCore.x, nebCore.y, nebCore.r);
      } else {
        f.el.classList.remove('is-core');
        f.el.removeAttribute('data-neb-close');
        f.el.setAttribute('aria-expanded', 'false');
        retireOrb(f.el, cx, cy, nebMode === 'field' ? 1.5 : 1.9);
      }
    });

    /* --- the notes. Born inside the core, home into it when they go --- */
    const home = nebCore || { x: cx, y: cy, r: 20 };
    nebShown.forEach((n) => {
      if (notes.indexOf(n) !== -1 || !n.el) return;
      n.el.classList.add('is-gone');
      placeOrb(n.el, home.x, home.y, Math.max(5, home.r * 0.08));
      const dead = n.el;
      n.el = null;
      window.setTimeout(() => dead.remove(), reduceMotion.matches ? 0 : 520);
    });
    nebShown = notes.slice();
    if (notes.length) {
      const items = notes.map((n) => ({ v: n._away ? 0.5 : Math.max(1, n.live), n: n }));
      /* SEARCH RESULTS ARE NOT SURVIVORS. The damping exists so a shrinking
         set closes ranks instead of ballooning, and a fresh set of search
         hits has nothing to close ranks with - five of them at the bloom's
         share left a field that was almost all air. */
      const finding = nebMode === 'find';
      /* 27 IS THE FLOOR ON BOTH NOW, not 24 on a phone. 24 draws a 48px disc,
         which is under the 54px the stylesheet calls `is-xs` and sets at
         10px - and the seat count above is derived from 27, so a floor below
         it would let the packer seat more discs than the cap admits and put
         the two answers back out of step. */
      nebSizes(items, g.W, g.H,
        finding ? 0.34 : nebShare(0.34, notes.length, 18),
        27, finding ? 0.16 : 0.125);
      const seed = home.r ? { x: home.x, y: home.y, r: home.r * 1.04 } : null;
      /* the gap stays TIGHT on a phone. Widening it to give the labels air
         backfired: the discs are already at the size floor there, so the
         pack could not seat them and shrank every one to 38px, which made
         the names relatively bigger and the field worse. */
      nebPack2(items, g.W, g.H, { gap: 6, cx: home.x, cy: home.y, seed: seed });
      /* the core is part of the cluster, so it travels with it - and `home`
         IS nebCore in bloom, which is what the notes are born out of */
      const shift = nebCentre(items, g.W, g.H, seed);
      if (core && nebCore && core.el) {
        nebCore.x += shift.dx;
        nebCore.y += shift.dy;
        placeOrb(core.el, nebCore.x, nebCore.y, nebCore.r);
      }
      items.forEach((it, i) => {
        const n = it.n;
        const famKey = NEB.byNote.get(n.key) || 'other';
        if (!n.el) {
          n.el = nebOrb('note', n.name, FAM_TINT[famKey] || FAM_TINT.other);
          n.el.setAttribute('data-neb-note', n.key);
          n.el.style.setProperty('--i', i);
          n.el.classList.add('is-born');
          nebCloud.appendChild(n.el);
          /* born INSIDE the core it belongs to, and it swims out from there */
          placeOrb(n.el, home.x, home.y, Math.max(5, (home.r || 20) * 0.1));
          nebArm(n.el, i);
          rising.push([n.el, it.x, it.y, it.r]);
        } else {
          nebDelay(n.el, wave(it.x, it.y, home.x, home.y));
          placeOrb(n.el, it.x, it.y, it.r);
        }
      });
    }
    if (rising.length) {
      void nebField.offsetWidth;                 /* the flush - see nebRise */
      rising.forEach((r) => nebRise(r[0], r[1], r[2], r[3]));
    }
    if (nebCloud) {
      const none = nebCloud.querySelector('.sf-neb__none');
      if (nebMode === 'find' && !notes.length && !none) {
        nebCloud.appendChild(el('p', 'sf-fine sf-neb__none', 'No note by that name.'));
      } else if (none && (nebMode !== 'find' || notes.length)) none.remove();
    }
    nebEl.classList.toggle('is-bloom', nebMode !== 'field');
    nebNumbers();
  }
  /* THE ENTRANCE, AND WHY IT IS A FLUSH RATHER THAN A FRAME. An orb is
     put down as a bead at the point it comes from, the pending layout is
     forced so that geometry is genuinely what its transition starts FROM,
     and only then is it placed where it belongs. The old field waited two
     animation frames for the same effect - and animation frames do not
     come in a background tab, so the whole field could sit invisible until
     someone happened to look at it. `void offsetWidth` is the same flush
     the panel transition already uses a few hundred lines up.

     The delay rides both layers because the glide and the swell are
     transitions on two different elements. */
  function nebArm(orb, i) { nebDelay(orb, Math.min(i * 30, 420)); }
  function nebRise(orb, x, y, r) {
    orb.classList.remove('is-born', 'is-gone');
    orb.inert = false;
    placeOrb(orb, x, y, r);
  }
  function nebPaint() {
    if (!NEB || !nebEl || nebEl.hidden) return;
    nebCount();
    nebFlow();
    nebNumbers();
    paintNebPicks();
    nebRescue();
  }

  /* ============================================================
     THE RESCUE - when the notes do not go together, say so and offer a set
     that does (2026-08-31, owner: "the note thingy must indicate if there is
     conflicting notes and such ... and if there is too many odd different
     notes, that it would suggest to optimise their selection based on 3 new
     notes or something like that, like a recovery system that is fully
     automated and dynamic ... to switch the vibe").

     WHAT WAS THERE. The room already told the truth AFTER the fact: narrow()
     drops the cheapest want it can and run() prints "Nothing we make answers
     all of that at once, so the season had to give way." That is honest and
     it is too late - by then the shopper is looking at an answer built out of
     a brief they did not quite give, with no idea which two of their taps
     were fighting each other and nothing to do about it.

     WHAT THIS IS. One reading, done where the taps happen, on the same
     candidate set everything else on the panel reads:

       THE CLASH. Loving two notes is a claim that some bottle carries both.
       If NO scent the chosen shelf admits carries every loved note, the set
       is unbuildable and the room can name exactly which notes cannot travel
       with the rest - not "something had to give", but "amber and sea air do
       not meet in anything we make".

       THE RECOVERY. The anchor is the shelf scent that carries the LARGEST
       subset of what was loved; that subset demonstrably co-exists, because
       one bottle is holding it. It is topped up to three from the anchor's
       own other notes, ranked first by whether they belong to a family the
       shopper is losing (so the trio keeps the VIBE they were reaching for,
       which is the part the owner asked for by name) and then by how many
       scents on the shelf carry them (so the field stays open rather than
       collapsing onto one bottle).

     WHY IT IS TRUSTWORTHY. Every number is counted off the catalogue, never
     modelled: the anchor is a real bottle, the trio is its real notes, and
     the "leaves N in" line is a real count of what would still fit. Nothing
     is proposed that the shopper has ruled out, and nothing is applied
     without a tap - the room offers, it never edits.

     AND IT SWITCHES THE VIBE BY ITSELF. Taking the trio rewrites S.love, and
     the room's tint follows the leading family (rooms-v2.js), so the ground,
     the crests and the canvas field all walk to the new colour on their own.
     That IS the "fully automated and dynamic" half of the ask; it needed no
     new machinery, only a reason to fire.
     ============================================================ */
  let nebFixEl = null;
  let nebFixSig = '';         /* what is on offer now */
  let nebFixWaved = '';       /* what the shopper said "keep mine" to */
  let nebFixSet = [];

  function noteKeysOf(rec) { return (NOTEKEYS && NOTEKEYS.get(rec.h)) || []; }
  /* the shelf, minus anything the shopper has refused. A proposal drawn from
     a bottle carrying a ruled-out note would be the room arguing with them. */
  function rescuePool() {
    const bad = S.avoid;
    const badFam = S.avoidFam;
    if (!bad.length && !badFam.length) return shelfPool();
    return shelfPool().filter((rec) => {
      const keys = noteKeysOf(rec);
      for (let i = 0; i < keys.length; i++) {
        if (bad.indexOf(keys[i]) !== -1) return false;
        const f = NEB && NEB.byNote.get(keys[i]);
        if (f && badFam.indexOf(f) !== -1) return false;
      }
      return true;
    });
  }
  function rescueRead() {
    if (!NEB || !PREP || !NOTEKEYS) return null;
    const loved = S.love.slice();
    if (loved.length < 2) return null;
    const pool = rescuePool();
    if (!pool.length) return null;

    let best = null, bestKeep = [];
    for (let i = 0; i < pool.length; i++) {
      const keys = noteKeysOf(pool[i]);
      const keep = loved.filter((k) => keys.indexOf(k) !== -1);
      if (keep.length > bestKeep.length) { best = pool[i]; bestKeep = keep; }
      if (bestKeep.length === loved.length) break;
    }
    /* something carries the lot: the brief is buildable and there is nothing
       to rescue, however narrow it has become */
    if (!best || bestKeep.length === loved.length) return null;

    const drop = loved.filter((k) => bestKeep.indexOf(k) === -1);
    const dropFams = drop.map((k) => NEB.byNote.get(k)).filter(Boolean);
    /* how many pool scents carry every note in `set` - the only number that
       matters when choosing what to add, because it IS what would be left */
    const holds = (set) => {
      let n = 0;
      for (let i = 0; i < pool.length; i++) {
        const keys = noteKeysOf(pool[i]);
        let all = true;
        for (let j = 0; j < set.length; j++) { if (keys.indexOf(set[j]) === -1) { all = false; break; } }
        if (all) n += 1;
      }
      return n;
    };
    /* THE FILL IS RANKED BY WHAT IT LEAVES STANDING, not by how common the
       note is on its own. A note 29 scents carry is worthless in this trio if
       none of those 29 also carry what is already in it - the first pass
       ranked on that bare popularity and picked exactly such a note, so the
       offer came out at "1 scent carries them". Scoring each candidate
       against the set it would JOIN is one extra pass over 41 records and it
       is the difference between a rescue and a second dead end.

       Family proximity still breaks ties, because that is what keeps the trio
       recognisable as the thing the shopper was reaching for - the vibe - and
       it can only ever choose between options that leave the same number in. */
    let trio = bestKeep.slice();
    while (trio.length < 3) {
      const taken = trio;
      const fill = noteKeysOf(best)
        .filter((k) => taken.indexOf(k) === -1 && S.avoid.indexOf(k) === -1)
        .map((k) => {
          const f = NEB.byNote.get(k);
          return { k: k, left: holds(taken.concat(k)), near: f && dropFams.indexOf(f) !== -1 ? 1 : 0 };
        })
        .filter((c) => c.left > 0);
      if (!fill.length) break;
      fill.sort((a, b) => (b.left - a.left) || (b.near - a.near) || String(a.k).localeCompare(String(b.k)));
      trio.push(fill[0].k);
    }
    if (trio.length < 2) return null;
    const left = holds(trio);
    /* an offer that leaves nothing is not an offer. It cannot happen while
       the anchor is a real bottle carrying every note in the trio, but the
       set is assembled in two places and this is a cheap thing to be sure of. */
    if (!left) return null;
    return { drop: drop, keep: bestKeep, trio: trio, anchor: best, left: left };
  }
  function nebRescue() {
    if (!nebEl || !nebPicks) return;
    const r = rescueRead();
    const sig = r ? r.drop.concat('>', r.trio).join(',') : '';
    if (!r || sig === nebFixWaved) {
      if (nebFixEl) { nebFixEl.hidden = true; }
      nebFixSig = '';
      nebFixSet = [];
      return;
    }
    nebFixSet = r.trio.slice();
    if (sig === nebFixSig && nebFixEl && !nebFixEl.hidden) return;
    nebFixSig = sig;
    if (!nebFixEl) {
      nebFixEl = el('div', 'sf-fix');
      nebFixEl.setAttribute('role', 'status');
      nebPicks.parentElement.insertBefore(nebFixEl, nebPicks.nextSibling);
    }
    const names = (list) => list.map(nebNoteName);
    const join = (list) => list.length === 1 ? list[0]
      : list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
    nebFixEl.replaceChildren();
    nebFixEl.appendChild(el('p', 'sf-fix__k',
      join(names(r.drop)) + (r.drop.length > 1 ? ' do not meet ' : ' does not meet ') +
      join(names(r.keep)) + '.'));
    nebFixEl.appendChild(el('p', 'sf-fix__s',
      'Nothing we make carries all of that at once. ' + r.anchor.t +
      ' comes closest, and these three hold the same idea' +
      (r.left ? ' - ' + r.left + (r.left === 1 ? ' scent carries them' : ' scents carry them') : '') + '.'));
    const set = el('div', 'sf-fix__set');
    names(r.trio).forEach((n) => set.appendChild(el('span', 'sf-fix__note', n)));
    nebFixEl.appendChild(set);
    const acts = el('div', 'sf-fix__acts');
    const take = btn('sf-fix__go', 'Use these three');
    take.setAttribute('data-neb-fix', '');
    acts.appendChild(take);
    const no = btn('sf-fix__no', 'Keep mine');
    no.setAttribute('data-neb-fixno', '');
    acts.appendChild(no);
    nebFixEl.appendChild(acts);
    nebFixEl.hidden = false;
  }

  /* the dock: what is loved and what is refused, always visible, each chip
     a way to take it back - and beside them the TOLL, which is what the last
     tap actually cost.

     THE TOLL IS WHY THIS PANEL IS WORTH USING (owner, 2026-08-28: "the note
     system needs to be ... actually usable / impactful"). The candidate count
     was only ever printed in the running head at the very bottom of the room,
     at eleven pixels, on the far side of the field - so loving a note looked
     like it did nothing. It is a fact about what is left, never a score, and
     it is now said where the tap happens. `.is-moved` is toggled off and back
     on around a change so the number can register the movement without the
     panel re-laying out. */
  let nebTollN = -1;
  function paintNebPicks() {
    if (!nebPicks) return;
    const frag = document.createDocumentFragment();
    const chip = (cls, mark, word, attr, key, label) => {
      const c = btn('sf-pickchip ' + cls, '');
      c.setAttribute(attr, key);
      c.appendChild(el('span', 'sf-pickchip__m', mark));
      c.appendChild(el('span', 'sf-pickchip__w', word));
      c.appendChild(el('span', 'sf-pickchip__x', '×'));
      c.setAttribute('aria-label', label);
      frag.appendChild(c);
    };
    /* '×' (U+00D7), not the U+2715 cross, for the No mark (2026-09-10, audit Q2).
       U+2715 is outside the Inter Latin slice, so the first No a shopper
       tapped fetched the full 187KB inter-var.woff2 - and Inter has no U+2715
       glyph, so the mark still drew from a system font. U+00D7 is in the slice
       the head already preloaded, the same one the chip's own x uses. */
    S.love.forEach((k) => chip('is-love', '♥', nebNoteName(k), 'data-neb-unpick', k,
      'Stop loving ' + nebNoteName(k)));
    S.avoid.forEach((k) => chip('is-no', '×', nebNoteName(k), 'data-neb-unpick', k,
      'Allow ' + nebNoteName(k) + ' again'));
    S.avoidFam.forEach((k) => chip('is-no', '×', famWord(k), 'data-neb-unfam', k,
      'Allow ' + famWord(k) + ' again'));

    const left = CAND ? CAND.live.length : 0;
    const total = SCENTS.length;
    const held = S.love.length + S.avoid.length + S.avoidFam.length;
    if (total) {
      const toll = el('p', 'sf-neb__toll');
      if (!left) {
        toll.appendChild(el('b', 'sf-neb__tolln', '0'));
        toll.appendChild(document.createTextNode(' left - take one back'));
      } else {
        toll.appendChild(el('b', 'sf-neb__tolln', String(left)));
        toll.appendChild(document.createTextNode(
          left === total ? ' scents, all of them still in' : ' of ' + total + ' still fit'));
      }
      frag.appendChild(toll);
      if (left !== nebTollN) {
        nebTollN = left;
        toll.classList.remove('is-moved');
        void toll.offsetWidth;
        toll.classList.add('is-moved');
      }
    }
    nebPicks.replaceChildren(frag);
    nebPicks.classList.toggle('has-picks', held > 0);
    paintNoteDoor();
  }
  /* THE DETOUR'S DOOR CARRIES ITS OWN STATE. A shopper who has been into the
     notes and come back needs the button to say so, or the only evidence of
     four taps is a number in the running head. It counts every mark - loved,
     refused, and a refused family - because all three are things they said in
     there. */
  function paintNoteDoor() {
    const n = S.love.length + S.avoid.length + S.avoidFam.length;
    qsa('[data-fx-notecount]').forEach((el2) => {
      el2.textContent = n ? String(n) : '';
      const door = el2.closest('.sf-go--glass');
      if (door) door.classList.toggle('has-picks', n > 0);
    });
  }
  /* a tap on a note, read through the mark. On Love it is the same
     three-state cycle it has always been. */
  function markNote(key) {
    if (!key) return;
    if (nebMark !== 'out') { cycleNote(key); return; }
    const inOut = S.avoid.indexOf(key);
    if (inOut !== -1) S.avoid.splice(inOut, 1);
    else {
      const loved = S.love.indexOf(key);
      if (loved !== -1) S.love.splice(loved, 1);
      S.avoid.push(key);
    }
    touched.love = true;
    changed();
    sayNote('note', key, 'mark');
  }
  /* a tap on a FAMILY under the Rule out mark, and the way back off it
     whatever the mark says - a pick must always be takeable back from
     where it was made */
  function cycleFam(key) {
    if (!key) return;
    const at2 = S.avoidFam.indexOf(key);
    if (at2 !== -1) S.avoidFam.splice(at2, 1);
    else S.avoidFam.push(key);        /* always taken - see toggleNote */
    touched.avoidFam = true;
    changed();
    sayNote('family', key, 'neb');
  }
  /* the Apple-Music tap: love, then ruled out, then clear */
  function cycleNote(key) {
    if (!key) return;
    const inLove = S.love.indexOf(key);
    const inOut = S.avoid.indexOf(key);
    if (inLove !== -1) {
      S.love.splice(inLove, 1);
      S.avoid.push(key);              /* always taken - see toggleNote */
    } else if (inOut !== -1) { S.avoid.splice(inOut, 1); }
    else { S.love.push(key); }
    touched.love = true;
    changed();
    sayNote('note', key, 'neb');
  }
  function openFam(key) {
    if (!NEB) return;
    const fam = NEB.fams.filter((f) => f.key === key)[0];
    if (!fam) return;
    if (fam.solo) { markNote(fam.solo.key); return; }
    nebQ = '';
    if (nebFind) nebFind.value = '';
    nebOpen = key;
    nebMode = 'bloom';
    nebFlow();
  }
  function closeFam() {
    if (!NEB) return;
    nebOpen = '';
    nebQ = '';
    nebMode = 'field';
    if (nebFind) nebFind.value = '';
    nebEl.style.removeProperty('--wash');
    nebFlow();
  }
  /* THE SEARCH ANSWERS WITH EVERY NOTE (owner, 2026-08-28: "when searching
     it must return results of all possible notes to allow for something to be
     shifted, but greyed out and say No Masculine / Femine ... fragrances with
     this note").

     It used to drop any note the running did not still carry, so typing a
     note we make - and one we do not make on the chosen shelf - both answered
     with the same nothing. A search is a question, and "we have none of that
     here" is an answer; hiding it is not.

     Three readings come back, and the disc says which:
       in the running  - a full glass, its number is what still fits
       away            - on this shelf, none of it left in the running; the
                         number is what tapping it would open (the arrow)
       none            - nothing on the chosen shelf carries it at all. The
                         quietest disc on the field, and the field says why
                         underneath in the shopper's own words.
     Ranked so the ones that can still act come first. */
  function nebSearch(qRaw) {
    if (!NEB) return;
    nebQ = String(qRaw || '').trim();
    if (!nebQ) { closeFam(); return; }
    nebOpen = '';
    nebMode = 'find';
    const q = JE.norm(nebQ);
    const hits = [];
    NEB.fams.forEach((f) => f.notes.forEach((n) => {
      if (JE.norm(n.name).indexOf(q) !== -1) hits.push(n);
    }));
    hits.sort((a, b) => (b.live - a.live) || ((b.wide || 0) - (a.wide || 0)) ||
      a.name.localeCompare(b.name));
    nebHits = hits.slice(0, 22);
    nebFlow();
  }
  /* the one sentence, said the same way wherever it is needed */
  function noneSay(key) {
    const name = nebNoteName(key);
    const shelf = shelfWord();
    return shelf
      ? 'No ' + shelf + ' scent we make carries ' + name.toLowerCase() + '.'
      : 'We do not make anything with ' + name.toLowerCase() + ' yet.';
  }
  /* the shelf, in the words the shopper picked it with, for the line that
     explains an empty answer. Several shelves join with "or". */
  function shelfWord() {
    if (!S.for.length) return '';
    const say = S.for.map((v) => String(v).toLowerCase() === 'gender neutral' ? 'gender neutral' : String(v).toLowerCase());
    return say.length < 2 ? say[0] : say.slice(0, -1).join(', ') + ' or ' + say[say.length - 1];
  }

  /* One mini, straight in. No tester rides along - the free 3ml is the
     50ml's promise, not the mini's. */
  function addMini(button) {
    const rec = PREP && PREP.byHandle[button.getAttribute('data-fx-mini')];
    if (!rec || !rec.mid || button.disabled) return;
    const label = button.textContent;
    button.style.minWidth = button.offsetWidth + 'px';
    button.disabled = true;
    button.textContent = 'Adding';
    const ticket = nextCartTicket();
    cartAdd([{ id: rec.mid, quantity: 1 }])
      .then((res) => {
        if (!res || (res.status && res.status !== 200)) return Promise.reject(new Error('add failed'));
        button.textContent = 'Added';
        button.classList.add('is-added');
        swapDrawer(res.sections, ticket);
        openDrawerConfirm();
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = label;
        button.style.minWidth = '';
        button.classList.remove('is-added');
        cartFail('We could not add that just now. Please try again.');
      });
  }

  /* ---- wiring. Delegated off the section root, so the answer's own
     controls - which do not exist yet - are covered too.

     NOTHING IS INTERCEPTED UNTIL THE CATALOGUE IS IN HAND. Every option
     on this page was rendered as a real link into the shop, and until
     PREP exists that link is the only honest thing it can do. ---- */
  /* SPACE PRESSES A CHECKBOX (2026-09-10, audit T11). Once armAnswers has
     made an answer role="checkbox", a keyboard or screen-reader user will
     reach for Space - and Space on a link scrolls the page. Enter already
     arrives as a click on its own. Keyed on the role, so a flattened room's
     plain links are left to behave like links. */
  root.addEventListener('keydown', (e) => {
    if (e.key !== ' ' || e.altKey || e.ctrlKey || e.metaKey) return;
    const box = e.target.closest && e.target.closest('[role="checkbox"][data-fx-pick], [role="checkbox"][data-fx-toggle]');
    if (!box) return;
    e.preventDefault();
    box.click();
  });
  root.addEventListener('click', (e) => {
    const mini = e.target.closest('[data-fx-mini]');
    if (mini) { e.preventDefault(); addMini(mini); return; }
    const clear = e.target.closest('[data-fx-reset]');
    if (clear) { e.preventDefault(); reset(); return; }

    if (!PREP) return;

    /* D12 - HER DOOR IS AN ACTION. stopPropagation because these carry
       data-chat-open as well: that is the seam initJennie binds at document
       level, and it is the right behaviour when this controller is not
       running. With the controller running it would open the panel a second
       time behind the one openJennie just opened. */
    const hand = e.target.closest('[data-fx-hand]');
    if (hand) { e.preventDefault(); e.stopPropagation(); openJennie(hand); return; }

    const step = e.target.closest('[data-fx-step]');
    const skip = e.target.closest('[data-fx-skip]');
    /* `back` is the detour's own way out, and it is a WORD rather than a step
       name because the panel it returns to depends on where it was opened
       from - see FOUR QUESTIONS, AND NOTES IS A DETOUR. */
    const stepTo = (n) => (n === 'back' ? (noteFrom || 'presence') : n);
    if (skip) {
      e.preventDefault();
      const key = skip.dataset.fxSkip;
      touched[key] = true;
      skipped[key] = true;
      S[key] = Array.isArray(S[key]) ? [] : '';
      changed();
      go(stepTo(skip.dataset.fxStep) || 'answer', skip.dataset.fxStep === 'back' ? -1 : 1, 'skip');
      return;
    }
    if (step) {
      e.preventDefault();
      const to = step.dataset.fxStep;
      if (to === 'notes' && at !== 'notes') noteFrom = at;
      go(stepTo(to), to === 'back' ? -1 : 1);
      return;
    }
    const done = e.target.closest('[data-fx-done]');
    if (done) { e.preventDefault(); go('answer', 1, 'done'); return; }

    const opt = e.target.closest('[data-fx-pick]');
    if (opt) {
      e.preventDefault();
      /* A DEAD OPTION DOES NOT ACT. Nothing in the shop answers it beside
         what is already held, so taking it could only empty the room - and
         until now it was still a live <a> straight out to the shop. The
         room says why rather than swallowing the tap in silence. */
      if (opt.classList.contains('is-dead')) {
        say('Nothing we stock is that as well as what you have already picked.');
        return;
      }
      /* the clear option on a multi group empties the whole selection */
      if (opt.hasAttribute('data-fx-clear') && Array.isArray(S[opt.dataset.fxPick])) {
        touched[opt.dataset.fxPick] = true;
        S[opt.dataset.fxPick] = [];
        changed();
        return;
      }
      const multi = opt.hasAttribute('data-fx-multi');
      pick(opt.dataset.fxPick, opt.dataset.fxValue || '', multi);
      /* AND THAT IS ALL A TAP DOES. Choosing an answer never moves the panel;
         Continue does. See AUTO-ADVANCE IS GONE above. */
      return;
    }
    const toggle = e.target.closest('[data-fx-toggle]');
    if (toggle) {
      e.preventDefault();
      S[toggle.dataset.fxToggle] = !S[toggle.dataset.fxToggle];
      changed();
      return;
    }
    const love = e.target.closest('[data-fx-love]');
    if (love) { toggleNote('love', 'avoid', love.closest('[data-fx-note]')); return; }
    const avoid = e.target.closest('[data-fx-avoid]');
    if (avoid) { toggleNote('avoid', 'love', avoid.closest('[data-fx-note]')); return; }

    /* the nebula's own gestures */
    const marker = e.target.closest('[data-fx-mark]');
    if (marker) { e.preventDefault(); setMark(marker.dataset.fxMark); return; }
    const unpick = e.target.closest('[data-neb-unpick]');
    if (unpick) {
      const k = unpick.getAttribute('data-neb-unpick');
      const li = S.love.indexOf(k); if (li !== -1) S.love.splice(li, 1);
      const ai = S.avoid.indexOf(k); if (ai !== -1) S.avoid.splice(ai, 1);
      changed();
      sayNote('note', k, 'chip');
      return;
    }
    const unfam = e.target.closest('[data-neb-unfam]');
    if (unfam) { cycleFam(unfam.getAttribute('data-neb-unfam')); return; }
    /* THE RESCUE'S TWO ANSWERS. Taking the trio REPLACES the loves rather
       than adding to them - the whole point is that the old set could not be
       held - and everything downstream (the counts, the dead options, the
       tint, the canvas field) is a reading of S.love, so one assignment moves
       the entire room. Waving it away is remembered against THIS offer only,
       so the next genuinely different clash still speaks up. */
    if (e.target.closest('[data-neb-fix]')) {
      if (!nebFixSet.length) return;
      S.love = nebFixSet.slice();
      touched.love = true;
      nebFixWaved = '';
      changed();
      /* the rescue rewrites every love at once; one line says so, and the
         brief on the next step carries the new set */
      loomSay('x.finder.note', { subject: 'fix', state: 'love', via: 'fix', n: nebFixSet.length });
      say('Swapped to ' + nebFixSet.map(nebNoteName).join(', ') + '.');
      return;
    }
    if (e.target.closest('[data-neb-fixno]')) {
      nebFixWaved = nebFixSig;
      if (nebFixEl) nebFixEl.hidden = true;
      return;
    }
    const nnote = e.target.closest('[data-neb-note]');
    if (nnote) {
      nebRipple(nnote);
      /* a note nothing on the chosen shelf carries cannot narrow anything -
         loving it would only make narrow() give it straight back. So the tap
         says what the disc is already showing, out loud, and changes nothing. */
      if (nnote.classList.contains('is-none')) { say(noneSay(nnote.getAttribute('data-neb-note'))); return; }
      markNote(nnote.getAttribute('data-neb-note'));
      return;
    }
    const nclose = e.target.closest('[data-neb-close]');
    if (nclose) { closeFam(); return; }
    /* the ground between the discs is the way back out - the core is a big
       target but the empty field is a bigger one */
    if (nebField && nebMode !== 'field' && (e.target === nebField || e.target === nebCloud)) { closeFam(); return; }
    const nfam = e.target.closest('[data-neb-fam]');
    if (nfam) {
      const fk = nfam.getAttribute('data-neb-fam');
      nebRipple(nfam);
      /* a family already ruled out is taken back by a tap whatever the
         mark says; otherwise the mark decides between opening and refusing */
      const fam = NEB && NEB.fams.filter((f) => f.key === fk)[0];
      /* a family that is its own only note has no inside to open - see
         buildNeb. Both marks route to the NOTE, so there is one truth about
         it in S.love / S.avoid rather than two half-truths across avoidFam. */
      if (fam && fam.solo) { markNote(fam.solo.key); return; }
      if (nebMark === 'out' || S.avoidFam.indexOf(fk) !== -1) cycleFam(fk);
      else openFam(fk);
      return;
    }

    /* the peek: about this scent, over the room */
    const info = e.target.closest('[data-fx-peek-info]');
    if (info) {
      e.preventDefault();
      const rec = PREP && PREP.byHandle[info.getAttribute('data-fx-peek-info')];
      if (rec) productPeek(rec, root);
      return;
    }

    const seed = e.target.closest('[data-fx-try]');
    if (seed) {
      e.preventDefault();
      S.text = seed.dataset.fxTry;
      if (textIn) textIn.value = S.text;
      touched.text = true;
      changed();
      go('answer', 1, 'try');
      return;
    }
    const more = e.target.closest('[data-fx-notemore]');
    if (more) { e.preventDefault(); foldNotes(!notesFolded); return; }
    const backHit = e.target.closest('[data-fx-back], [data-fx-edgeback]');
    if (backHit) { e.preventDefault(); back(); }
  });

  root.addEventListener('submit', (e) => {
    if (!e.target.closest('.sf-say')) return;
    if (!PREP) return;                        /* no index: let it post to /search */
    e.preventDefault();
    S.text = (textIn && textIn.value.trim()) || '';
    writeUrl();
    run();
    go('answer', 1, 'typed');
  });

  if (textIn) {
    let t;
    textIn.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => { S.text = textIn.value.trim(); writeUrl(); run(); }, 220);
    });
  }
  if (noteFind) {
    let nt;
    noteFind.addEventListener('input', () => {
      clearTimeout(nt);
      nt = setTimeout(() => findNote(noteFind.value), 120);
    });
  }
  if (nebFind) {
    let bt;
    nebFind.addEventListener('input', () => {
      clearTimeout(bt);
      bt = setTimeout(() => nebSearch(nebFind.value), 140);
    });
  }
  /* the field re-packs itself when the room changes shape - only while the
     nebula is actually standing, and through theme.js's settled-resize bus
     (steering/12 §6.1) rather than a listener and a timer of its own */
  onResizeSettled(() => {
    if (!NEB || !nebLaid) return;
    nebFlow(true);
  });
  /* ---- the field answers the pointer (fine pointers only): every disc
     leans a few pixels toward the cursor, scaled by its own --depth, so
     the cluster reads as a floating VOLUME rather than a wall of stickers.
     rAF-batched to one pass per frame; the offsets ride --mx/--my, which
     the drift keyframes already compose, so drift and lean never fight. */
  if (nebField) {
    const finePtr = matchMedia('(hover: hover) and (pointer: fine)');
    let parRaf = 0;
    const parClear = () => {
      qsa('.sf-orb').forEach((b) => {
        b.style.setProperty('--mx', '0px');
        b.style.setProperty('--my', '0px');
      });
    };
    nebField.addEventListener('pointermove', (e) => {
      if (!finePtr.matches || reduceMotion.matches || !NEB || parRaf) return;
      const x = e.clientX, y = e.clientY;
      parRaf = requestAnimationFrame(() => {
        parRaf = 0;
        const r = nebField.getBoundingClientRect();
        if (!r.width) return;
        /* every disc is measured before any is moved: reading a rect after
           the previous disc's --mx write forced a style pass per disc, up to
           27 in one frame (2026-09-22, lane 8) */
        const orbs = qsa('.sf-orb:not(.is-gone):not(.is-core)');
        const boxes = orbs.map((b) => b._g.getBoundingClientRect());
        orbs.forEach((b, oi) => {
          const br = boxes[oi];
          const dx = (x - (br.left + br.width / 2)) / r.width;
          const dy = (y - (br.top + br.height / 2)) / r.height;
          const depth = parseFloat(b.style.getPropertyValue('--depth')) || 1;
          b.style.setProperty('--mx', (dx * 10 * depth).toFixed(1) + 'px');
          b.style.setProperty('--my', (dy * 8 * depth).toFixed(1) + 'px');
        });
      });
    }, { passive: true });
    nebField.addEventListener('pointerleave', () => {
      if (parRaf) { cancelAnimationFrame(parRaf); parRaf = 0; }
      parClear();
    }, { passive: true });
  }
  /* the tap lands as a ring of light leaving the disc - one shot, removed
     the moment it finishes */
  /* ============================================================
     THE SWELL - the field parts around the pointer (2026-08-31, owner: "The
     bubble system is FANTASTIC! And I think we can improve it abit more nd
     cooler and animation").

     Every disc within about 190px of the cursor leans away from it, hardest
     where the cursor is and falling off on a gaussian, so moving across the
     field pushes a slow bulge through it and the discs close behind. It is
     the one gesture the field was missing: until now it drifted beautifully
     and ignored you completely.

     WHAT IT COSTS, and why it is allowed. One rAF-throttled pass writing two
     custom properties on at most ~28 elements, and ONLY while a fine pointer
     is actually inside the field. The properties feed `translate` on
     .sf-orb__d - a transform property of its own, so it composes with the
     drift's `transform` and the press's `scale` without overwriting either -
     which means every frame is a compositor transform and not one of them
     touches layout or paint. It never runs on a touch device (there is no
     hover to answer) and it never runs under reduced motion.

     THE LAG IS THE POINT. The transition on the translate is half a second of
     ease-out, so the discs arrive after the cursor rather than tracking it.
     A body with mass in a fluid does not keep up, and the field reads as
     liquid precisely because it does not.
     ============================================================ */
  const swellOK = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');
  let swellRaf = 0, swellX = -1e5, swellY = -1e5;
  const SWELL_R = 190;     /* how far the bulge reaches, px */
  const SWELL_K = 16;      /* how hard it pushes at the centre, px */
  function swellOne(o) {
    if (!o || !o.isConnected || o._x == null) return;
    const dx = o._x - swellX, dy = o._y - swellY;
    const d2 = dx * dx + dy * dy;
    const f = Math.exp(-d2 / (SWELL_R * SWELL_R));
    if (f < 0.02) {
      if (o._sw) { o._sw = 0; o.style.removeProperty('--nx'); o.style.removeProperty('--ny'); }
      return;
    }
    const d = Math.sqrt(d2) || 1;
    o._sw = 1;
    o.style.setProperty('--nx', (dx / d * SWELL_K * f).toFixed(2) + 'px');
    o.style.setProperty('--ny', (dy / d * SWELL_K * f).toFixed(2) + 'px');
  }
  function swellPass() {
    swellRaf = 0;
    if (!NEB) return;
    NEB.fams.forEach((f) => { swellOne(f.el); f.notes.forEach((n) => swellOne(n.el)); });
  }
  function swellQueue() {
    if (swellRaf || reduceMotion.matches) return;
    swellRaf = window.requestAnimationFrame(swellPass);
  }
  function armSwell() {
    if (!nebField || !swellOK || !swellOK.matches) return;
    nebField.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch') return;
      const b = nebField.getBoundingClientRect();
      swellX = e.clientX - b.left;
      swellY = e.clientY - b.top;
      swellQueue();
    }, { passive: true });
    /* out of the field is "infinitely far away", which relaxes every disc
       through the same transition it arrived on */
    nebField.addEventListener('pointerleave', () => {
      swellX = -1e5; swellY = -1e5;
      swellQueue();
    }, { passive: true });
  }

  function nebRipple(node) {
    if (!node || !node._g || reduceMotion.matches) return;
    /* the disc swells for a beat as well as ringing: on a phone there is
       no hover to answer a tap with, so the release is the whole feedback */
    node.classList.add('is-pop');
    window.setTimeout(() => node.classList.remove('is-pop'), 190);
    const ring = el('span', 'sf-ring');
    node._g.appendChild(ring);
    ring.addEventListener('animationend', () => ring.remove());
  }
  document.addEventListener('keydown', (e) => {
    if (root.classList.contains('is-flat')) return;
    if (e.key !== 'Escape' || at === 'for') return;
    const open = document.querySelector('dialog[open], [aria-modal="true"]');
    if (open) return;
    e.preventDefault();
    back();
  });

  /* ---- boot ---- */
  readUrl();
  reflect();
  /* ---- A SHARED BRIEF LANDS ON ITS ANSWER, ONCE (2026-08-31, owner: "on
     page load at spesific stage it must immediatelly show that stage we
     don't want layout lag, glitching and shifting and catchup and stuff").

     answered() reads the state the URL just wrote, so the room knows HERE,
     with no fetch behind it, that this load is pointed at the answer. The
     answer panel itself still cannot be built until the index lands - it is
     made out of the catalogue - but the room no longer arrives twice: it
     does not paint the first question, sit on it, and then slide 680ms into
     an answer the URL named before anything loaded.

     `.is-jump` (finder.css / rooms-v2.css) turns off the panel transition
     and every entrance animation under it for exactly that one landing, and
     comes off on the frame after, so every step the shopper takes for
     themselves still moves the way it always did. ---- */
  const jumping = answered();
  if (jumping) root.classList.add('is-jump');
  function landed() {
    if (!jumping) return;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      root.classList.remove('is-jump');
    }));
  }
  /* the season outside gets a quiet "Now" on its chip - a guide, never a
     preselection. Server-rendered chips, so this needs no index. */
  if (CTX) {
    qsa('[data-fx-pick="season"]').forEach((chip) => {
      if ((chip.dataset.fxValue || '') === CTX.season) {
        chip.classList.add('is-now');
        chip.appendChild(el('span', 'sf-nowtag', 'Now'));
      }
    });
  }
  juceIndex().then((prep) => {
    if (!prep || !prep.list.length) {
      flatten('We could not load the range just now, so the options below will take you into the shop instead.');
      return;
    }
    PREP = prep;
    /* THE DECK, NOT THE INDEX - see the note on SCENTS above. A scent has
       notes; the gift card is the only row in the shop with none. */
    SCENTS = PREP.list.filter((p) => p.notes && p.notes.length);
    if (!SCENTS.length) SCENTS = PREP.list;
    DECK = SCENTS.map((p) => p.h);
    NOTEKEYS = new Map();
    SCENTS.forEach((p) => { NOTEKEYS.set(p.h, p.notes.map((n) => JE.canonicalNote(n))); });
    root.classList.add('is-live');
    /* the answers stop being links and start being checkboxes here, the same
       moment the click handler starts intercepting them - reflect() below
       writes their first aria-checked */
    armAnswers(true);
    /* it said "read note by note", which was true when the notes were the
       fourth of five questions and has not been since 2026-08-31 */
    if (countEl) countEl.textContent = SCENTS.length + ' scents. Four questions.';
    orderNotes();
    buildNeb();
    reflect();
    run();
    backable();
    /* a shared link arrives with a brief already in it and opens straight
       on the answer; everyone else starts where the page already is. */
    if (answered()) go('answer', 1, 'link');
    /* the room opens ON the first question with no go(), so the first
       panel's arrival is announced here or the ladder has no first rung */
    else { root.dataset.at = at; loomSay('x.finder.step', { subject: at, from: '', dir: 0, brief: loomBrief() }); }
    landed();
  });
}
